import axios from "axios";
import type { FlowAnswer, FlowTask, FlowOutcome, FlowEvent, FlowShare, FlowPublication, TaskInput, OutcomeInput, TaskSummary, TaskStatusFilter, TaskDueFilter, TimelineFilter } from './personalFlowTypes';
import type { PersonalAccess, PersonalState, ProvisionContext, PreviewPlan, PolicyProposal,
  ProvisioningProposal, RolloutRequest, RequestList, ExecutionReceipt, IdentityCandidate,
  IdentityProposal, IdentityReview, PersonalFile, PersonalFileList, PersonalKnowledgeCapability,
  PersonalMemory, PersonalMemoryInput, PersonalTemplate, PersonalTemplateApplication, PersonalKnowledgeResult,
  DirectoryCurrent, DirectoryProposal, DirectoryPreview, DirectoryReview } from "./personalWorkspaceTypes";

const API_BASE_URL = process.env.REACT_APP_API_URL || "";  // 相对路径=同源，适配单进程+反代域名

export const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: { "Content-Type": "application/json" },
});

// 电商经营统一使用带登录令牌的客户端；文件下载也先鉴权。
export const tmallQaAPI = {
  chat: (body: { question: string; history: string[] }, signal?: AbortSignal) => api.post('/api/cs/tmall-qa/chat', body, { signal, timeout: 45000 }),
  access: () => api.get('/api/cs/tmall-qa/access'),
  overview: () => api.get('/api/cs/tmall-qa/overview'),
  search: (params: any, signal?: AbortSignal) => api.get('/api/cs/tmall-qa/search', { params, signal }),
  preview: (file: File) => { const data = new FormData(); data.append('file', file); return api.post('/api/cs/tmall-qa/preview', data, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 }); },
  import: (file: File, hash: string) => { const data = new FormData(); data.append('file', file); data.append('expected_hash', hash); return api.post('/api/cs/tmall-qa/import', data, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 }); },
  approve: (id: string, body: any) => api.post('/api/cs/tmall-qa/entries/' + encodeURIComponent(id) + '/approve', body),
  batches: () => api.get('/api/cs/tmall-qa/batches'),
  revoke: (id: number) => api.post('/api/cs/tmall-qa/batches/' + id + '/revoke'),
};

export const ecommerceAPI = {
  productSpend: (params: any, signal?: AbortSignal) => api.get('/api/ecommerce/product-spend', { params, signal, timeout: 120000 }),
  removeProductSpend: (body: any) => api.post('/api/ecommerce/product-spend/remove', body),
  myWork: (params: any, signal?: AbortSignal) => api.get('/api/ecommerce/my-work', { params, signal }),
  assignees: (shop: number, kind: string, signal?: AbortSignal) => api.get('/api/ecommerce/assignees', { params: { shop_id: shop, kind }, signal }),
  workDetail: (shop: number, id: number, signal?: AbortSignal) => api.get('/api/ecommerce/work/' + id, { params: { shop_id: shop }, signal }),
  batchCheck: (shop: number, id: number) => api.get('/api/ecommerce/imports/' + id + '/check', { params: { shop_id: shop } }),
  savedBriefs: (params: any, signal?: AbortSignal) => api.get('/api/ecommerce/briefs', { params, signal }),
  savedBrief: (shop: number, id: number, signal?: AbortSignal) => api.get('/api/ecommerce/briefs/' + id, { params: { shop_id: shop }, signal }),
  saveBrief: (shop: number, body: any) => api.post('/api/ecommerce/briefs', body, { params: { shop_id: shop } }),
  savedBriefExport: (shop: number, id: number) => api.get('/api/ecommerce/briefs/' + id + '/export', { params: { shop_id: shop }, responseType: 'blob' }),
  brief: (params: any, signal?: AbortSignal) => api.get('/api/ecommerce/brief', { params, signal }),
  briefExport: (params: any) => api.get('/api/ecommerce/brief/export', { params, responseType: 'blob' }),
  reverseImport: (shop: number, id: number, body: any) => api.post('/api/ecommerce/imports/' + id + '/reverse', body, { params: { shop_id: shop } }),
  meta: () => api.get('/api/ecommerce/meta'),
  shop: (body: any) => api.post('/api/ecommerce/shops', body),
  members: (shop: number) => api.get('/api/ecommerce/shops/' + shop + '/members'),
  member: (shop: number, body: any) => api.post('/api/ecommerce/shops/' + shop + '/members', body),
  overview: (params: any, signal?: AbortSignal) => api.get('/api/ecommerce/overview', { params, signal }),
  records: (params: any, signal?: AbortSignal) => api.get('/api/ecommerce/records', { params, signal }),
  batches: (shop: number) => api.get('/api/ecommerce/imports', { params: { shop_id: shop } }),
  import: (body: FormData) => api.post('/api/ecommerce/imports', body, { timeout: 120000 }),
  template: (kind: string) => api.get('/api/ecommerce/template/' + kind, { responseType: 'blob' }),
  map: (shop: number, body: any) => api.post('/api/ecommerce/products/map', body, { params: { shop_id: shop } }),
  work: (params: any, signal?: AbortSignal) => api.get('/api/ecommerce/work', { params, signal }),
  saveWork: (shop: number, body: any, id?: number) => id
    ? api.put('/api/ecommerce/work/' + id, body, { params: { shop_id: shop } })
    : api.post('/api/ecommerce/work', body, { params: { shop_id: shop } }),
  history: (params: any) => api.get('/api/ecommerce/history', { params }),
};

// ── 登录令牌管理 ──────────────────────────────────────────────
const TOKEN_KEY = "tenon_token";
export const getToken = () => localStorage.getItem(TOKEN_KEY) || "";
export const setToken = (t: string) => localStorage.setItem(TOKEN_KEY, t);
export const clearToken = () => localStorage.removeItem(TOKEN_KEY);

// 客户端校验令牌是否仍在有效期内（解 payload 的 exp）。过期的令牌不应被当作已登录，
// 否则企业微信里带着过期 ?wxtoken= 的缓存网址会陷入 401→reload→再读同一过期token 的死循环。
export const tokenAlive = (t: string = getToken()): boolean => {
  if (!t || t.indexOf(".") < 0) return false;
  try {
    const b64 = t.split(".")[0].replace(/-/g, "+").replace(/_/g, "/");
    const pad = b64 + "=".repeat((4 - (b64.length % 4)) % 4);
    const { exp } = JSON.parse(decodeURIComponent(escape(atob(pad))));
    return typeof exp === "number" && exp * 1000 > Date.now() + 5000;
  } catch {
    return false;
  }
};

// 从当前网址移除 wxtoken 参数（避免 reload 后又把过期令牌读回来）
const stripWxToken = () => {
  try {
    const u = new URL(window.location.href);
    if (u.searchParams.has("wxtoken")) {
      u.searchParams.delete("wxtoken");
      window.history.replaceState({}, "", u.pathname + (u.search ? u.search : "") + u.hash);
    }
  } catch {}
};

// 请求自动带令牌
api.interceptors.request.use((config) => {
  const t = getToken();
  if (t) config.headers.Authorization = `Bearer ${t}`;
  // FormData(含文件上传/图生图/图生视频)：去掉默认 JSON 头，让浏览器自动设 multipart 边界
  if (typeof FormData !== "undefined" && config.data instanceof FormData) {
    delete (config.headers as any)["Content-Type"];
  }
  return config;
});
// 401 自动登出并回登录页
api.interceptors.response.use(
  (resp) => resp,
  (error) => {
    if (error?.response?.status === 401) {
      clearToken();
      stripWxToken();   // 关键：清掉网址里的过期 wxtoken，否则 reload 会再次读回它
      const isWework = /wxwork/i.test(navigator.userAgent);
      const hasErr = new URLSearchParams(window.location.search).get("wxerr");
      if (isWework && !hasErr) {
        // 企业微信内：令牌失效 → 静默重新 OAuth 换发新令牌（节流，避免授权失败时反复跳）
        const last = Number(sessionStorage.getItem("wx_reauth_ts") || 0);
        if (Date.now() - last > 8000) {
          sessionStorage.setItem("wx_reauth_ts", String(Date.now()));
          // 带上当前路径：否则重新授权后会落回首页，移动页(如 /hd-m)的人被弹去 PC 版。
          // next 只传站内路径(后端 _safe_next 还会再校验一次)；不带 hash，那会挤掉 wxtoken。
          const here = window.location.pathname + window.location.search;
          window.location.href = "/api/wecom/oauth/login?next=" + encodeURIComponent(here);
          return Promise.reject(error);
        }
      }
      if (!window.location.pathname.includes("login")) window.location.reload();
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  login: (username: string, password: string) =>
    api.post("/api/auth/login", { username, password }),
};

// 法务业务权限完全由领域接口核验；菜单权限不构成法务授权。
export const legalAPI = {
  materialAccess: () => api.get('/api/legal/materials/access'),
  overview: (offset = 0) => api.get('/api/legal/overview', { params: { limit: 20, offset } }),
  execution: (id: string, version: number, offset = 0) => api.get(`/api/legal/matters/${encodeURIComponent(id)}/versions/${version}/execution`, { params: { limit: 20, offset } }),
  executionFile: (id: string, version: number, asset: string, offset = 0) => api.get(`/api/legal/matters/${encodeURIComponent(id)}/versions/${version}/execution/assets/${asset}`, { params: { limit: 20, offset } }),
  executionDownload: (id: string, version: number, asset: string) => api.get(`/api/legal/matters/${encodeURIComponent(id)}/versions/${version}/execution/assets/${asset}/download`, { responseType: 'blob' }),
  executionUpload: (id: string, version: number, file: File, source_note: string, expected_revision: number, key: string) =>
    api.post(`/api/legal/matters/${encodeURIComponent(id)}/versions/${version}/execution/assets`, file,
      { params: { filename: file.name, source_note, expected_revision }, headers: { 'Content-Type': 'application/octet-stream', 'Idempotency-Key': key }, timeout: 120000 }),
  deadlines: (id: string, version: number, offset = 0) => api.get(`/api/legal/matters/${encodeURIComponent(id)}/versions/${version}/deadlines`, { params: { limit: 20, offset } }),
  clauses: (id: string, version: number, offset = 0) => api.get(`/api/legal/matters/${encodeURIComponent(id)}/versions/${version}/clauses`, { params: { limit: 20, offset } }),
  compareVersions: (id: string, base_version: number, target_version: number, offset = 0, expected_report_sha256?: string) =>
    api.get(`/api/legal/matters/${encodeURIComponent(id)}/version-comparison`, { params: { base_version, target_version, offset, limit: 50, expected_report_sha256 } }),
  preparationWorkspace: (id: string, version: number) => api.get(`/api/legal/matters/${encodeURIComponent(id)}/versions/${version}/approval-preparation`),
  preparations: (id: string, version: number, offset = 0) => api.get(`/api/legal/matters/${encodeURIComponent(id)}/versions/${version}/approval-preparations`, { params: { limit: 20, offset } }),
  preparation: (id: string, version: number, preparationId: string) => api.get(`/api/legal/matters/${encodeURIComponent(id)}/versions/${version}/approval-preparations/${encodeURIComponent(preparationId)}`),
  impactFollowup: (id: string) => api.get(`/api/legal/impacts/${encodeURIComponent(id)}/followup`),
  impactFollowupList: (id: string, kind: 'candidates' | 'links', offset = 0) => api.get(`/api/legal/impacts/${encodeURIComponent(id)}/followup/${kind}`, { params: { limit: 20, offset } }),
  impacts: (offset = 0, matter?: string, version?: number) => api.get(matter ? `/api/legal/matters/${encodeURIComponent(matter)}/versions/${version}/impacts` : '/api/legal/impacts', { params: { offset, limit: 20 } }),
  impact: (id: string) => api.get(`/api/legal/impacts/${encodeURIComponent(id)}`),
  ruleLinks: (id: string, version: number, offset = 0) => api.get(`/api/legal/matters/${encodeURIComponent(id)}/versions/${version}/rule-links`, { params: { offset, limit: 20 } }),
  ruleReleaseWorkspace: (id: string, version: number) => api.get(`/api/legal/library/${encodeURIComponent(id)}/versions/${version}/release-workspace`),
  ruleReleases: (company_id: string, offset = 0) => api.get('/api/legal/rule-releases', { params: { company_id, offset, limit: 20 } }),
  ruleRelease: (id: string) => api.get(`/api/legal/rule-releases/${encodeURIComponent(id)}`),
  ruleReviewInbox: (company_id: string, offset = 0) => api.get('/api/legal/rule-review-inbox', { params: { company_id, offset, limit: 20 } }),
  ruleReview: (id: string, version: number) => api.get(`/api/legal/library/${encodeURIComponent(id)}/versions/${version}/review-workspace`),
  ruleSources: (id: string, version: number, offset = 0) => api.get(`/api/legal/library/${encodeURIComponent(id)}/versions/${version}/source-checks`, { params: { offset, limit: 20 } }),
  simulations: (id: string, version: number, offset = 0) => api.get(`/api/legal/library/${encodeURIComponent(id)}/versions/${version}/simulations`, { params: { offset, limit: 20 } }),
  simulation: (id: string, version: number, runId: string) => api.get(`/api/legal/library/${encodeURIComponent(id)}/versions/${version}/simulations/${encodeURIComponent(runId)}`),
  libraryAccess: () => api.get('/api/legal/library/access'),
  library: (company_id: string, offset = 0) => api.get('/api/legal/library', { params: { company_id, offset, limit: 20 } }),
  libraryHistory: (id: string, offset = 0) => api.get(`/api/legal/library/${encodeURIComponent(id)}/versions`, { params: { offset, limit: 20 } }),
  libraryVersion: (id: string, version: number) => api.get(`/api/legal/library/${encodeURIComponent(id)}/versions/${version}`),
  libraryCheck: (id: string, version: number) => api.get(`/api/legal/library/${encodeURIComponent(id)}/versions/${version}/check`),
  file: (id: string, version: number) => api.get(`/api/legal/matters/${encodeURIComponent(id)}/versions/${version}/file`),
  download: (id: string, version: number) => api.get(`/api/legal/matters/${encodeURIComponent(id)}/versions/${version}/download`, { responseType: 'blob' }),
  upload: (id: string, file: File, source_note: string, expected_revision: number, key: string) =>
    api.post(`/api/legal/matters/${encodeURIComponent(id)}/files`, file, { params: { filename: file.name, source_note, expected_revision },
      headers: { 'Content-Type': 'application/octet-stream', 'Idempotency-Key': key }, timeout: 45000 }),
  access: () => api.get('/api/legal/access'),
  matters: (offset = 0) => api.get('/api/legal/matters', { params: { offset, limit: 20 } }),
  workspace: (id: string) => api.get(`/api/legal/matters/${encodeURIComponent(id)}/workspace`),
  body: (id: string, version: number) => api.get(`/api/legal/matters/${encodeURIComponent(id)}/versions/${version}`),
  reviews: (id: string, version: number) => api.get(`/api/legal/matters/${encodeURIComponent(id)}/versions/${version}/reviews`),
  inbox: (offset = 0) => api.get('/api/legal/inbox', { params: { offset, limit: 20 } }),
  grants: (id: string, offset = 0) => api.get(`/api/legal/matters/${encodeURIComponent(id)}/grants`, { params: { offset, limit: 20 } }),
  write: (path: string, payload: unknown, key: string) => api.post(`/api/legal/${path}`, payload,
    { headers: { 'Idempotency-Key': key } }),
};

// 个人空间：复用请求头鉴权；幂等键由页面按操作持有，失败重试不能换键。
const personalKey = (key: string) => ({ headers: { "Idempotency-Key": key } });
export const personalWorkspaceAPI = {
  flowAnswers: (offset = 0) => api.get<{items: FlowAnswer[]; total: number}>('/api/personal/flow/answers', {params: {offset}}),
  flowTasks: (offset = 0, status: TaskStatusFilter = 'all', due: TaskDueFilter = 'all') => api.get<{items: FlowTask[]; total: number; today: string}>('/api/personal/flow/tasks', {params: {offset, status, due}}),
  flowTaskSummary: () => api.get<TaskSummary>('/api/personal/flow/tasks/summary'),
  flowOutcomes: (offset = 0) => api.get<{items: FlowOutcome[]; total: number}>('/api/personal/flow/outcomes', {params: {offset}}),
  flowTimeline: (offset = 0, filter: TimelineFilter = {}) => api.get<{items: FlowEvent[]; total: number}>('/api/personal/flow/timeline', {params: {offset, ...filter}}),
  flowAsk: (body: {question: string; memory_ids: string[]; file_ids: string[]; confirmed: true; purpose?: 'answer' | 'tasks'}, key: string) => api.post<FlowAnswer>('/api/personal/flow/ask', body, {...personalKey(key), timeout: 120000}),
  flowTaskSave: (body: TaskInput, key: string, taskId?: string) => taskId ? api.patch<FlowTask>('/api/personal/flow/tasks/' + encodeURIComponent(taskId), body) : api.post<FlowTask>('/api/personal/flow/tasks', body, personalKey(key)),
  flowOutcomeSave: (body: OutcomeInput, key: string) => api.post<FlowOutcome>('/api/personal/flow/outcomes', body, personalKey(key)),
  flowRecipients: (q = '') => api.get<{items: {recipient_id: string; name: string}[]; truncated: boolean}>('/api/personal/flow/recipients', {params: {q}}),
  flowShares: (received = false) => api.get<{items: FlowShare[]}>('/api/personal/flow/shares', {params: {received}}),
  flowShare: (id: string, body: {recipient_id: string; valid_days: number; confirmed: true}, key: string) => api.post('/api/personal/flow/outcomes/' + encodeURIComponent(id) + '/share', body, personalKey(key)),
  flowRevoke: (id: string) => api.post('/api/personal/flow/shares/' + encodeURIComponent(id) + '/revoke', {confirmed: true}),
  flowCategories: () => api.get<{items: {domain: string; category: string; label: string}[]}>('/api/personal/flow/categories'),
  flowPublish: (id: string, body: {domain: string; category: string; confirmed: true; review_visibility_confirmed: true}) => api.post('/api/personal/flow/outcomes/' + encodeURIComponent(id) + '/publish', body),
  flowPublications: () => api.get<{items: FlowPublication[]}>('/api/personal/flow/publications'),
  flowWithdraw: (id: string) => api.post('/api/personal/flow/publications/' + encodeURIComponent(id) + '/withdraw', {confirmed: true}),
  directoryCurrent: () => api.get<DirectoryCurrent>('/api/personal-admin/directory/current'),
  directoryPreview: (body: DirectoryProposal) => api.post<DirectoryPreview>('/api/personal-admin/directory-imports/preview', body),
  directorySubmit: (body: DirectoryProposal, key: string) => api.post<DirectoryReview>('/api/personal-admin/directory-imports', body, personalKey(key)),
  directoryReviews: (status: string, offset = 0) => api.get<RequestList<DirectoryReview>>('/api/personal-admin/directory-imports', { params: { status, offset, limit: 10 } }),
  directoryReview: (id: string) => api.get<DirectoryReview>('/api/personal-admin/directory-imports/' + encodeURIComponent(id)),
  directoryDecision: (id: string, decision: 'approve' | 'reject', reason: string) => api.post<DirectoryReview>(
    '/api/personal-admin/directory-imports/' + encodeURIComponent(id) + '/decision', { decision, reason }),
  access: () => api.get<PersonalAccess>("/api/personal/access"),
  me: () => api.get<PersonalState>("/api/personal/me"),
  templates: () => api.get<{ items: PersonalTemplate[] }>("/api/personal/templates"),
  applyTemplate: (item: PersonalTemplate) => api.post<PersonalTemplateApplication>(
    `/api/personal/templates/${encodeURIComponent(item.template_id)}/apply`,
    { version: item.version, content_hash: item.content_hash, confirmed: true }),
  memories: (offset = 0) => api.get<{ items: PersonalMemory[]; total: number }>("/api/personal/memories", { params: { offset, limit: 10 } }),
  memoryHistory: (id: string) => api.get<{ items: PersonalMemory[] }>(`/api/personal/memories/${encodeURIComponent(id)}/history`),
  saveMemory: (body: PersonalMemoryInput, key: string, previous?: string) => api.post<PersonalMemory>(
    previous ? `/api/personal/memories/${encodeURIComponent(previous)}/revise` : '/api/personal/memories', body, personalKey(key)),
  revokeMemory: (id: string) => api.post<PersonalMemory>(`/api/personal/memories/${encodeURIComponent(id)}/revoke`, { confirmed: true }),
  files: (offset = 0, limit = 20) => api.get<PersonalFileList>("/api/personal/files", { params: { offset, limit } }),
  knowledgeCapabilities: () => api.get<PersonalKnowledgeCapability>("/api/personal/knowledge/capabilities"),
  searchFiles: (file_ids: string[], question: string) => api.post<PersonalKnowledgeResult>(
    "/api/personal/knowledge/search", { file_ids, question }, { timeout: 90000 }),
  askFiles: (file_ids: string[], question: string) => api.post<PersonalKnowledgeResult>(
    "/api/personal/knowledge/ask", { file_ids, question, confirmed: true }, { timeout: 120000 }),
  uploadFile: (file: File, key: string) => api.post<PersonalFile>("/api/personal/files", file, {
    timeout: 120000, headers: { "Content-Type": "application/octet-stream", "X-Filename": encodeURIComponent(file.name),
      "Idempotency-Key": key, "X-Upload-Confirmed": "true" },
  }),
  downloadFile: (id: string) => api.get<Blob>("/api/personal/files/" + encodeURIComponent(id) + "/download", { responseType: 'blob' }),
  context: () => api.get<ProvisionContext>("/api/personal-admin/provisioning/context"),
  preview: (body: { organization_version: string; policy_version: string }, key: string) =>
    api.post<PreviewPlan>("/api/personal-admin/provisioning/preview", body, personalKey(key)),
  plan: (id: string) => api.get<PreviewPlan>("/api/personal-admin/provisioning/" + encodeURIComponent(id)),
  submitPolicy: (body: PolicyProposal, key: string) =>
    api.post<RolloutRequest>("/api/personal-admin/policy-reviews", body, personalKey(key)),
  submitProvisioning: (body: ProvisioningProposal, key: string) =>
    api.post<RolloutRequest>("/api/personal-admin/provisioning-requests", body, personalKey(key)),
  requests: (kind: 'policy' | 'provisioning', status: string, offset = 0, limit = 10) =>
    api.get<RequestList<RolloutRequest>>("/api/personal-admin/" + (kind === 'policy' ? 'policy-reviews' : 'provisioning-requests'),
      { params: { status, offset, limit } }),
  request: (kind: 'policy' | 'provisioning', id: string) => api.get<RolloutRequest>("/api/personal-admin/" +
    (kind === 'policy' ? 'policy-reviews' : 'provisioning-requests') + '/' + encodeURIComponent(id)),
  decide: (kind: 'policy' | 'provisioning', id: string, body: { decision: 'approve' | 'reject'; reason: string }) =>
    api.post<RolloutRequest>("/api/personal-admin/" + (kind === 'policy' ? 'policy-reviews' : 'provisioning-requests') +
      '/' + encodeURIComponent(id) + '/decision', body),
  execute: (id: string, approval_hash: string, key: string) => api.post<ExecutionReceipt>(
    '/api/personal-admin/provisioning-requests/' + encodeURIComponent(id) + '/execute', { approval_hash }, personalKey(key)),
  cancel: (id: string, reason: string) => api.post<RolloutRequest>(
    '/api/personal-admin/provisioning-requests/' + encodeURIComponent(id) + '/cancel', { reason }),
  candidate: (wecom_userid: string) => api.get<IdentityCandidate>('/api/personal-admin/identity-candidate', { params: { wecom_userid } }),
  submitIdentity: (body: IdentityProposal, key: string) => api.post<IdentityReview>('/api/personal-admin/identity-reviews', body, personalKey(key)),
  identities: (status: string, offset = 0, limit = 10) => api.get<RequestList<IdentityReview>>(
    '/api/personal-admin/identity-reviews', { params: { status, offset, limit } }),
  identity: (id: string) => api.get<IdentityReview>('/api/personal-admin/identity-reviews/' + encodeURIComponent(id)),
  decideIdentity: (id: string, decision: 'approve' | 'reject', reason: string) => api.post<IdentityReview>(
    '/api/personal-admin/identity-reviews/' + encodeURIComponent(id) + '/decision', { decision, reason }),
};

export const wecomAPI = {
  // JS-SDK 签名（wx.config 用）。url 传 location.href，后端负责去 hash 并回传
  // signedUrl 供前端自查——url 差一个字符就是 invalid signature。
  jssdkConfig: (url: string) =>
    api.get("/api/wecom/jssdk-config", { params: { url } }),
};

// 采购下单草稿（行内录入→预览→导出T100）
export const purchaseDraftAPI = {
  saveDraft: (data: any) => api.post("/api/purchase-draft/draft", data),
  listDrafts: (params: { status?: string; buyer?: string; batch?: string } = {}) =>
    api.get("/api/purchase-draft/drafts", { params }),
  deleteDraft: (id: number) => api.delete(`/api/purchase-draft/draft/${id}`),
  exportDrafts: (ids?: number[]) =>
    api.post("/api/purchase-draft/export", ids && ids.length ? { ids } : {},
             { responseType: "blob", timeout: 120000 }),
  listBatches: () => api.get("/api/purchase-draft/batches"),
  getConfig: () => api.get("/api/purchase-draft/config"),
  pushDrafts: (ids?: number[]) =>
    api.post("/api/purchase-draft/push", ids && ids.length ? { ids } : {}, { timeout: 180000 }),
};

export const supplyChainAPI = {
  getBiShortage: (codes: string, signal?: AbortSignal) =>
    api.get('/api/supply-chain/procurement/bi-shortage', { params: { codes }, signal, timeout: 30000 }),
  getWorkorderChanges: (params: Record<string, string | number>, signal?: AbortSignal) =>
    api.get('/api/supply-chain/procurement/workorder-changes', { params, signal, timeout: 20000 }),
  getWorkorderChangeDetail: (params: { work_order: string; batch: number; page: number; view?: 'business' | 'raw' }, signal?: AbortSignal) =>
    api.get('/api/supply-chain/procurement/workorder-changes/detail', { params, signal, timeout: 20000 }),
  getDemandChangeTrace: (params: Record<string,string|number>,signal?: AbortSignal) =>
    api.get('/api/supply-chain/procurement/demand-change-trace',{params,signal,timeout:50000}),
  getDemandChangeTraceDetail: (params: Record<string,string|number>,signal?: AbortSignal) =>
    api.get('/api/supply-chain/procurement/demand-change-trace/detail',{params,signal,timeout:50000}),
  getProcurementAdviceQuick: (refresh: boolean, all: boolean, signal: AbortSignal, version = "") =>
    api.get("/api/supply-chain/procurement/advice", {
      params: { quick: 1, version, ...(refresh ? { refresh: 1 } : {}), ...(all ? { all: 1 } : {}) },
      signal, timeout: 20000,
    }),
  // all=1：并入无缺口料的轻量行，列表覆盖全部采购件（页面「显示所有料件」按钮；响应体 4.25→9.6MB）
  getProcurementAdvice: (refresh?: boolean, all?: boolean) =>
    api.get("/api/supply-chain/procurement/advice", {
      params: { ...(refresh ? { refresh: 1 } : {}), ...(all ? { all: 1 } : {}) },
      timeout: 360000 }),
  getMaterialProfile: (code: string) =>
    api.get("/api/supply-chain/material/profile", { params: { code }, timeout: 60000 }),
  getDemandDetail: (code: string) =>
    api.get("/api/supply-chain/procurement/demand-detail", { params: { code }, timeout: 60000 }),
  saveFeeOverride: (data: any) => api.post("/api/supply-chain/procurement/fee-override", data),
  // 采购价目表（外采直发成本价）：T100核价/报价表/爬来的市场价都覆盖不到的那一块
  priceList: (params?: any) => api.get("/api/supply-chain/price-list", { params }),
  savePriceRow: (data: any) => api.post("/api/supply-chain/price-list", data),
  deactivatePriceRow: (id: number) => api.post("/api/supply-chain/price-list/deactivate", { id }),
  importPriceList: (file: File, effective_date?: string) => {
    const fd = new FormData();
    fd.append("file", file);
    return api.post("/api/supply-chain/price-list/import", fd, {
      params: effective_date ? { effective_date } : undefined,
      headers: { "Content-Type": "multipart/form-data" }, timeout: 180000,
    });
  },
  getReturnPlan: (code: string) =>
    api.get("/api/supply-chain/procurement/return-plan", { params: { code }, timeout: 120000 }),
  getStockDetail: (code: string) =>
    api.get("/api/supply-chain/procurement/stock-detail", { params: { code }, timeout: 60000 }),
  getUpperStockDetail: (code: string) =>
    api.get("/api/supply-chain/procurement/upper-stock-detail", { params: { code }, timeout: 30000 }),
  getInTransitDetail: (code: string) =>
    api.get("/api/supply-chain/procurement/intransit-detail", { params: { code }, timeout: 60000 }),
  // 单料实时刷新：只现拉这一个料号(约0.5秒)，按其类别原公式重算净缺口；比全量强刷(14秒)轻得多
  refreshRow: (code: string) =>
    api.get("/api/supply-chain/procurement/row-refresh", { params: { code }, timeout: 60000 }),
  itemLookup: (codes: string) =>
    api.get("/api/supply-chain/procurement/item-lookup", { params: { codes }, timeout: 120000 }),
  getWhereUsed: (code: string) =>
    api.get("/api/supply-chain/procurement/where-used", { params: { code }, timeout: 120000 }),
  getProdDemandDetail: (code: string) =>
    api.get("/api/supply-chain/production/demand-detail", { params: { code }, timeout: 60000 }),
  getWipDetail: (code: string) =>
    api.get("/api/supply-chain/production/wip-detail", { params: { code }, timeout: 60000 }),
  purchaseForecast: (material_code: string, qty: number) =>
    api.post("/api/supply-chain/purchase-forecast", { material_code, qty }, { timeout: 120000 }),
  getProductionAdvice: (refresh = false) => api.get("/api/supply-chain/production/advice", { params: { refresh: refresh ? 1 : 0 }, timeout: 360000 }),
  getOutsourcingAdvice: () => api.get("/api/supply-chain/outsourcing/advice", { timeout: 360000 }),
  outsourcingExcelUrl: () => `${API_BASE_URL}/api/supply-chain/outsourcing/advice/excel?token=${getToken()}`,
  getTodayFollowUp: () => api.get("/api/supply-chain/follow-up/today"),
  // 排产记录
  addProductionRecord: (data: any) => api.post("/api/supply-chain/production/record", data),
  getProductionRecords: () => api.get("/api/supply-chain/production/records"),
  // 双维度排产（8.6）
  getSchedule: () => api.get("/api/supply-chain/production/schedule", { timeout: 180000 }),
  getCapacityLoad: (days = 14) => api.get("/api/supply-chain/production/capacity-load", { params: { days } }),
  orderBusinessParse: (text: string) => api.post("/api/supply-chain/order-business/parse", { text }, { timeout: 60000 }),
  orderBusinessUpsert: (data: any) => api.post("/api/supply-chain/order-business", data),
  orderBusinessGet: (order_no: string) => api.get("/api/supply-chain/order-business", { params: { order_no } }),
  getScheduleDetail: (code: string) =>
    api.get(`/api/supply-chain/production/schedule/${encodeURIComponent(code)}`, { timeout: 120000 }),
  // 手动/急插单（第2步）
  createManualOrder: (data: any) => api.post("/api/supply-chain/production/manual-order", data),
  listManualOrders: () => api.get("/api/supply-chain/production/manual-orders"),
  deleteManualOrder: (id: number) => api.post("/api/supply-chain/production/manual-order/delete", { id }),
  importManualOrders: (file: File) => { const fd = new FormData(); fd.append("file", file); return api.post("/api/supply-chain/production/manual-order/import", fd, { timeout: 120000 }); },
  // 报工（第3步·生产执行跟踪）
  reportProgress: (data: any) => api.post("/api/supply-chain/production/report", data),
  // 排产确认流转（9.5·阶段二·版本锁定）
  confirmProduction: (data: any) => api.post("/api/supply-chain/production/confirm", data),
  confirmHistory: (product_code: string, advice_date = "") =>
    api.get("/api/supply-chain/production/confirm-history", { params: { product_code, advice_date } }),
  getCalendar: (days = 7) => api.get("/api/supply-chain/production/calendar", { params: { days }, timeout: 180000 }),
  // 生产看板（一行一单，对齐《生产看板模型》）
  getKanban: () => api.get("/api/supply-chain/production/kanban", { timeout: 180000 }),
  // 工单明细下钻（点看板「工单号」）：订单号走 query，可能带斜杠
  getWorkOrders: (order_no: string) =>
    api.get("/api/supply-chain/production/workorders", { params: { order_no }, timeout: 60000 }),
  // AI解释层：插单影响预演（不落单） + 排产异动解释
  simulateInsert: (data: any) => api.post("/api/supply-chain/production/simulate-insert", data, { timeout: 180000 }),
  getScheduleDiff: () => api.get("/api/supply-chain/production/schedule-diff", { timeout: 120000 }),
  // 生产经理工作台（8.6）
  getSchedConfig: () => api.get("/api/supply-chain/production/config"),
  setSchedConfig: (data: any) => api.post("/api/supply-chain/production/config", data),
  getMgrToday: () => api.get("/api/supply-chain/production/manager/today"),
  addMgrTask: (data: any) => api.post("/api/supply-chain/production/manager/task", data),
  completeMgrTask: (id: number) => api.post(`/api/supply-chain/production/manager/task/${id}/complete`),
  addMgrIssue: (data: any) => api.post("/api/supply-chain/production/manager/issue", data),
  resolveMgrIssue: (id: number, data: any) =>
    api.post(`/api/supply-chain/production/manager/issue/${id}/resolve`, data),
  setManualStart: (data: any) => api.post("/api/supply-chain/production/manager/manual-start", data),
};

// 交付状态综合看板（9.5·阶段三）
// ── 呆滞库存清理（/api/dull-stock · T100只读；待处理量已扣订单占用）────────
export interface DullItem {
  code: string; name: string; spec: string; category: string;
  is_tt: boolean; supplier: string; substitute: string; mclass: string;
  buyer: string;                         // 采购员(与采购建议页同源)
  warehouse: string;                     // 所在仓库编号，多仓用/分隔，如 "S01/S02"
  warehouse_name: string;                // 对应中文仓名，如 "电子仓/包装仓"
  s15_qty: number;                       // 销退仓(S15)数量
  bi_remark: string;                     // BI「物料跟进表」说明列(nuhguc_t.nuhguc003)
  bi_remark_by: string;                  // 说明录入人
  bi_remark_at: string;                  // 说明录入日期
  remark_intents: string[];              // 从BI说明抽出的处置意图标签
  remark_conflict: string;               // BI说明与系统建议分歧的提示（空=无分歧）
  // 多阶替代链（与采购建议同一算法 tt_substitute.build_chain），链尾为在用料
  substitute_chain: { code: string; name: string; is_tt: boolean; stock: number | null }[];
  stock_qty: number; in_transit: number; avail_qty: number;
  occupied_qty: number;            // 订单占用(销售未出货+工单未发料)
  pending_qty: number;             // 待处理量 = 可用总量 - 订单占用
  pending_transit_qty: number;     // 其中在途部分(应优先砍单/延期)
  unit_price: number; pending_amount: number;
  dull_days: number; dull_date: string; dull_label: string;
  cover_months: number;            // 9999 = ∞(近12月零消耗)
  m12_avg: number; priority: string;
  price_src: "core" | "hist" | "none";   // 单价来源：现行核价/末次采购价/无价可依
  price_src_date: string;                // hist 时的末次采购日期
  // BOM 状态：normal=仍用于有效成品 / yellow=只用于已TT成品 / red=未用于任何成品
  bom_status: "normal" | "yellow" | "red";
  bom_status_text: string;
  // 追溯到的根成品明细（带型号，点开BOM状态查看；最多60个）
  /** overview 出口已剥离(占响应体45%)，弹窗时用 dullStockAPI.roots 按需取 */
  root_products?: { code: string; model: string; name: string; tt: boolean; category: string }[];
  root_models: string[];                 // 型号去重汇总（未TT优先，列上直接显示）
  root_count: number;                    // 根成品**料号**数
  root_model_count: number;              // 根成品**型号**数（同型号常有多个料号）
  root_no_model_count: number;           // 只到组件层、主档无型号的根数
  root_alive_count: number;              // 其中未TT（仍有效）的根成品料号数
  action: string;                  // 建议处理方式
  action_reason: string;           // 理由
  evidence: string[];              // 可核验依据（承认书/BOM父件/在途等）
  bom_parents: { code: string; name: string }[];
}
export interface DullPack {
  supplier?: string; target?: string; mclass?: string;
  codes: number; qty: number; amount: number;
  items: { code: string; name: string; qty: number; amount: number; dull_days: number }[];
}
export interface DullPackages {
  cut_transit: DullPack[]; supplier_return: DullPack[];
  substitute: DullPack[]; scrap: DullPack[];
  downgrade_codes: string[]; pending_codes: string[]; hint: string;
}
export interface DullSupplier {
  supplier: string; items: number; amount: number; qty: number; codes: string[];
}
export interface DullOverview {
  summary: {
    as_of: string; min_days: number; total_codes: number; pending_codes: number;
    total_amount: number; occupied_codes: number; occupied_qty: number;
    transit_amount: number; p1_codes: number;
    by_bucket: { label: string; codes: number; qty: number; amount: number }[];
    by_action: { action: string; codes: number; qty: number; amount: number }[];
    price_src: { src: string; label: string; codes: number; amount: number }[];
    bom_status: { status: string; label: string; codes: number; qty: number; amount: number }[];
    by_buyer: { buyer: string; codes: number; qty: number; amount: number }[];
  };
  items: DullItem[];
  by_supplier: DullSupplier[];
  packages: DullPackages;
  notes: string[];
}
// 清理台账（P3）：建议 → 认领 → 审批(报废/退货或大额需二级) → 执行 → 核销
export interface DullTicket {
  target_date?: string | null; target_version?: number;
  target_status?: 'missing' | 'invalid' | 'scheduled' | 'today' | 'overdue' | 'closed';
  overdue_days?: number | null; target_reason?: string; target_updated_by?: string;
  id: number; ticket_no: string; batch_no: string;
  material_code: string; material_name: string; category: string; supplier: string;
  dull_days: number; snap_pending_qty: number; snap_unit_price: number; snap_amount: number;
  priority: string; suggest_action: string; evidence: string;
  status: string; status_text: string; owner: string; owner_name: string;
  final_action: string; need_two_level: boolean;
  approver1: string; approver1_at: string | null;
  approver2: string; approver2_at: string | null;
  reject_reason: string; cancel_reason: string;
  actual_qty: number; actual_amount: number; settle_note: string; settled_at: string | null;
  created_by: string; created_at: string | null;
}
export interface DullKpi {
  settled_tickets: number; settled_amount: number; settled_qty: number; adopt_rate: number;
  by_month: { month: string; tickets: number; qty: number; amount: number }[];
  by_action: { action: string; tickets: number; amount: number }[];
  open_by_status: { status: string; status_text: string; tickets: number; amount: number }[];
  open_amount: number;
}
export const dullStockAPI = {
  targetDate: (id: number, body: { target_date: string; expected_version: number; reason?: string }) =>
    api.post('/api/dull-stock/tickets/' + id + '/target-date', body),
  materialLookup: (code: string, min_days = 365) =>
    api.get("/api/dull-stock/material-lookup", { params: { code, min_days }, timeout: 30000 }),
  overview: (min_days = 365, force = false) =>
    api.get<DullOverview>("/api/dull-stock/overview", {
      params: { min_days, force }, timeout: 300000 }),
  // 单料号「用于以下成品」明细：overview 已不再下发 root_products(占响应体45%)，弹窗时按需取
  roots: (code: string, min_days = 365) =>
    api.get<{ code: string; root_count: number; root_products: any[] }>(
      "/api/dull-stock/roots", { params: { code, min_days } }),
  // 台账
  ticketCreate: (items: any[], batch_no = "") =>
    api.post("/api/dull-stock/tickets/create", { items, batch_no }, { timeout: 120000 }),
  ticketList: (params: { status?: string; owner?: string; priority?: string;
                         supplier?: string; keyword?: string; page?: number;
                         page_size?: number } = {}) =>
    api.get<{ total: number; items: DullTicket[] }>("/api/dull-stock/tickets", { params }),
  ticketLogs: (id: number) =>
    api.get<{ logs: any[] }>(`/api/dull-stock/tickets/${id}/logs`),
  claim: (id: number) => api.post(`/api/dull-stock/tickets/${id}/claim`, {}),
  submit: (id: number, body: { final_action?: string; remark?: string }) =>
    api.post(`/api/dull-stock/tickets/${id}/submit`, body),
  approve: (id: number, remark = "") =>
    api.post(`/api/dull-stock/tickets/${id}/approve`, { remark }),
  reject: (id: number, reason: string) =>
    api.post(`/api/dull-stock/tickets/${id}/reject`, { reason }),
  execute: (id: number, remark = "") =>
    api.post(`/api/dull-stock/tickets/${id}/execute`, { remark }),
  settle: (id: number, body: { actual_qty: number; actual_amount?: number; note?: string }) =>
    api.post(`/api/dull-stock/tickets/${id}/settle`, body),
  cancelTicket: (id: number, reason: string) =>
    api.post(`/api/dull-stock/tickets/${id}/cancel`, { reason }),
  kpi: (months = 6) => api.get<DullKpi>("/api/dull-stock/kpi", { params: { months } }),
};

// ── 京东UE 待接单队列（/api/jd-ue · 自营店上门安装工单→无忧服务）──────────
// 隐私口径：list 不含客户姓名/电话/门牌，要看得单独调 reveal（后端会留痕）。
export interface JdUeTicket {
  id: number;
  service_order_id: string;
  order_no: string | null;
  service_order_status: string | null;
  sku_id: string | null;
  sku_name: string | null;
  shop_name: string | null;
  category2: string | null;
  category3: string | null;
  order_category: string;            // 门订单/锁订单/其他（由品类派生）
  stock_mode: string;                // 京仓现货/现货即发/定制/待确认（按 skuId 查规格映射派生）
  spec_text: string;                 // 规格原文，如 'J6 960*2050左外开[京仓现货]'
  first_service_type_name: string | null;
  region: string;                    // 省市区街道拼接(明文，门牌在密文里)
  buy_date: string | null;
  jd_create_date: string | null;
  phone_bind_timeout: string | null; // 虚拟号失效时间
  phone_expired: boolean;
  phone_expiring_soon: boolean;      // 24小时内失效
  status: string;
  status_text: string;
  order_code: string | null;         // 确认后生成的无忧工单号
  flow_order_code?: string | null;
  flow_href?: string | null;
  service_href?: string | null;
  handler_name: string | null;
  handled_at: string | null;
  note: string | null;
  has_privacy: boolean;
  created_at: string | null;
  // 仅 reveal 后出现
  customer_name?: string | null;
  customer_phone?: string | null;
  customer_address?: string | null;
}

/** 京东 SKU 规格映射（skuId → 规格名 → 库存模式档位） */
export interface JdSkuSpec {
  sku_id: string;
  sku_title: string | null;
  spec_text: string | null;
  auto_mode: string | null;          // 按规格名自动判定
  manual_mode: string | null;        // 人工覆盖（优先于 auto_mode）
  mode: string;                      // 最终档位
  fetch_status: string;              // ok / failed / pending
  fetch_err: string | null;
  fetch_attempts: number;
  fetched_at: string | null;
  last_try_at: string | null;
}

export interface JdUeDateRange { date_from?: string; date_to?: string }

export interface JdUeConfirmResult {
  ok: boolean; order_code?: string; flow_order_code?: string | null;
  flow_href?: string | null; service_href?: string | null;
  already?: boolean; recovered?: boolean; message?: string; error?: string;
}

export const jdUeAPI = {
  /** jd_status: 京东侧状态多选（「出库」=待上门安装、「完成」=京东侧已完工）
   *  order_category: 订单类别多选（门订单/锁订单/其他）
   *  stock_mode: 库存模式多选（京仓现货/现货即发/定制/待确认） */
  list: (params: { status?: string; keyword?: string; page?: number; page_size?: number;
                   jd_status?: string[]; order_category?: string[];
                   stock_mode?: string[] } & JdUeDateRange = {}) =>
    api.post<{ ok: boolean; total: number; items: JdUeTicket[] }>("/api/jd-ue/list", params),
  stats: (params: JdUeDateRange = {}) => api.get<{ ok: boolean; as_of: string; pending: number; expiring_soon: number;
                         expired: number; confirmed_today: number;
                         by_jd_status: { value: string; count: number }[];
                         by_order_category: { value: string; count: number }[];
                         by_stock_mode: { value: string; count: number }[] }>(
    "/api/jd-ue/stats", { params }),
  /** 筛选项：京东状态（库里实际值）+ 订单类别 + 库存模式（均固定档位），带单数 */
  filters: (params: JdUeDateRange = {}) => api.get<{ ok: boolean;
                           jd_status: { value: string; label: string; count: number }[];
                           order_category: { value: string; label: string; count: number }[];
                           stock_mode: { value: string; label: string; count: number }[] }>(
    "/api/jd-ue/filters", { params }),
  /** SKU 规格映射全量（十几个 SKU，不分页） */
  specs: () => api.get<{ ok: boolean; total: number; items: JdSkuSpec[] }>("/api/jd-ue/specs"),
  /** 抓规格：不传 sku_ids=刷全部；force=连已抓到的也重抓。**一个约6秒，超时给足** */
  refreshSpecs: (body: { sku_ids?: string[]; force?: boolean } = {}) =>
    api.post<{ ok: boolean; fetched?: number; ok_count?: number; failed?: number;
               skipped?: number; total_skus?: number; truncated?: number; error?: string }>(
      "/api/jd-ue/specs/refresh", body, { timeout: 600000 }),
  /** 人工指定档位（mode 传空=撤销覆盖，回到自动判定） */
  setSpecMode: (skuId: string, mode: string) =>
    api.post<{ ok: boolean; item?: JdSkuSpec; error?: string }>(
      `/api/jd-ue/specs/${encodeURIComponent(skuId)}/mode`, { mode }),
  /** 手动同步；京东侧偶有延迟，定时任务之外的兜底 */
  sync: () => api.post<{ ok: boolean; fetched?: number; added?: number;
                         updated?: number; error?: string }>("/api/jd-ue/sync", {}, { timeout: 120000 }),
  /** 解密客户姓名/虚拟号/门牌——敏感操作，后端记日志 */
  reveal: (sid: string) =>
    api.post<{ ok: boolean; item: JdUeTicket }>(`/api/jd-ue/${sid}/reveal`, {}),
  confirm: (sid: string) =>
    api.post<JdUeConfirmResult>(
      `/api/jd-ue/${sid}/confirm`, {}, { timeout: 60000 }),
  ignore: (sid: string, note = "") =>
    api.post<{ ok: boolean; error?: string }>(`/api/jd-ue/${sid}/ignore`, { note }),
};

export const deliveryAPI = {
  getDashboard: (params: { risk?: string; confirm?: string; search?: string } = {}) =>
    api.get("/api/delivery/dashboard", { params, timeout: 180000 }),
  getDetail: (product_code: string, order_no = "") =>
    api.get("/api/delivery/detail", { params: { product_code, order_no }, timeout: 120000 }),
  aiRefresh: (limit?: number) => api.post("/api/delivery/ai-refresh", { limit }, { timeout: 300000 }),
};

// 供应商公司级画像（蓝图9.9一期：交付/配合/稳定三维）
export const supplierManualProfileAPI = {
  suppliers: (search: string) => api.get('/api/supplier-profile/v2/manual-suppliers', { params: { search } }),
  history: (key: string, before?: number) => api.get('/api/supplier-profile/v2/manual-profiles', { params: { key, before } }),
  submit: (body: { supplier_key: string; supplier_code: string; supplier_name: string; original: string; request_key: string }) =>
    api.post('/api/supplier-profile/v2/manual-profiles', body, { timeout: 120000 }),
  retry: (id: number) => api.post('/api/supplier-profile/v2/manual-profiles/' + id + '/retry', {}, { timeout: 120000 }),
};

export const supplierProfileAPI = {
  getRanking: (params: { search?: string; risk?: string; grade?: string } = {}) =>
    api.get("/api/supplier-profile/ranking", { params, timeout: 60000 }),
  getDetail: (key: string) => api.get("/api/supplier-profile/detail", { params: { key } }),
  getHighRisk: (limit = 10) => api.get("/api/supplier-profile/high-risk", { params: { limit } }),
  refresh: () => api.post("/api/supplier-profile/refresh", {}, { timeout: 120000 }),
};

export const influencerAPI = {
  search: (data: any) => api.post("/api/influencer/search", data),
  list: (status?: string) => api.get("/api/influencer/list", { params: { status } }),
  generateInvite: (id: number) => api.post(`/api/influencer/${id}/invite`),
  createCoupon: (id: number) => api.post(`/api/influencer/${id}/coupon`),
  getPendingOutreach: () => api.get("/api/influencer/outreach/pending"),
};

export const chatAPI = {
  // 智能模式可能触发 T100 计算/多步骤(采购建议/齐套/BOM/发货等)，超时放大到 5 分钟
  send: (message: string, mode: string = "smart", history: { role: string; content: string }[] = [], conv_id = "") =>
    api.post("/api/chat/chat", { message, mode, history, conv_id }, { timeout: 300000 }),
  command: (command: any) => api.post("/api/chat/command", command, { timeout: 300000 }),
  // 带附件的对话：图片走视觉模型，文档抽文本+RAG+路由
  sendWithFile: (files: File[], message: string, conv_id = "", history: { role: string; content: string }[] = []) => {
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    fd.append("message", message);
    fd.append("conv_id", conv_id);
    fd.append("history", JSON.stringify(history));
    return axios.post(`${API_BASE_URL}/api/chat/chat-with-file`, fd, { timeout: 600000, headers: { Authorization: `Bearer ${getToken()}` } });
  },
};

export const competitorAPI = {
  monitor: (data: any) => api.post("/api/competitor/monitor", data),
  compare: (data: any) => api.post("/api/competitor/compare", data),
};

// 对话会话(主题/历史)：服务端持久化，关程序也在
export const conversationsAPI = {
  list: (mode = "") => api.get("/api/chat/conversations", { params: { mode } }),
  create: (mode = "smart") => api.post("/api/chat/conversations", { mode }),
  get: (id: string) => api.get(`/api/chat/conversations/${id}`),
  rename: (id: string, title: string) => api.post(`/api/chat/conversations/${id}/rename`, { title }),
  remove: (id: string) => api.delete(`/api/chat/conversations/${id}`),
};

// 高级对话（Claude，授权用户）+ 权限管理
// Agent 模式：多步自主查询（只读工具），慢，超时给足
export const agentAPI = {
  // scene：场景名（如 "creative"），后端按白名单换成场景引导追加到系统提示；
  // 只能传名字，传提示词后端不认（防提示词注入）
  ask: (question: string, history: { role: string; content: string }[] = [], conv_id = "",
        scene = "") =>
    api.post("/api/ai-agent/ask", { question, history, conv_id, scene }, { timeout: 300000 }),
  tools: () => api.get("/api/ai-agent/tools"),
  // 流式问答：边跑边把每一步推回来（SSE）。onEvent 收到 {type:'tool_start'|'tool_end'|...}。
  // 用原生 fetch 读流——axios 拿不到增量。任何异常都抛出去，让调用方回退到 ask()。
  askStream: async (
    question: string,
    history: { role: string; content: string }[],
    conv_id: string,
    onEvent: (ev: any) => void,
    scene = "",
  ): Promise<any> => {
    const resp = await fetch(`${API_BASE_URL}/api/ai-agent/ask-stream`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
      body: JSON.stringify({ question, history, conv_id, scene }),
    });
    if (!resp.ok || !resp.body) throw new Error(`stream ${resp.status}`);
    const reader = resp.body.getReader();
    const dec = new TextDecoder();
    let buf = "";
    let result: any = null;
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buf += dec.decode(value, { stream: true });
      // SSE 以空行分帧；半截的帧留在 buf 里等下一块
      const frames = buf.split("\n\n");
      buf = frames.pop() || "";
      for (const f of frames) {
        const line = f.split("\n").find((x) => x.startsWith("data:"));
        if (!line) continue;                       // 心跳 ": ping" 跳过
        let ev: any;
        try { ev = JSON.parse(line.slice(5).trim()); } catch { continue; }
        if (ev.type === "done") result = ev.result;
        else if (ev.type === "error") throw new Error(ev.message || "stream error");
        else onEvent(ev);
      }
    }
    if (!result) throw new Error("流中断，没拿到最终结果");
    return result;
  },
  // 文件知识库：上传即入库（原文件永久保留、按页/按行解析），返回真实验收报告
  upload: (files: File[], conv_id = "", signal?: AbortSignal) => {
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    fd.append("conv_id", conv_id);
    return api.post("/api/ai-agent/upload", fd, { timeout: 600000, signal });
  },
  files: (conv_id = "") => api.get("/api/ai-agent/files", { params: { conv_id } }),
  // 下载导出文件：接口要鉴权，直接点 markdown 里的 /api/... 链接会 401，
  // 所以走 blob 下载（带上令牌），再用临时 <a> 触发保存
  downloadExport: async (name: string) => {
    const r = await api.get(`/api/ai-agent/export/${encodeURIComponent(name)}`,
                            { responseType: "blob", timeout: 300000 });
    const url = URL.createObjectURL(r.data as Blob);
    const a = document.createElement("a");
    a.href = url; a.download = name;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 4000);
  },
  // 翻回历史会话时把「查询过程」还原出来（steps 不存在 chat_message 里，只在 agent_trace）
  traceByConv: (conv_id: string) =>
    api.get("/api/ai-agent/trace/by-conversation", { params: { conv_id } }),
  // 长文档：后台分章生成（几万字以上走这条），前端轮询进度
  longdoc: (job_id: string) => api.get(`/api/ai-agent/longdoc/${job_id}`),
  longdocs: () => api.get("/api/ai-agent/longdoc"),
  fileStatus: (file_id: string) => api.get(`/api/ai-agent/files/${file_id}`),
  removeFile: (file_id: string) => api.delete(`/api/ai-agent/files/${file_id}`),
};

export const advancedAPI = {
  access: () => api.get("/api/chat/advanced/access"),
  chat: (message: string, history: { role: string; content: string }[] = [], conv_id = "") =>
    api.post("/api/chat/advanced", { message, history, conv_id }, { timeout: 300000 }),
  // 高级对话附件：图片/文档全用 Claude（视觉/文本）
  chatWithFile: (files: File[], message: string, history: { role: string; content: string }[] = [], conv_id = "") => {
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    fd.append("message", message);
    fd.append("history", JSON.stringify(history));
    fd.append("conv_id", conv_id);
    return api.post("/api/chat/advanced-with-file", fd, { timeout: 300000 });
  },
  getUsers: () => api.get("/api/chat/advanced/users"),
  setUsers: (users: { userid: string; name: string }[]) => api.post("/api/chat/advanced/users", { users }),
  contacts: () => api.get("/api/wecom/contacts"),
};

// 家装潜在客户（招商管理子模块）
export const homedecorAPI = {
  meta: () => api.get("/api/homedecor/meta"),
  stats: () => api.get("/api/homedecor/stats"),
  list: (params: any) => api.get("/api/homedecor/customers", { params }),
  detail: (id: number) => api.get(`/api/homedecor/customers/${id}`),
  checkDuplicate: (company_name: string) =>
    api.get("/api/homedecor/customers/check-duplicate", { params: { company_name } }),
  create: (data: any) => api.post("/api/homedecor/customers", data),
  update: (id: number, data: any) => api.put(`/api/homedecor/customers/${id}`, data),
  remove: (id: number) => api.delete(`/api/homedecor/customers/${id}`),
  recognize: (raw_text: string) => api.post("/api/homedecor/customers/recognize", { raw_text }),
  importFile: (file: File, commit: boolean) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("commit", commit ? "1" : "0");
    return api.post("/api/homedecor/customers/import", fd, { timeout: 120000 });
  },
  templateUrl: () => `/api/homedecor/customers/import/template?token=${getToken()}`,
  exportUrl: (params: Record<string, string>) =>
    `/api/homedecor/export?token=${getToken()}&` + new URLSearchParams(params).toString(),
  assign: (id: number, to_user: string, reason: string, to_name = "") =>
    api.post(`/api/homedecor/customers/${id}/assign`, { to_user, reason, to_name }),
  batchAssign: (ids: number[], to_user: string, reason: string, to_name = "") =>
    api.post("/api/homedecor/customers/batch-assign", { ids, to_user, reason, to_name }),
  directory: () => api.get("/api/homedecor/directory"),
  claim: (id: number) => api.post(`/api/homedecor/customers/${id}/claim`),
  timeline: (id: number) => api.get(`/api/homedecor/customers/${id}/timeline`),
  // 记跟进。lat/lng 可选：移动端带上（企微 getLocation 的 GCJ-02）→ 后端判 拜访/日常
  // 并把判定结果随响应返回；PC 端不传，行为与从前一致（记为未判定）。
  addFollowup: (id: number, content: string, lat?: number, lng?: number, store_id?: number,
                asr_raw?: string, ai_polished?: boolean) =>
    api.post(`/api/homedecor/customers/${id}/followups`,
      { content, lat, lng, store_id, asr_raw, ai_polished }),
  // 语音转写文本的 AI 整理（转写本身由企微 translateVoice 在手机上完成，录音不上传）
  voicePolish: (text: string) => api.post("/api/homedecor/voice-polish", { text }, { timeout: 60000 }),
  // 名片截图：手机端传企微 serverId，电脑端直传 file；recognize_only=1 只识别不入档
  uploadCard: (contactId: number, opts: { serverId?: string; file?: File; recognizeOnly?: boolean }) => {
    const fd = new FormData();
    if (opts.serverId) fd.append("server_id", opts.serverId);
    if (opts.file) fd.append("file", opts.file);
    fd.append("recognize_only", opts.recognizeOnly ? "1" : "0");
    return api.post(`/api/homedecor/contacts/${contactId}/card`, fd, { timeout: 120000 });
  },
  // 名片图取回。**刻意不做成 `?token=` 的 <img src>**：令牌是全站通行证，一旦有人把
  // 图片链接转发出去，对方就拿到了他的账号（2026-08-17 创意工厂媒体已因此改签名URL）。
  // 名片又比产品图敏感——含个人信息、且有「只有跟进人和总监能看」的归属校验，
  // 所以也不适合退化成"拿到链接就能看"的路径签名。这里带 Authorization 头取二进制、
  // 前端转 blob URL：既不把凭据放进网址，也完整保留服务端的归属校验。
  cardBlob: async (storedName: string): Promise<string> => {
    const r = await api.get(`/api/homedecor/card/${storedName}`, { responseType: "blob" });
    return URL.createObjectURL(r.data as Blob);
  },
  // 考勤核对：筛跟进记录（业务员只见自己的；总监可见全部并按人筛）
  visits: (params: { follower?: string; visit_type?: string; date_from?: string;
                     date_to?: string; page?: number; page_size?: number }) =>
    api.get("/api/homedecor/visits", { params }),
  visitStats: (params: { date_from?: string; date_to?: string }) =>
    api.get("/api/homedecor/visits/stats", { params }),
  addContact: (id: number, data: any) => api.post(`/api/homedecor/customers/${id}/contacts`, data),
  updateContact: (contactId: number, data: any) => api.put(`/api/homedecor/contacts/${contactId}`, data),
  deleteContact: (contactId: number) => api.delete(`/api/homedecor/contacts/${contactId}`),
  setPrimary: (contactId: number) => api.put(`/api/homedecor/contacts/${contactId}/primary`),
  members: (include_disabled = false) =>
    api.get("/api/homedecor/members", { params: { include_disabled: include_disabled ? 1 : 0 } }),
  saveMember: (data: any) => api.post("/api/homedecor/members", data),
  // 字段级变更留痕：带 customer_id = 单客户（跟进人自己可看）；不带 = 全量（仅总监）
  audit: (params: { customer_id?: number; entity_type?: string; operator?: string; limit?: number }) =>
    api.get("/api/homedecor/audit", { params }),
  // 设客户坐标（拜访判定基准）。首次设置=可编辑者；改动已有坐标=仅总监
  setGeo: (id: number, lat: number, lng: number, source: "onsite" | "manual" = "onsite") =>
    api.post(`/api/homedecor/customers/${id}/geo`, { lat, lng, source }),
  // 该集团下的门店（写跟进时选「本条挂到哪家门店」）。频次考核按门店算，所以这一步
  // 是管理视角那张表能有数的前提。
  customerStores: (id: number) => api.get(`/api/homedecor/customers/${id}/stores`),
};

// 家装管理视角（团队客户看板 + AI 风险提示；仅业务总监）
export const homedecorMgmtAPI = {
  filterOptions: () => api.get("/api/homedecor/mgmt/filter-options"),
  // 多选一律 join(',') 传：后端 _as_list 两种都认，但统一走逗号便于拼导出链接
  teamStores: (p: {
    groups?: string[]; stores?: string[]; types?: string[]; keywords?: string[];
    date_from?: string; date_to?: string; overdue_only?: boolean;
    page?: number; page_size?: number;
  }) => api.get("/api/homedecor/mgmt/team-stores", {
    params: {
      groups: (p.groups || []).join(","), stores: (p.stores || []).join(","),
      types: (p.types || []).join(","), keywords: (p.keywords || []).join(","),
      date_from: p.date_from || "", date_to: p.date_to || "",
      overdue_only: p.overdue_only ? 1 : 0,
      page: p.page || 1, page_size: p.page_size || 50,
    },
  }),
  storeFollowups: (store_id: number, p: { page?: number; date_from?: string; date_to?: string } = {}) =>
    api.get("/api/homedecor/mgmt/store-followups", { params: { store_id, ...p } }),
  // 跑一次 20-60 秒（要过一遍 DeepSeek）；后端有 15 分钟缓存，refresh 才重算
  riskBrief: (refresh = false) =>
    api.post("/api/homedecor/mgmt/risk-brief", { refresh: refresh ? 1 : 0 }, { timeout: 180000 }),
};

// IT运维知识库（素材→DeepSeek提取→条目库→检索）
export const itkbAPI = {
  process: (text: string, filename = "", hint = "") =>
    api.post("/api/it-knowledge/process", { text, filename, hint }, { timeout: 300000 }),
  processFile: (file: File, hint = "") => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("hint", hint);
    return api.post("/api/it-knowledge/process-file", fd, { timeout: 300000 });
  },
  index: () => api.get("/api/it-knowledge/index"),
  version: () => api.get("/api/it-knowledge/version"),
  search: (query: string) => api.post("/api/it-knowledge/search", { query }),
  missing: () => api.get("/api/it-knowledge/missing"),
  items: (params: any) => api.get("/api/it-knowledge/items", { params }),
  confirm: (ids: number[]) => api.post("/api/it-knowledge/items/confirm", { ids }),
  updateItem: (id: number, data: any) => api.put(`/api/it-knowledge/items/${id}`, data),
  voidItem: (id: number) => api.post(`/api/it-knowledge/items/${id}/void`),
  exportUrl: () => `/api/it-knowledge/export-doc?token=${getToken()}`,
};

// IT物理文件资产索引（V7.0 file_index：源码/BI报表/故障记录/接口文档 → 文件级检索）
export const itAssetAPI = {
  search: (params: { q?: string; table?: string; ftype?: string; category?: string; limit?: number } = {}) =>
    api.get("/api/it-asset/search", { params }),
  stats: () => api.get("/api/it-asset/stats"),
  scan: (limit = 0) => api.post("/api/it-asset/scan", { limit }, { timeout: 300000 }),
  // 代码复用引擎（10.1）：AI 参考历史代码生成新代码，记录可追溯
  generate: (data: { description: string; kind: string; requester?: string }) =>
    api.post("/api/it-asset/generate", data, { timeout: 300000 }),
  genRecords: (params: { status?: string; kind?: string; limit?: number } = {}) =>
    api.get("/api/it-asset/gen-records", { params }),
  genDecide: (id: number, action: "adopted" | "rejected", feedback = "") =>
    api.post(`/api/it-asset/gen-records/${id}/decide`, { action, feedback }),
};

// OKKI（小满）客户智能分析（10.1）：同步/分层/Excel导入
export const okkiAPI = {
  stats: () => api.get("/api/okki/stats"),
  customers: (params: { tier?: string; q?: string; sales_rep?: string; page?: number; page_size?: number } = {}) =>
    api.get("/api/okki/customers", { params }),
  sync: () => api.post("/api/okki/sync", {}, { timeout: 600000 }),
  importExcel: (file: File) => {
    const fd = new FormData();
    fd.append("file", file);
    return api.post("/api/okki/import-excel", fd, { timeout: 300000 });
  },
};

// 家装门店档案 + AI健康度诊断 + 派单闭环（白皮书v3.0）
export const hdStoreAPI = {
  stores: (params: any = {}) => api.get("/api/homedecor-store/stores", { params }),
  saveStore: (data: any) => api.post("/api/homedecor-store/stores", data),
  deleteStore: (id: number) => api.post(`/api/homedecor-store/stores/${id}/delete`, {}),
  // 设门店坐标（拜访判定基准）。首次设置放开；改动已有坐标仅总监
  setStoreGeo: (id: number, lat: number, lng: number, source: "onsite" | "manual" = "onsite") =>
    api.post(`/api/homedecor-store/stores/${id}/geo`, { lat, lng, source }),
  stats: () => api.get("/api/homedecor-store/stats"),
  visits: (id: number, limit = 20) => api.get(`/api/homedecor-store/stores/${id}/visits`, { params: { limit } }),
  addVisit: (data: any) => api.post("/api/homedecor-store/visits", data),
  diagnose: (id: number) => api.post(`/api/homedecor-store/stores/${id}/diagnose`, {}, { timeout: 180000 }),
  diagnoseBatch: (limit = 30) => api.post("/api/homedecor-store/diagnose-batch", { limit }, { timeout: 600000 }),
  regionInsight: (province = "") => api.get("/api/homedecor-store/region-insight", { params: { province }, timeout: 180000 }),
  // 派单闭环
  dispatchOrders: (params: any = {}) => api.get("/api/homedecor-store/dispatch/orders", { params }),
  dispatchStats: () => api.get("/api/homedecor-store/dispatch/stats"),
  dispatchRequest: (data: any) => api.post("/api/homedecor-store/dispatch/request", data),
  dispatchReturn: (id: number, reason: string) => api.post(`/api/homedecor-store/dispatch/${id}/return`, { reason }),
  dispatchResubmit: (id: number, data: any) => api.post(`/api/homedecor-store/dispatch/${id}/resubmit`, data),
  dispatchSurvey: (id: number, note: string) => api.post(`/api/homedecor-store/dispatch/${id}/survey`, { note }),
  dispatchCompensation: (id: number, amount: number, reason: string) =>
    api.post(`/api/homedecor-store/dispatch/${id}/compensation`, { amount, reason }),
  refreshSla: () => api.post("/api/homedecor-store/dispatch/refresh-sla", {}),
  // 门店批量导入（仅总监）。两阶段：commit=false 只出试算报告，true 才落库。
  // updateExisting 默认 false——已存在的门店跳过不覆盖（别拿一次上传冲掉现场维护的资料）
  importFile: (file: File, commit: boolean, updateExisting = false) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("commit", commit ? "1" : "0");
    fd.append("update_existing", updateExisting ? "1" : "0");
    return api.post("/api/homedecor-store/stores/import", fd, { timeout: 180000 });
  },
  importTemplateUrl: () =>
    `/api/homedecor-store/stores/import/template?token=${getToken()}`,
  addCustomerTypes: (values: string[]) =>
    api.post("/api/homedecor-store/customer-types", { values }),
};

// 家装风控与考核（白皮书v3.0）：坏账防火墙 + 价值指数 + 服务部KPI
export const hdFinAPI = {
  credits: (params: any = {}) => api.get("/api/homedecor-finance/credits", { params }),
  saveCredit: (data: any) => api.post("/api/homedecor-finance/credits", data),
  syncT100: (id: number) => api.post(`/api/homedecor-finance/credits/${id}/sync-t100`, {}, { timeout: 120000 }),
  approve: (id: number, action: string, reason = "") =>
    api.post(`/api/homedecor-finance/credits/${id}/approve`, { action, reason }),
  receivables: (params: any = {}) => api.get("/api/homedecor-finance/receivables", { params }),
  saveReceivable: (data: any) => api.post("/api/homedecor-finance/receivables", data),
  refreshWarnings: () => api.post("/api/homedecor-finance/receivables/refresh-warnings", {}),
  collections: (id: number) => api.get(`/api/homedecor-finance/receivables/${id}/collections`),
  addCollection: (id: number, action: string, result = "") =>
    api.post(`/api/homedecor-finance/receivables/${id}/collections`, { action, result }),
  riskStats: () => api.get("/api/homedecor-finance/risk-stats"),
  valueIndex: (month = "") => api.get("/api/homedecor-finance/value-index", { params: { month } }),
  computeValue: (month: string, product_costs: any = {}, protected_persons: string[] = []) =>
    api.post("/api/homedecor-finance/value-index/compute", { month, product_costs, protected_persons }, { timeout: 180000 }),
  expenses: (month = "", person = "") => api.get("/api/homedecor-finance/expenses", { params: { month, person } }),
  addExpense: (data: any) => api.post("/api/homedecor-finance/expenses", data),
  serviceKpi: (month = "") => api.get("/api/homedecor-finance/service-kpi", { params: { month } }),
  saveKpi: (data: any) => api.post("/api/homedecor-finance/service-kpi", data),
};

// 公式配置中心（9.8第18章）：业务公式权重/阈值可热改，带留痕与回退
export const formulaAPI = {
  list: () => api.get("/api/formula/list"),
  get: (key: string) => api.get(`/api/formula/${key}`),
  save: (key: string, config: any, reason: string) =>
    api.post(`/api/formula/${key}`, { config, reason }),
  setStatus: (key: string, status: string, reason = "") =>
    api.post(`/api/formula/${key}/status`, { status, reason }),
  revert: (key: string, to_version: number, reason = "") =>
    api.post(`/api/formula/${key}/revert`, { to_version, reason }),
  history: (key: string, limit = 30) => api.get(`/api/formula/${key}/history`, { params: { limit } }),
};

// 后台管理（仅管理员；/me 任意登录用户可调用以过滤菜单）
export const adminAPI = {
  me: () => api.get("/api/admin/me"),
  modules: () => api.get("/api/admin/modules"),
  getLoginPolicy: () => api.get("/api/admin/login-policy"),
  setLoginPolicy: (mode: string, allow: { userid: string; name: string }[], block: { userid: string; name: string }[]) =>
    api.post("/api/admin/login-policy", { mode, allow, block }),
  getRoles: () => api.get("/api/admin/roles"),
  setRoles: (roles: Record<string, { role: string; modules: string[] | "all"; name: string }>) =>
    api.post("/api/admin/roles", { roles }),
  getConfig: () => api.get("/api/admin/config"),
  setConfig: (updates: Record<string, any>) => api.post("/api/admin/config", { updates }),
  getAudit: (date = "", limit = 200) => api.get("/api/admin/audit", { params: { date, limit } }),
  // 工单「分享/传阅」的可分享人白名单（仅管理员；保存即热加载生效，不用重启）
  getShareWhitelist: () => api.get("/api/admin/share-whitelist"),
  validateShareWhitelist: (names: string[]) => api.post("/api/admin/share-whitelist/validate", { names }),
  setShareWhitelist: (names: string[]) => api.post("/api/admin/share-whitelist", { names }),
};

// 出货查询（T100 直连，与 FineBI 同源）
export const shipAPI = {
  summary: (start: string, end: string) => api.get("/api/ship/summary", { params: { start, end }, timeout: 120000 }),
  shipments: (start: string, end: string, keyword = "", limit = 1000) =>
    api.get("/api/ship/shipments", { params: { start, end, keyword, limit }, timeout: 120000 }),
  modelDetail: (start: string, end: string, model: string) =>
    api.get("/api/ship/model-detail", { params: { start, end, model }, timeout: 120000 }),
};

// ── 京东产销看板（/api/jd-sales · WDT 京东系店铺 销/退聚合）────────
// 契约字段与后端约定一致，勿改：groups/series/top_products/notes
export type JdSalesGranularity = "day" | "week" | "month";
export interface JdSalesGroup {
  key: string;            // 店铺组键：jd_pop / jd_ziying / jingxi / jd_door / jd_other
  name: string;           // 组显示名，如「京东POP官方旗舰店」
  raw_shops: string[];    // 该组包含的原始店铺名（WDT 口径）
  orders: number;         // 订单量
  amount: number;         // 订单金额
  refunds: number;        // 退款单数
  refund_amount: number;  // 退款金额
  cancelled: number;      // 取消单数
}
export interface JdSalesSeriesPoint {
  bucket: string;         // 时间桶（按 granularity：日=YYYY-MM-DD / 周 / 月）
  group: string;          // 店铺组键
  orders: number;
  amount: number;
  refunds: number;
}
export interface JdSalesTopProduct { name: string; orders: number; amount: number }
export interface JdSalesOverviewResp {
  range: { start: string; end: string; granularity: JdSalesGranularity };
  groups: JdSalesGroup[];
  series: JdSalesSeriesPoint[];
  top_products: JdSalesTopProduct[];
  notes: string[];        // 口径注记（如：自营店历史单金额多为0）
}
export interface JdSalesShopGroup {
  key: string;
  name: string;
  shops: { shop: string; orders: number }[];   // 原始店铺名 → 组 的映射明细
}
export const jdSalesOverview = (params: { start: string; end: string; granularity: JdSalesGranularity; group?: string }) =>
  api.get<JdSalesOverviewResp>("/api/jd-sales/overview", { params, timeout: 120000 });
export const jdSalesShops = () =>
  api.get<{ groups: JdSalesShopGroup[] }>("/api/jd-sales/shops", { timeout: 60000 });

// 财务模块：科目余额表（T100 直连，复刻 aglq710）
export const financeAPI = {
  accountSets: () => api.get("/api/finance/account-sets", { timeout: 30000 }),
  accountBalance: (account_set: string, start: string, end: string, level = 99, show_control = false, status = "1") =>
    api.get("/api/finance/account-balance", { params: { account_set, start, end, level, show_control, status }, timeout: 120000 }),
  ledgerAccounts: (account_set: string, keyword = "") =>
    api.get("/api/finance/ledger-accounts", { params: { account_set, keyword }, timeout: 30000 }),
  ledger: (account_set: string, account: string, start: string, end: string, status = "1", show_analysis = true) =>
    api.get("/api/finance/ledger", { params: { account_set, account, start, end, status, show_analysis }, timeout: 120000 }),
  cashflow: (account_set: string, start: string, end: string, status = "1") =>
    api.get("/api/finance/cashflow", { params: { account_set, start, end, status }, timeout: 120000 }),
  channelProfit: (year: number, month: number) =>
    api.get("/api/finance/channel-profit", { params: { year, month }, timeout: 120000 }),
  hkShipments: (start: string, end: string) =>
    api.get("/api/finance/hk-shipments", { params: { start, end }, timeout: 120000 }),
  hkMargin: (start: string, end: string) =>
    api.get("/api/finance/hk-margin", { params: { start, end }, timeout: 120000 }),
  // 导出 Excel：拼带 token 的下载直链（中间件按 query token 鉴权）
  exportUrl: (report: "balance" | "ledger" | "cashflow" | "channel-profit" | "hk-shipments" | "hk-margin", params: Record<string, any>) => {
    const qs = new URLSearchParams({ ...params, token: getToken() } as any).toString();
    return `${API_BASE_URL}/api/finance/export/${report}?${qs}`;
  },
};

// 客服 copilot（客服助手 + 客服经理评分 + 聊天导入）
export const csAPI = {
  suggest: (message: string, shop = "", agent = "") =>
    api.post("/api/cs/suggest", { message, shop, agent }, { timeout: 120000 }),
  log: (payload: any) => api.post("/api/cs/log", payload),
  importChats: (file: File) => {
    const fd = new FormData();
    fd.append("file", file);
    return api.post("/api/cs/import", fd, { timeout: 120000 });
  },
  agents: (start: string, end: string) => api.get("/api/cs/agents", { params: { start, end } }),
  records: (agent: string, start: string, end: string) =>
    api.get("/api/cs/records", { params: { agent, start, end } }),
  score: (payload: any) => api.post("/api/cs/score", payload),
  ranking: (start: string, end: string) => api.get("/api/cs/ranking", { params: { start, end } }),
  // 产品图库
  listImages: () => api.get("/api/cs/images/list"),
  uploadImage: (file: File, caption: string, keywords: string, model: string) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("caption", caption);
    fd.append("keywords", keywords);
    fd.append("model", model);
    return api.post("/api/cs/images", fd, { timeout: 120000 });
  },
  deleteImage: (id: number) => api.delete(`/api/cs/images/${id}`),
  // <img> 用：图片接口在 /api 鉴权之后，<img> 不带头，用 token 查询参数
  imageSrc: (url: string) => `${API_BASE_URL}${url}?token=${getToken()}`,
};

// 万相台无界·投放数据导入 + 每日AI日报
export const wxtAPI = {
  importReport: (file: File) => {
    const fd = new FormData();
    fd.append("file", file);
    return api.post("/api/wxt/import", fd, { timeout: 120000 });
  },
  ingest: () => api.post("/api/wxt/ingest", null, { timeout: 120000 }),
  overview: (days = 7) => api.get("/api/wxt/overview", { params: { days } }),
  analyze: (days = 7) => api.post("/api/wxt/analyze", null, { params: { days }, timeout: 240000 }),
  latestReport: () => api.get("/api/wxt/report/latest"),
  getReport: (id: number) => api.get(`/api/wxt/report/${id}`),
  reports: (limit = 30) => api.get("/api/wxt/reports", { params: { limit } }),
  // 全店产品盈利：生意参谋(.xls必传) + 线索/短视频/无界(.csv可选)
  profit: (files: { sycm: File; lead?: File; video?: File; wujie?: File; mapping?: File }) => {
    const fd = new FormData();
    fd.append("sycm", files.sycm);
    if (files.lead) fd.append("lead", files.lead);
    if (files.video) fd.append("video", files.video);
    if (files.wujie) fd.append("wujie", files.wujie);
    if (files.mapping) fd.append("mapping", files.mapping);
    return api.post("/api/wxt/profit", fd, { timeout: 180000 });
  },
  profitExcelUrl: () => `${API_BASE_URL}/api/wxt/profit/excel?token=${getToken()}`,
  profitMappingTemplateUrl: () => `${API_BASE_URL}/api/wxt/profit/mapping-template?token=${getToken()}`,
  // 一站式批量导入：多文件自动识别 → 投放日报入库 + 全店盈利
  analyzeBundle: (files: File[]) => {
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    return api.post("/api/wxt/analyze-bundle", fd, { timeout: 240000 });
  },
};

// 工单中心（客户服务域 L2）
export const ticketAPI = {
  meta: () => api.get("/api/ticket/meta"),
  stats: (me = "") => api.get("/api/ticket/stats", { params: { me } }),
  list: (params: { status?: string; category?: string; assigned_to?: string; search?: string; start?: string; end?: string; page?: number; page_size?: number; include_test?: boolean; hide_urgent?: boolean; hide_closed?: boolean }) =>
    api.get("/api/ticket/tickets", { params }),
  create: (data: any) => api.post("/api/ticket/tickets", data),
  get: (id: number) => api.get(`/api/ticket/tickets/${id}`),
  addMessage: (id: number, content: string, attachment_url = "") => api.post(`/api/ticket/tickets/${id}/message`, { content, attachment_url }),
  uploadImage: (file: File) => { const fd = new FormData(); fd.append("file", file); return api.post("/api/ticket/upload-image", fd, { timeout: 120000 }); },
  imgSrc: (url: string) => `${API_BASE_URL}${url}${url.includes("?") ? "&" : "?"}token=${getToken()}`,
  setStatus: (id: number, status: string, resolution = "", result_tag = "") => api.post(`/api/ticket/tickets/${id}/status`, { status, resolution, result_tag }),
  learn: (id: number, force = false) => api.post(`/api/ticket/tickets/${id}/learn`, { force }, { timeout: 60000 }),
  aiSuggest: (id: number) => api.post(`/api/ticket/tickets/${id}/ai-suggest`, {}, { timeout: 120000 }),
  suggestFeedback: (id: number, body: { original: string; corrected: string; adopted: string }) =>
    api.post(`/api/ticket/tickets/${id}/suggest-feedback`, body, { timeout: 60000 }),
  markCase: (id: number, is_case: boolean) => api.post(`/api/ticket/tickets/${id}/mark-case`, { is_case }),
  similarCases: (id: number) => api.get(`/api/ticket/tickets/${id}/similar-cases`),
  assign: (id: number, assigned_to: string, handover = "") => api.post(`/api/ticket/tickets/${id}/assign`, { assigned_to, handover }),
  markTest: (id: number, is_test: boolean) => api.post(`/api/ticket/tickets/${id}/mark-test`, { is_test }),
  // 处理中改分类（升级为投诉/差评等）；改成差评/投诉会补推企微提醒
  changeCategory: (id: number, category: string, reason = "") =>
    api.post(`/api/ticket/tickets/${id}/category`, { category, reason }),
  // 同订单反复开单（「重复做第二次还一样」的证据，判据是 order_no）
  repeat: (id: number) => api.get(`/api/ticket/tickets/${id}/repeat`),
  // 分享/传阅：收件人 value 用 userid（按姓名推送遇到同名员工会不报错地推错人）
  shareCandidates: () => api.get("/api/ticket/share-candidates"),
  shares: (id: number) => api.get(`/api/ticket/tickets/${id}/shares`),
  //  need_solution 不传/为假 = 纯知会：后端不登记限期、不催办（用户 2026-09-18
  //  「不勾选就不填不催」）。due_hours 只在勾选时才传。
  share: (id: number, body: {
    to_userids: string[]; note: string;
    need_solution?: boolean; due_hours?: number;
  }) => api.post(`/api/ticket/tickets/${id}/share`, body),
  shareRead: (id: number) => api.post(`/api/ticket/tickets/${id}/share-read`, {}),
  // 被分享人提交结构化解决方案（不是"在工单里回句话"）
  submitSolution: (shareId: number, text: string) =>
    api.post(`/api/ticket/shares/${shareId}/solution`, { text }),
  // 发到企微群：群清单在 config/wecom_groups.json，webhook 地址在 .env
  shareToGroup: (id: number, body: { group: string; note: string; mention_owner?: boolean }) =>
    api.post(`/api/ticket/tickets/${id}/share-group`, body),
  // 复制群消息：群机器人只能在群里手工加、外部群加不了，所以给一条零配置的路
  shareText: (id: number, note = "") =>
    api.post(`/api/ticket/tickets/${id}/share-text`, { note }),
  // 审批：赔偿/货损/询单议价…（my_actions 由后端给，前端不自己算权限）
  approvals: (id: number) => api.get(`/api/ticket/tickets/${id}/approvals`),
  submitApproval: (id: number, body: { kind: string; amount?: number; reason: string }) =>
    api.post(`/api/ticket/tickets/${id}/approvals`, body),
  decideApproval: (approvalId: number, approve: boolean, note = "") =>
    api.post(`/api/ticket/approvals/${approvalId}/decide`, { approve, note }),
  escalateApproval: (approvalId: number, to: string, note = "") =>
    api.post(`/api/ticket/approvals/${approvalId}/escalate`, { to, note }),
  cleanupTest: (dry_run: boolean) => api.post("/api/ticket/cleanup-test", {}, { params: { dry_run }, timeout: 120000 }),
  customers: (search = "") => api.get("/api/ticket/customers", { params: { search } }),
  lookupOrder: (order_no: string) => api.get("/api/ticket/order-lookup", { params: { order_no }, timeout: 30000 }),
  startSummary: (status = "", category = "") => api.post("/api/ticket/ai-summary", {}, { params: { status, category } }),
  summaryStatus: (job_id: string) => api.get("/api/ticket/ai-summary/status", { params: { job_id } }),
};

// 工程资料中心（请款/结算/质保金·适配甲方模板自动生成）
export const engDocAPI = {
  stdFields: () => api.get("/api/engdoc/std-fields"),
  listTemplates: (doc_type = "", party = "", creator = "") =>
    api.get("/api/engdoc/templates", { params: { doc_type, party, creator } }),
  creators: () => api.get("/api/engdoc/creators"),   // 管理员"按业务员分类"下拉
  getTemplate: (id: number) => api.get(`/api/engdoc/templates/${id}`),
  uploadTemplate: (file: File, doc_type: string, party_name: string) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("doc_type", doc_type);
    fd.append("party_name", party_name);
    return api.post("/api/engdoc/templates/upload", fd, { timeout: 120000 });
  },
  saveMapping: (id: number, mapping: any[]) => api.post(`/api/engdoc/templates/${id}/mapping`, { mapping }),
  // 匹配界面可编辑：锚点整体保存（删/改名/手动添加） + 自定义标准字段 CRUD
  saveAnchors: (id: number, anchors: any[]) => api.post(`/api/engdoc/templates/${id}/anchors`, { anchors }),
  addCustomField: (label: string, category: string, ftype: string) =>
    api.post("/api/engdoc/custom-fields", { label, category, ftype }),
  renameCustomField: (key: string, label: string) => api.put(`/api/engdoc/custom-fields/${key}`, { label }),
  deleteCustomField: (key: string) => api.delete(`/api/engdoc/custom-fields/${key}`),
  // AI 驯化：已有甲方名下拉 / DeepSeek 识别可填位置(耗时30~120s,单独放大超时) / 勾选确认注入{{占位符}}生成驯化版
  parties: () => api.get("/api/engdoc/parties"),
  aiSuggest: (tid: number) => api.post(`/api/engdoc/templates/${tid}/ai-suggest`, {}, { timeout: 180000 }),
  // 预览图可视化驯化：页图(首次LO转换较慢) / <img>直链带token / AI建议文本定位钉子
  preview: (tid: number) => api.get(`/api/engdoc/templates/${tid}/preview`, { timeout: 120000 }),
  previewImageUrl: (tid: number, page: number) => `${API_BASE_URL}/api/engdoc/preview-image/${tid}/${page}?token=${getToken()}`,
  previewLocate: (tid: number, items: { key: string; text: string; page?: number }[]) =>
    api.post(`/api/engdoc/templates/${tid}/preview-locate`, { items }, { timeout: 60000 }),
  tame: (tid: number, suggestions: { i: number; mode: string; match_text: string; std_field: string; transform: string }[]) =>
    api.post(`/api/engdoc/templates/${tid}/tame`, { suggestions }, { timeout: 120000 }),
  listProjects: (keyword = "") => api.get("/api/engdoc/projects", { params: { keyword } }),
  projectData: (ref: string) => api.get(`/api/engdoc/projects/${ref}/data`),
  // 项目参考资料（合同等）：上传→AI抽取字段值→生成自动带出（每份文档一次LLM抽取，超时放宽）
  projectDocs: (project_ref: string) => api.get("/api/engdoc/project-docs", { params: { project_ref } }),
  uploadProjectDocs: (project_ref: string, files: File[]) => {
    const fd = new FormData();
    fd.append("project_ref", project_ref);
    files.forEach((f) => fd.append("files", f));
    return api.post("/api/engdoc/project-docs", fd, { timeout: 600000 });
  },
  deleteProjectDoc: (id: number) => api.delete(`/api/engdoc/project-docs/${id}`),
  generate: (id: number, data: { project_ref: string; project_name: string; variables: any }) =>
    api.post(`/api/engdoc/templates/${id}/generate`, data, { timeout: 120000 }),
  downloadUrl: (recordId: number) => `${API_BASE_URL}/api/engdoc/download/${recordId}?token=${getToken()}`,
  // 匹配落位预览：保存匹配后即时看【字段名】落在模板哪里（LO转换约3~10秒）
  matchPreview: (tid: number) => api.post(`/api/engdoc/templates/${tid}/match-preview`, {}, { timeout: 120000 }),
  matchPreviewImageUrl: (tid: number, page: number, ts: number) =>
    `${API_BASE_URL}/api/engdoc/match-preview-image/${tid}/${page}?token=${getToken()}&ts=${ts}`,
  attachmentChecklist: (doc_type: string) => api.get("/api/engdoc/attachment-checklist", { params: { doc_type } }),
  mergePackage: (recordId: number, files: File[], includeMain = true) => {
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    fd.append("include_main", includeMain ? "1" : "0");   // 需求5.3：主资料转PDF置于包最前
    return api.post(`/api/engdoc/records/${recordId}/merge-package`, fd, { timeout: 300000 });
  },
  packageUrl: (recordId: number) => `${API_BASE_URL}/api/engdoc/package/${recordId}?token=${getToken()}`,
  versions: (party: string, doc_type: string) => api.get("/api/engdoc/versions", { params: { party, doc_type } }),
  diff: (old_id: number, new_id: number) => api.get("/api/engdoc/diff", { params: { old_id, new_id } }),
  // 需求3.6 批量多项目 + 需求4.5 有效期
  generateBatch: (payload: { template_id?: number; set_id?: number; projects: any[]; variables: any }) =>
    api.post("/api/engdoc/generate-batch", payload, { timeout: 600000 }),
  tokenUrl: (path: string) => `${API_BASE_URL}${path}?token=${getToken()}`,   // 后端返回的相对下载路径 → 带token直链
  templateValidity: (id: number, effective_date: string, expiry_date: string) =>
    api.post(`/api/engdoc/templates/${id}/validity`, { effective_date, expiry_date }),
  setValidity: (id: number, effective_date: string, expiry_date: string) =>
    api.post(`/api/engdoc/sets/${id}/validity`, { effective_date, expiry_date }),
  // 整套(套)
  listSets: (doc_type = "", party = "", creator = "") =>
    api.get("/api/engdoc/sets", { params: { doc_type, party, creator } }),
  getSet: (id: number) => api.get(`/api/engdoc/sets/${id}`),
  uploadSet: (files: File[], doc_type: string, party_name: string, roles = "") => {
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    fd.append("doc_type", doc_type);
    fd.append("party_name", party_name);
    if (roles) fd.append("roles", roles);
    return api.post("/api/engdoc/sets/upload", fd, { timeout: 600000 });
  },
  generateSet: (id: number, data: { project_ref: string; project_name: string; variables: any }) =>
    api.post(`/api/engdoc/sets/${id}/generate`, data, { timeout: 300000 }),
  setDownloadUrl: (recordId: number) => `${API_BASE_URL}/api/engdoc/set-download/${recordId}?token=${getToken()}`,
};

// 物料承认书索引（域十五）
export const ackAPI = {
  list: (material_code = "", keyword = "") => api.get("/api/ack/list", { params: { material_code, keyword } }),
  upload: (file: File, fields: { material_code: string; material_name?: string; supplier?: string; ack_no?: string; version?: string; spec?: string }) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("material_code", fields.material_code);
    fd.append("material_name", fields.material_name || "");
    fd.append("supplier", fields.supplier || "");
    fd.append("ack_no", fields.ack_no || "");
    fd.append("version", fields.version || "");
    fd.append("spec", fields.spec || "");
    return api.post("/api/ack/upload", fd, { timeout: 300000 });
  },
  ask: (query: string, material_code = "", selected_codes: string[] | null = null) =>
    api.post("/api/ack/ask", { query, material_code, selected_codes }, { timeout: 180000 }),
  transfer: (query: string, rejected: string[] = []) =>
    api.post("/api/ack/transfer", { query, rejected }, { timeout: 60000 }),
  backfill: (limit = 200, series = "") =>
    api.post("/api/ack/backfill", { limit, series }, { timeout: 600000 }),
  downloadUrl: (id: number) => `${API_BASE_URL}/api/ack/download/${id}?token=${getToken()}`,
  // 台账有一半是 T100 回填的、盘上没原件；给可点链接前先批量问一遍，别让人点出 404
  available: (ids: number[]) =>
    api.get("/api/ack/available", { params: { ids: ids.join(",") } }),
};

// 统一知识库（10.3）：上传 → AI 识别 → 待审核区 → 管理员确认 → 正式入库
export const unifiedKbAPI = {
  // 上传要抽文本 + 跑模型识别分类，大文件慢，超时给足
  upload: (file: File, comment = "") => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("comment", comment);
    return api.post("/api/unified-kb/upload", fd, { timeout: 300000 });
  },
  // 我投的稿（任何登录用户）；pending 是审核视角，要管理员权限
  my: (limit = 50) => api.get("/api/unified-kb/my", { params: { limit } }),
  pending: (status = "", limit = 200) =>
    api.get("/api/unified-kb/pending", { params: { status, limit } }),
  // action: approve 批准入库 / reject 拒绝 / modify 改分类后待重审
  review: (data: {
    pending_id: string; action: "approve" | "reject" | "modify";
    domain?: string; category?: string; title?: string; comment?: string;
    //: 三/四级：**不传 = 沿用 AI 抽的；传空串 = 审核人显式清空**。
    //: 两者语义不同，别用 `|| ""` 把它们压平（后端 review 靠 None 区分）。
    sub_category?: string; topic?: string;
  }) => api.post("/api/unified-kb/review", data, { timeout: 120000 }),
  // 批量审核：**每条按它自己的分类走**，不接受统一的 domain/category
  //（那会一次性制造一批错分类）。每条 approve 都要向量化，超时给足。
  reviewBatch: (data: {
    pending_ids: string[]; action: "approve" | "reject"; comment?: string;
  }) => api.post("/api/unified-kb/review-batch", data, { timeout: 600000 }),
  stats: () => api.get("/api/unified-kb/stats"),

  // ── 产品型号注册表（2026-09-07）。型号是分类的第三个维度，不是 category：
  //    只登记「系列」（约 57 个），M6 PRO/PLUS 这类子型号由系列自动派生，
  //    出新子型号零维护。读对所有登录用户开放，写要审核权限。
  registry: () => api.get("/api/unified-kb/registry"),
  // 抠出来但注册表里没有的 token——注册表靠这个补全，否则永远缺
  registryUnknown: (limit = 500) =>
    api.get("/api/unified-kb/registry/unknown", { params: { limit } }),
  registryUpsert: (data: { key: string; display?: string; line: string; aliases?: string[] }) =>
    api.post("/api/unified-kb/registry/upsert", data),
  registryRemove: (key: string) =>
    api.post("/api/unified-kb/registry/remove", { key }),
  registryExternalBrand: (name: string) =>
    api.post("/api/unified-kb/registry/external-brand", { name }),

  // 三/四级分类标签（AI 自由抽的，不设白名单）。看已出现的标签+条数，
  // 把同义的合并掉（"安装" → "安装调试"）——靠事后合并收敛，而不是事前登记。
  levelTags: () => api.get("/api/unified-kb/level-tags"),
  levelTagMerge: (src: string, dst: string) =>
    api.post("/api/unified-kb/level-tags/merge", { src, dst }),

  // 清理被拒原件（满 N 天）+ 孤儿文件。平时计划任务每天 03:50 自动跑，
  // 这个是手动催一次；dry_run 只看会删什么。**只删盘上文件，审核记录留着**。
  purgeRejected: (dryRun = false) =>
    api.post("/api/unified-kb/purge-rejected", { dry_run: dryRun }, { timeout: 120000 }),
};

// 质量检验工作台（域四 IQC/OQC）
export const inspectionAPI = {
  meta: () => api.get("/api/inspection/meta"),
  // ── 批次级免检（10.4）。注意与「免检料白名单」是两件事：
  //    白名单 = 这个料号本来就不逐项检（螺丝垫片）；这里 = 该检的料这一批不检。
  skipReasons: () => api.get("/api/inspection/skip-reasons"),
  skip: (id: number, body: { reason_code: string; comment?: string; recheck_days?: number }) =>
    api.post(`/api/inspection/batches/${id}/skip`, body),
  skipCancel: (id: number, note = "") =>
    api.post(`/api/inspection/batches/${id}/skip-cancel`, { note }),
  recheckDone: (id: number, note = "") =>
    api.post(`/api/inspection/batches/${id}/recheck-done`, { note }),
  skipLogs: (params: { kind?: string; start?: string; end?: string; by?: string; status?: string }) =>
    api.get("/api/inspection/skip-logs", { params }),
  skipStats: (params: { kind?: string; start?: string; end?: string }) =>
    api.get("/api/inspection/skip-stats", { params }),
  recheckOverdue: () => api.get("/api/inspection/recheck-overdue"),
  // kind 必传：IQC/OQC 是两个独立页面，统计不带类型两边数字会一模一样
  stats: (kind?: string) => api.get("/api/inspection/stats", { params: { kind } }),
  list: (params: { kind?: string; status?: string; search?: string; page?: number }) =>
    api.get("/api/inspection/batches", { params }),
  create: (data: any) => api.post("/api/inspection/batches", data),
  get: (id: number) => api.get(`/api/inspection/batches/${id}`),
  saveResults: (id: number, items: any[]) => api.post(`/api/inspection/batches/${id}/results`, { items }),
  aiVerdict: (id: number) => api.post(`/api/inspection/batches/${id}/ai-verdict`, {}, { timeout: 120000 }),
  setStatus: (id: number, status: string, conclusion = "") => api.post(`/api/inspection/batches/${id}/status`, { status, conclusion }),
  addNegotiation: (id: number, role: string, content: string) => api.post(`/api/inspection/batches/${id}/negotiation`, { role, content }),
  // ── IQC 检验闭环（规格书 10.2-SUP-001）──
  iqcSync: (body: { material?: string; supplier?: string } = {}) =>
    api.post("/api/inspection/iqc/sync", body, { timeout: 120000 }),
  // checked: ""=待办口径 / "no"=未检 / "yes"=已检（2026-09-10 筛选项）
  iqcPending: (params: { status?: string; search?: string; page?: number; checked?: string }) =>
    api.get("/api/inspection/iqc/pending", { params }),
  iqcBatch: (id: number) => api.get(`/api/inspection/iqc/batch/${id}`),
  // 聚合四层标准要跑模型，慢；已生成过的会秒回（cached=true）
  iqcInstruction: (id: number, regenerate = false) =>
    api.get(`/api/inspection/iqc/instruction/${id}`, { params: { regenerate }, timeout: 300000 }),
  iqcSaveResult: (id: number, items: any[], submit = false) =>
    api.post(`/api/inspection/iqc/result/${id}`, { items, submit }),
  iqcImportResult: (id: number, file: File) => {
    const fd = new FormData();
    fd.append("file", file);
    return api.post(`/api/inspection/iqc/result/${id}/import`, fd, { timeout: 120000 });
  },
  // 改抽样数(留痕) / 录不良件数。两个都可选，只传要改的那个
  iqcSetCounts: (id: number, body: { sample_qty?: number; defect_pcs?: number }) =>
    api.post(`/api/inspection/iqc/counts/${id}`, body),
  iqcJudge: (id: number) => api.post(`/api/inspection/iqc/judge/${id}`, {}, { timeout: 60000 }),
  // 处置三选项：检验合格 / 挑选使用 / 不合格退货。与系统建议不一致时 note 必填
  iqcSetDisposition: (id: number, code: string, note = "") =>
    api.post(`/api/inspection/iqc/disposition/${id}`, { code, note }),
  // 录完一步到位：自动判定 + 落处置（免得三选项藏在"提交判定"之后没人看见）
  iqcJudgeDispose: (id: number, code: string, note = "") =>
    api.post(`/api/inspection/iqc/judge-dispose/${id}`, { code, note }, { timeout: 60000 }),
  // OQC 型号功能配置（八项必检的精确取舍依据）
  oqcModelFeatures: (search = "") =>
    api.get("/api/inspection/oqc/model-features", { params: { search } }),
  oqcSaveModelFeature: (model: string, features: Record<string, string>, note = "") =>
    api.post("/api/inspection/oqc/model-features", { model, features, note }),
  oqcDeleteModelFeature: (id: number) =>
    api.delete(`/api/inspection/oqc/model-features/${id}`),
  oqcImportModelFeatures: (overwrite_manual = false) =>
    api.post("/api/inspection/oqc/model-features/import", { overwrite_manual },
             { timeout: 120000 }),
  iqcSetStatus: (id: number, status: string, conclusion = "") =>
    api.post(`/api/inspection/iqc/status/${id}`, { status, conclusion }),
  samplingPlan: (lot_size: number, aql?: number) =>
    api.get("/api/inspection/sampling-plan", { params: { lot_size, aql } }),
  // ── OQC 出货检验闭环（阶段2）──
  oqcSync: (body: { customer?: string; model?: string } = {}) =>
    api.post("/api/inspection/oqc/sync", body, { timeout: 120000 }),
  oqcPending: (params: { status?: string; search?: string; page?: number; checked?: string }) =>
    api.get("/api/inspection/oqc/pending", { params }),
  // 按订单聚合（2026-08-24）：一张订单一行，点开里面是这张单的全部型号
  // checked="yes" 时 = 整单型号全部检完（不是"有已检型号"）
  oqcOrders: (params: { status?: string; search?: string; page?: number; checked?: string }) =>
    api.get("/api/inspection/oqc/orders", { params }),
  // order_no 走 query：订单号可能带斜杠、也可能是中文占位（"备货出货"）
  oqcOrder: (orderNo: string) =>
    api.get("/api/inspection/oqc/order", { params: { order_no: orderNo } }),
  oqcOrderExport: (orderNo: string) =>
    api.get("/api/inspection/oqc/order/export",
            { params: { order_no: orderNo }, responseType: "blob", timeout: 180000 }),
  oqcBatch: (id: number) => api.get(`/api/inspection/oqc/batch/${id}`),
  oqcImportResult: (id: number, file: File) => {
    const form = new FormData();
    form.append("file", file);
    return api.post(`/api/inspection/oqc/result/${id}/import`, form);
  },
  oqcScheme: (id: number, regenerate = false) =>
    api.get(`/api/inspection/oqc/scheme/${id}`, { params: { regenerate }, timeout: 300000 }),
  oqcSaveResult: (id: number, items: any[], submit = false) =>
    api.post(`/api/inspection/oqc/result/${id}`, { items, submit }),
  oqcJudge: (id: number) => api.post(`/api/inspection/oqc/judge/${id}`, {}, { timeout: 60000 }),
  oqcSetStatus: (id: number, status: string, conclusion = "") =>
    api.post(`/api/inspection/oqc/status/${id}`, { status, conclusion }),
  // ── 标准人工层：免检白名单 + 就地补录 + AI起草（2026-08-14）──
  stdExemptions: (kind = "") => api.get("/api/inspection/std/exemptions", { params: { kind } }),
  stdExemptionSave: (body: { pattern: string; kind: string; label?: string; note?: string; is_active?: boolean }) =>
    api.post("/api/inspection/std/exemptions", body),
  stdExemptionToggle: (id: number, isActive: boolean) =>
    api.post(`/api/inspection/std/exemptions/${id}/toggle`, { is_active: isActive }),
  stdManualGet: (materialCode: string, kind: string) =>
    api.get("/api/inspection/std/manual", { params: { material_code: materialCode, kind } }),
  stdManualSave: (body: any) => api.post("/api/inspection/std/manual", body),
  // AI 起草只返回草稿不落库，落库永远是质检员点保存
  stdDraft: (body: { material_code: string; material_name?: string; spec?: string; kind: string }) =>
    api.post("/api/inspection/std/draft", body, { timeout: 180000 }),
};

// 产品配置防错（域三）
export const pcfgAPI = {
  products: () => api.get("/api/pcfg/products"),
  params: (product: string) => api.get("/api/pcfg/params", { params: { product } }),
  saveParam: (data: any) => api.post("/api/pcfg/params", data),
  delParam: (id: number) => api.delete(`/api/pcfg/params/${id}`),
  rules: (product: string) => api.get("/api/pcfg/rules", { params: { product } }),
  saveRule: (data: any) => api.post("/api/pcfg/rules", data),
  delRule: (id: number) => api.delete(`/api/pcfg/rules/${id}`),
  validate: (product: string, selections: any) => api.post("/api/pcfg/validate", { product, selections }),
  createInstance: (product: string, customer: string, selections: any) => api.post("/api/pcfg/instances", { product, customer, selections }),
  instances: (product = "", search = "") => api.get("/api/pcfg/instances", { params: { product, search } }),
};

// 师傅网络（域二）
export const techAPI = {
  meta: () => api.get("/api/tech/meta"),
  stats: () => api.get("/api/tech/stats"),
  technicians: (params: any = {}) => api.get("/api/tech/technicians", { params }),
  saveTechnician: (data: any) => api.post("/api/tech/technicians", data),
  delTechnician: (id: number) => api.delete(`/api/tech/technicians/${id}`),
  teams: () => api.get("/api/tech/teams"),
  saveTeam: (data: any) => api.post("/api/tech/teams", data),
  orders: (params: any = {}) => api.get("/api/tech/orders", { params }),
  createOrder: (data: any) => api.post("/api/tech/orders", data),
  getOrder: (id: number) => api.get(`/api/tech/orders/${id}`),
  dispatch: (id: number, technician_id: number) => api.post(`/api/tech/orders/${id}/dispatch`, { technician_id }),
  advance: (id: number, action: string, data: any = {}) => api.post(`/api/tech/orders/${id}/advance`, { action, ...data }),
  knowledge: (params: any = {}) => api.get("/api/tech/knowledge", { params }),
  saveKnowledge: (data: any) => api.post("/api/tech/knowledge", data),
  delKnowledge: (id: number) => api.delete(`/api/tech/knowledge/${id}`),
};

// BOM 工程（域六：工程状态 + 变更日志ECN + 替代料持久化）
export const bomEngAPI = {
  meta: () => api.get("/api/bom-eng/meta"),
  stats: () => api.get("/api/bom-eng/stats"),
  headers: (params: any = {}) => api.get("/api/bom-eng/headers", { params }),
  saveHeader: (data: any) => api.post("/api/bom-eng/headers", data),
  setHeaderStatus: (id: number, status: string) => api.post(`/api/bom-eng/headers/${id}/status`, { status }),
  delHeader: (id: number) => api.delete(`/api/bom-eng/headers/${id}`),
  changes: (params: any = {}) => api.get("/api/bom-eng/changes", { params }),
  addChange: (data: any) => api.post("/api/bom-eng/changes", data),
  reviewChange: (id: number, approve: boolean) => api.post(`/api/bom-eng/changes/${id}/review`, { approve }),
  delChange: (id: number) => api.delete(`/api/bom-eng/changes/${id}`),
  substitutes: (params: any = {}) => api.get("/api/bom-eng/substitutes", { params }),
  saveSubstitute: (data: any) => api.post("/api/bom-eng/substitutes", data),
  toggleSubstitute: (id: number) => api.post(`/api/bom-eng/substitutes/${id}/toggle`),
  delSubstitute: (id: number) => api.delete(`/api/bom-eng/substitutes/${id}`),
};

// ITOps 自监控（系统体检）
export const itopsAPI = {
  status: () => api.get("/api/itops/status"),
  run: () => api.post("/api/itops/run", {}, { timeout: 60000 }),
  ack: (id: number) => api.post(`/api/itops/alerts/${id}/ack`),
  diagnose: (id: number) => api.post(`/api/itops/alerts/${id}/diagnose`, {}, { timeout: 120000 }),
  heal: (id: number) => api.post(`/api/itops/alerts/${id}/heal`),
  kbAsk: (query: string) => api.post("/api/itops/kb/ask", { query }, { timeout: 120000 }),
  kbIndexCode: () => api.post("/api/itops/kb/index-code", {}, { timeout: 600000 }),
  kbIndexFaults: () => api.post("/api/itops/kb/index-faults", {}, { timeout: 120000 }),
  kbStats: () => api.get("/api/itops/kb/stats"),
};

// AI建议采纳追踪（域一 knowledge_suggestion）
export const ksugAPI = {
  meta: () => api.get("/api/ksug/meta"),
  stats: (scene = "") => api.get("/api/ksug/stats", { params: { scene } }),
  list: (params: any = {}) => api.get("/api/ksug/list", { params }),
  record: (data: any) => api.post("/api/ksug/record", data),
  decide: (id: number, adopted: string, final_answer = "", note = "") =>
    api.post(`/api/ksug/${id}/decide`, { adopted, final_answer, note }),
  pushKb: (id: number) => api.post(`/api/ksug/${id}/push-kb`, {}, { timeout: 60000 }),
  // AI 预分类：给待定项打「建议裁决 + 纠正合并草稿」，只给建议不生效（慢，给足超时）
  aiTriage: (scene = "", limit = 30) =>
    api.post("/api/ksug/ai-triage", { scene, limit }, { timeout: 300000 }),
  // 批量裁决：采纳会顺手入库，逐条回报结果
  batchDecide: (items: { id: number; adopted: string; final_answer?: string; note?: string }[]) =>
    api.post("/api/ksug/batch-decide", { items }, { timeout: 300000 }),
  // 裁决答案附件：上传图片/视频/pdf → {url, kind, name}，以 markdown 插入答案文本随沉淀进知识库
  uploadKbFile: (file: File) => {
    const fd = new FormData();
    fd.append("file", file);
    return api.post("/api/kb/upload-file", fd, { timeout: 300000 });
  },
  del: (id: number) => api.delete(`/api/ksug/${id}`),
  startSummary: (scene = "", adopted = "") =>
    api.post("/api/ksug/ai-summary", {}, { params: { scene, adopted } }),
  summaryStatus: (job_id: string) =>
    api.get("/api/ksug/ai-summary/status", { params: { job_id } }),
};

// 微信模板消息主动通知（域八）
export const wxNotifyAPI = {
  meta: () => api.get("/api/wxnotify/meta"),
  stats: () => api.get("/api/wxnotify/stats"),
  templates: () => api.get("/api/wxnotify/templates"),
  saveTemplate: (data: any) => api.post("/api/wxnotify/templates", data),
  toggleTemplate: (id: number) => api.post(`/api/wxnotify/templates/${id}/toggle`),
  delTemplate: (id: number) => api.delete(`/api/wxnotify/templates/${id}`),
  messages: (params: any = {}) => api.get("/api/wxnotify/messages", { params }),
  trigger: (data: any) => api.post("/api/wxnotify/trigger", data),
  resend: (id: number) => api.post(`/api/wxnotify/messages/${id}/resend`),
  users: (search = "") => api.get("/api/wxnotify/users", { params: { search } }),
  bindUser: (data: any) => api.post("/api/wxnotify/users", data),
  delUser: (id: number) => api.delete(`/api/wxnotify/users/${id}`),
};

// 运营留痕（域五排产持久化 / 域七渠道订单扩展 / 域九GEO发布日志）
export const opsAPI = {
  meta: () => api.get("/api/ops/meta"),
  snapshotSchedule: () => api.post("/api/ops/schedule/snapshot", {}, { timeout: 360000 }),
  scheduleMasters: (limit = 60) => api.get("/api/ops/schedule/masters", { params: { limit } }),
  scheduleDetail: (id: number) => api.get(`/api/ops/schedule/masters/${id}`),
  delScheduleMaster: (id: number) => api.delete(`/api/ops/schedule/masters/${id}`),
  orderExt: (params: any = {}) => api.get("/api/ops/order-ext", { params }),
  saveOrderExt: (data: any) => api.post("/api/ops/order-ext", data),
  delOrderExt: (id: number) => api.delete(`/api/ops/order-ext/${id}`),
  geoLogs: (params: any = {}) => api.get("/api/ops/geo-logs", { params }),
  setGeoLogUrl: (id: number, url: string) => api.post(`/api/ops/geo-logs/${id}/url`, { url }),
};

// 订单跟单（客服智能门订单按服务节点跟单 + 考核）
export const orderTrackAPI = {
  meta: () => api.get("/api/order-track/meta"),
  stats: () => api.get("/api/order-track/stats"),
  list: (params: any = {}) => api.get("/api/order-track/list", { params }),
  get: (id: number) => api.get(`/api/order-track/${id}`),
  create: (data: { order_no: string; owner?: string; note?: string }) =>
    api.post("/api/order-track/create", data, { timeout: 30000 }),
  advance: (id: number, data: { handler?: string; content?: string; remark?: string }) =>
    api.post(`/api/order-track/${id}/advance`, data),
  cancel: (id: number, reason = "") => api.post(`/api/order-track/${id}/cancel`, { reason }),
  saveNodes: (nodes: any[]) => api.post("/api/order-track/nodes", { nodes }),
  importMis: (days = 7, limit = 200) => api.post("/api/order-track/import", { days, limit }, { timeout: 60000 }),
  wdtShipment: (order_no: string) => api.get("/api/order-track/wdt-shipment", { params: { order_no }, timeout: 40000 }),
  wdtSync: (id: number) => api.post(`/api/order-track/${id}/wdt-sync`, {}, { timeout: 40000 }),
  wdtBatchStart: (params: { seq?: number; owner?: string; search?: string; limit?: number } = {}) =>
    api.post("/api/order-track/wdt-sync-batch", {}, { params }),
  wdtBatchStatus: (job_id: string) => api.get("/api/order-track/wdt-sync-batch/status", { params: { job_id } }),
};

export const creativeAPI = {
  mixAdoptSample: (planId: string, mixId: string, checks: Record<string, boolean>) =>
    api.post('/api/creative/forge/mix-plan/' + planId + '/adopt-sample', { mix_id: mixId, checks }),
  domesticImageCapability: () => api.get("/api/creative/domestic/images/capability"),
  domesticImages: () => api.get("/api/creative/domestic/images"),
  domesticImage: (id: string) => api.get(`/api/creative/domestic/images/${id}`),
  domesticImageSave: (body: any) => api.post("/api/creative/domestic/images", body),
  domesticImageSubmit: (id: string, version: number) =>
    api.post(`/api/creative/domestic/images/${id}/submit`, { version }),
  domesticImageReconcile: (id: string, note: string) =>
    api.post(`/api/creative/domestic/image-task/${id}/reconcile`, { note }),
  styles: () => api.get("/api/creative/styles"),
  models: () => api.get("/api/creative/models"),
  // 出图较慢(同步)，超时放宽到 4 分钟
  // 文生图/图生图：multipart（不传图=文生图，可带 size；传图=图生图批量）
  generate: (form: FormData) => api.post("/api/creative/generate", form, { timeout: 240000 }),
  // Playground：迭代修图 / 多图融合 / 局部重绘 / 多模型对比（均 multipart）
  edit: (form: FormData) => api.post("/api/creative/edit", form, { timeout: 240000 }),
  fuse: (form: FormData) => api.post("/api/creative/fuse", form, { timeout: 240000 }),
  inpaint: (form: FormData) => api.post("/api/creative/inpaint", form, { timeout: 240000 }),
  compare: (form: FormData) => api.post("/api/creative/compare", form, { timeout: 300000 }),
  // 视频异步：multipart 提交(可带参考图,批量)，立即返回任务；再轮询 poll
  generateVideo: (form: FormData) => api.post("/api/creative/generate-video", form, { timeout: 180000 }),
  pollVideos: () => api.get("/api/creative/poll", { timeout: 180000 }),
  tasks: (limit = 30) => api.get("/api/creative/tasks", { params: { limit } }),
  // ── 2026-08-17 对话式创意工厂用的三个接口 ──
  // 公共画廊：全公司做过的图/视频，所有登录用户都看得到
  gallery: (params: { page?: number; size?: number; type?: string; q?: string; by?: string;
                      include_failed?: boolean }) =>
    api.get("/api/creative/gallery", { params }),
  // 视频确认闸：Agent 只摆参数，用户点这里才真提交、才扣费
  confirmVideo: (task_id: string, approve = true) =>
    api.post("/api/creative/confirm-video", { task_id, approve }, { timeout: 180000 }),
  quota: () => api.get("/api/creative/quota"),
  // 产品图库检索（素材栏）：只要登录即可，不像 csAPI.listImages 需要客服经理权限
  kbImages: (q = "", limit = 60) => api.get("/api/creative/kb-images", { params: { q, limit } }),
  // 手动重查视频（救回被超时误判失败、但上游其实已完成的片子）
  videoRecheck: (taskId: string) =>
    api.post(`/api/creative/video/${taskId}/recheck`, null, { timeout: 180000 }),
  // ── 风格预设库 ──
  presets: (kind = "", includeDisabled = true) =>
    api.get("/api/creative/presets", { params: { kind, include_disabled: includeDisabled } }),
  presetSave: (body: { kind?: string; key?: string; name?: string; prompt_tmpl?: string;
                       size?: string; note?: string; sort?: number; enabled?: boolean }) =>
    api.post("/api/creative/presets", body),
  presetDelete: (kind: string, key: string) =>
    api.delete("/api/creative/presets", { params: { kind, key } }),
  // ── 批量出图 ──
  batchOptions: () => api.get("/api/creative/batch-options"),
  // 展开任务计划（不出图不花钱）
  batchPlan: (body: { models?: string[]; styles?: string[]; size?: string; n_per_item?: number;
                      prompt_extra?: string; auto_ref?: boolean; rows?: any[] }) =>
    api.post("/api/creative/batch/plan", body, { timeout: 180000 }),
  batchSubmit: (body: { items: any[]; naming?: string; name?: string }) =>
    api.post("/api/creative/batch", body, { timeout: 120000 }),
  batchList: (limit = 20) => api.get("/api/creative/batches", { params: { limit } }),
  batchStatus: (batchId: string) => api.get(`/api/creative/batch/${batchId}`),
  batchRetry: (batchId: string) => api.post(`/api/creative/batch/${batchId}/retry`),
  // zip 走浏览器直接下载 → 需要把令牌挂在 query 上（<a download> 发不出 header）
  batchZipUrl: (batchId: string) =>
    `${API_BASE_URL}/api/creative/batch/${batchId}/zip?token=${encodeURIComponent(getToken() || "")}`,
  batchImport: (file: File) => {
    const fd = new FormData();
    fd.append("file", file);
    return api.post("/api/creative/batch/import-file", fd, { timeout: 120000 });
  },
  // 上传本地图当参考图，拿回 file:xxx.png 引用（Agent 工具只认引用）
  uploadRef: (files: File[]) => {
    const fd = new FormData();
    files.forEach((f) => fd.append("images", f));
    return api.post("/api/creative/upload-ref", fd, { timeout: 180000 });
  },
  // 媒体地址：后端返回的已经是**带 HMAC 签名**的相对地址（?exp=&sig=），直接用。
  // 2026-08-17 前这里给每个 url 拼 ?token=<登录令牌> —— 用户随手转发一张图的链接
  // 就等于转发了账号。现在只在极少数还没签名的老路径上兜底加令牌。
  mediaUrl: (url: string) => {
    if (!url) return "";
    if (url.includes("sig=")) return `${API_BASE_URL}${url}`;      // 已签名
    return `${API_BASE_URL}${url}${url.includes("?") ? "&" : "?"}token=${encodeURIComponent(getToken() || "")}`;
  },
  // 批次打包：先鉴权换一条 10 分钟有效的签名链接，再让浏览器去下
  batchZipLink: (batchId: string) =>
    api.post(`/api/creative/batch/${batchId}/zip-link`, null, { timeout: 180000 }),

  // ── 视频拆解与三稿（双工作台接入第 1 期）──
  forgeHealth: () => api.get("/api/creative/forge/health"),
  // 能力自检：缺哪个模型要在界面上说清楚，不静默降级成假分析
  contentAiCapability: () => api.get("/api/creative/content-ai/capability"),
  // 上传素材（服务端按魔术字节判类型、流式写盘，视频落盘后自动压到 720p）。
  // `onUploadProgress` 只覆盖**浏览器→服务器那一段字节**：它到 100% 不等于完成，
  // 之后还有服务端压缩（一两分钟），而那一段 HTTP 层测不到。界面必须把这两段
  // 分开显示，否则进度条停在 100% 不动，看着比转圈更像卡死。
  contentAiUpload: (files: File[],
                    onUploadProgress?: (percent: number, loaded: number,
                                        total: number) => void) => {
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    return api.post("/api/creative/content-ai/upload", fd, {
      timeout: 600000,
      onUploadProgress: onUploadProgress
        ? (e) => {
          // `e.total` 在少数环境下拿不到（没有 Content-Length）。
          // 拿不到就报 -1，让界面退回"不确定进度"而不是显示 NaN%
          const total = e.total || 0;
          const pct = total > 0 ? Math.round((e.loaded / total) * 100) : -1;
          onUploadProgress(pct, e.loaded || 0, total);
        }
        : undefined,
    });
  },
  // ⚠ 旧的一次跑完入口（三段 + 三稿），**已弃用**。新链路见下面两条
  contentAiAnalyze: (assetFile: string, productId = "") =>
    api.post("/api/creative/content-ai/analyze",
      { asset_file: assetFile, product_id: productId }, { timeout: 60000 }),

  // ── 两阶段视频拆解（第 5 期①）──
  // 第一步：**只拆原片、不绑产品**。请求体刻意只有 asset_file——
  // 界面有了全局产品态之后，顺手把当前产品带进来是极其自然的写法，
  // 而那会让「原片在卖什么」被产品资料带跑偏（服务端也不读这个字段）
  contentAiSourceAnalyze: (assetFile: string) =>
    api.post("/api/creative/content-ai/source-analyze",
      { asset_file: assetFile }, { timeout: 60000 }),
  // 第二步：选产品出参考复刻 + 创意改编两稿（并行，不重复拆视频）
  contentAiDraft: (recordId: string, productId: string) =>
    api.post("/api/creative/content-ai/draft",
      { record_id: recordId, product_id: productId }, { timeout: 60000 }),

  // ── 图文参考复刻三步（第 5 期②）──
  contentAiFramecopyAnalyze: (assetFile: string) =>
    api.post("/api/creative/content-ai/framecopy/analyze",
      { asset_file: assetFile }, { timeout: 60000 }),
  contentAiFramecopyDraft: (recordId: string, productId: string) =>
    api.post("/api/creative/content-ai/framecopy/draft",
      { record_id: recordId, product_id: productId }, { timeout: 60000 }),

  // ── 原素材删除（第 5 期④）。三层语义见后端 source_asset.remove ──
  contentAiAssetUsage: (filename: string) =>
    api.get(`/api/creative/content-ai/asset/${encodeURIComponent(filename)}/usage`),
  contentAiAssetDelete: (filename: string, confirm = false) =>
    api.delete(`/api/creative/content-ai/asset/${encodeURIComponent(filename)}`,
      { params: { confirm: confirm ? 1 : 0 } }),

  // 出视频能力自检：未验证的参数界面要标出来、不给承诺（第 5 期③）
  videoCapability: (probe = false) =>
    api.get("/api/creative/video/capability", { params: { probe: probe ? 1 : 0 } }),
  contentAiRecords: (limit = 20, mine = false) =>
    api.get("/api/creative/content-ai/records", { params: { limit, mine: mine ? 1 : 0 } }),
  contentAiRecord: (recordId: string) =>
    api.get(`/api/creative/content-ai/record/${recordId}`),
  // 保存人工改稿。后端仍会过完整校验（人也可能把三稿改成一个样），且改完要重新确认
  contentAiSaveEdits: (recordId: string, drafts: any) =>
    api.patch(`/api/creative/content-ai/record/${recordId}`, { drafts },
      { timeout: 60000 }),
  contentAiConfirm: (recordId: string) =>
    api.post(`/api/creative/content-ai/record/${recordId}/confirm`),
  // 图文三稿（第 2 期）：mode=reference 要参考图；mode=original 用创意简报
  contentAiFramecopy: (body: { mode: string; asset_file?: string;
                               product_id?: string; brief?: string }) =>
    api.post("/api/creative/content-ai/framecopy", body, { timeout: 60000 }),
  // 出图模型选项：**无权的也会返回并说明原因**，界面据此置灰而不是悄悄换模型
  contentAiImageModels: () => api.get("/api/creative/content-ai/image-models"),
  // 按已确认稿的某个方向出图。入口只有这条，服务端第一步就是审核门
  contentAiGenerate: (recordId: string, body: { direction_id: string;
                                                model_key?: string; n?: number;
                                                refs?: string }) =>
    api.post(`/api/creative/content-ai/record/${recordId}/generate`, body,
      { timeout: 300000 }),
  // 画册产品资料（只读，每条都标着「资料记载」）
  catalogSearch: (q = "", category = "", limit = 20) =>
    api.get("/api/creative/catalog/search", { params: { q, category, limit } }),
  catalogProduct: (ident: string) =>
    api.get(`/api/creative/catalog/product/${encodeURIComponent(ident)}`),

  // ── 内容资产库与混剪成片（双工作台接入第 3 期）──
  // 普通检索**刻意不返回**来源/授权/审核/调用次数——那些是后台审计字段，
  // 界面上不显示也不要求同事维护（交付方 0828 的 UI 契约）。要看走 assetsAudit
  assetsMeta: () => api.get("/api/creative/assets/meta"),
  // 分镜用途可由同事补充（2026-09-04）。**没有删除接口**——已入库素材存的是
  // 用途名字符串，删掉会让它们变孤儿值、按用途筛时凭空消失
  purposeAdd: (body: { name: string; note?: string; visual_task?: string }) =>
    api.post("/api/creative/assets/purposes", body),
  purposeToggle: (name: string, active: boolean) =>
    api.patch(`/api/creative/assets/purposes/${encodeURIComponent(name)}`,
      { active }),
  assets: (p: { media_type?: string; purpose?: string; product?: string;
                q?: string; limit?: number }) =>
    api.get("/api/creative/assets", { params: p }),
  assetsByPurpose: (product = "", media_type = "video") =>
    api.get("/api/creative/assets/by-purpose", { params: { product, media_type } }),
  assetsAudit: (p: { media_type?: string; purpose?: string; product?: string;
                     q?: string; limit?: number }) =>
    api.get("/api/creative/assets/audit", { params: p }),
  // 直接入库：文件先走 contentAiUpload 落到 data/creative，这里只登记 object_key
  //（**不复制文件**——这是"入库"的定义，见后端 intake.py）
  assetImport: (body: { object_key: string; product_id?: string;
                        storyboard_purpose: string; source_type?: string;
                        product_name?: string; name?: string }) =>
    api.post("/api/creative/assets/import", body, { timeout: 60000 }),
  assetReviewIntake: (body: { task_id: string; file_index?: number;
                              product_id?: string; storyboard_purpose: string;
                              product_name?: string; name?: string }) =>
    api.post("/api/creative/assets/review-intake", body, { timeout: 60000 }),
  assetUpdate: (assetId: string, body: any) =>
    api.patch(`/api/creative/assets/${assetId}`, body),

  // 媒体加工能力：FFmpeg / 中文字体 / 配音，缺什么界面上要说清楚
  forgeCapability: () => api.get("/api/creative/forge/capability"),
  forgeMix: (body: { asset_ids: string[]; name?: string; product_id?: string;
                     product_name?: string }) =>
    api.post("/api/creative/forge/mix", body, { timeout: 60000 }),
  forgeFinalize: (body: { mix_id?: string; source_object_key?: string;
                          sticker_preset?: string; end_marker?: boolean;
                          script_id?: string; name?: string }) =>
    api.post("/api/creative/forge/finalize", body, { timeout: 60000 }),
  forgeJob: (jobId: string) => api.get(`/api/creative/forge/job/${jobId}`),
  forgeJobs: (limit = 20, job_type = "", mine = false) =>
    api.get("/api/creative/forge/jobs",
      { params: { limit, job_type, mine: mine ? 1 : 0 } }),
  forgeJobRetry: (jobId: string) =>
    api.post(`/api/creative/forge/job/${jobId}/retry`),
  forgeMixes: (limit = 20) =>
    api.get("/api/creative/forge/mixes", { params: { limit } }),
  forgeScriptNew: (mix_id: string, variant = -1) =>
    api.post("/api/creative/forge/script", { mix_id, variant }, { timeout: 60000 }),
  forgeScripts: (mix_id: string) =>
    api.get("/api/creative/forge/scripts", { params: { mix_id } }),
  forgeScript: (scriptId: string) =>
    api.get(`/api/creative/forge/script/${scriptId}`),
  forgeScriptSave: (scriptId: string, body: any) =>
    api.patch(`/api/creative/forge/script/${scriptId}`, body),
  forgeFinalVideos: (limit = 20) =>
    api.get("/api/creative/forge/final-videos", { params: { limit } }),

  // ── 脚本先行混剪（0904 智能混剪补充包，第 6 期）──
  // 四步：确定脚本 → 配候选画面 → 声画对齐 → 组合计算与批量生成。
  // 注意 mixCapability().duration.honest_label：配音未接通时它写的是
  // 「镜头长度按字数估算、成片没有配音音轨」，**界面必须显示**。
  mixCapability: () => api.get("/api/creative/forge/mix-capability"),
  // 写稿要等模型（AI 创作/爆款仿写），给足超时；「自己写」是瞬时的
  mixScriptNew: (body: { product_name?: string; product_id?: string;
                         script_source: "ai" | "replicate" | "self";
                         script_format: "whole" | "segmented";
                         brief?: string; reference_script?: string;
                         script_text?: string }) =>
    api.post("/api/creative/forge/mix-script", body, { timeout: 300000 }),
  mixScripts: (p: { limit?: number; product_id?: string; mine?: boolean } = {}) =>
    api.get("/api/creative/forge/mix-scripts",
      { params: { ...p, mine: p.mine ? 1 : 0 } }),
  mixScript: (draftId: string) =>
    api.get(`/api/creative/forge/mix-script/${draftId}`),
  mixScriptSave: (draftId: string, body: any) =>
    api.patch(`/api/creative/forge/mix-script/${draftId}`, body),
  // 编排要抽帧做联系表再送模型，比写稿更慢
  mixPlanNew: (body: { draft_id: string; groups: string[][]; count?: number }) =>
    api.post("/api/creative/forge/mix-plan", body, { timeout: 600000 }),
  mixPlan: (planId: string) =>
    api.get(`/api/creative/forge/mix-plan/${planId}`),
  // 纯计算、不落库：界面实时算组合上限与重复率用它
  mixStats: (body: { groups: string[][]; count?: number }) =>
    api.post("/api/creative/forge/mix-stats", body),
  mixBatch: (body: { plan_id: string; count?: number; name?: string; request_id?: string }) =>
    api.post("/api/creative/forge/mix-batch", body, { timeout: 60000 }),
  // 导出：先鉴权换一条带附件头的签名链接，再让浏览器去下（同 batchZipLink 的两段式）
  forgeExportLink: (finalId: string) =>
    api.post(`/api/creative/forge/final/${finalId}/export-link`),

  // ── 发布与复盘回流（双工作台接入第 4 期）──
  publishMeta: () => api.get("/api/creative/publish/meta"),
  // 自动取数/自动发布接没接通。当前都是 false，界面据此说明"要手工回填"
  publishCapability: () => api.get("/api/creative/publish/capability"),
  publishWorks: (p: { status?: string; kind?: string; product?: string;
                      limit?: number }) =>
    api.get("/api/creative/publish/works", { params: p }),
  // 建创意：final_id（成片）/ record_id（图文稿，必须已确认）/ object_keys 三选一
  publishWorkCreate: (body: { final_id?: string; record_id?: string;
                              object_keys?: string[]; name?: string; kind?: string;
                              product_id?: string; product_name?: string }) =>
    api.post("/api/creative/publish/work", body, { timeout: 60000 }),
  publishWork: (creativeId: string) =>
    api.get(`/api/creative/publish/work/${creativeId}`),
  publishWorkUpdate: (creativeId: string, body: any) =>
    api.patch(`/api/creative/publish/work/${creativeId}`, body),
  publishRecords: (p: { channel?: string; creative_id?: string; limit?: number }) =>
    api.get("/api/creative/publish/records", { params: p }),
  publishRecordCreate: (body: { creative_id: string; channel: string; url?: string;
                                title?: string; account_name?: string;
                                published_at?: string; note?: string }) =>
    api.post("/api/creative/publish/record", body),
  publishRecordUpdate: (publishId: string, body: any) =>
    api.patch(`/api/creative/publish/record/${publishId}`, body),
  publishRecordDelete: (publishId: string) =>
    api.delete(`/api/creative/publish/record/${publishId}`),
  // 回填某一天的表现。**只传真的填了的字段**——0 与"没填"在复盘里不是一回事
  publishMetricsFill: (publishId: string, values: Record<string, number>,
                       statDate = "") =>
    api.post(`/api/creative/publish/record/${publishId}/metrics`,
      { values, stat_date: statDate }),
  publishMetrics: (publishId: string) =>
    api.get(`/api/creative/publish/record/${publishId}/metrics`),
  publishReviewSubmit: (creativeId: string, body: { verdict: string;
                                                    reason_tags?: string[];
                                                    conclusion?: string }) =>
    api.post(`/api/creative/publish/work/${creativeId}/review`, body),
  publishReviews: (creativeId: string) =>
    api.get(`/api/creative/publish/work/${creativeId}/reviews`),
  // 素材表现榜：哪段素材真的带来了转化（资产库普通视图不显示表现字段，看这里）
  publishAssetLeaderboard: (p: { limit?: number; purpose?: string;
                                 product?: string }) =>
    api.get("/api/creative/publish/asset-leaderboard", { params: p }),
  publishChannelSummary: () => api.get("/api/creative/publish/channel-summary"),
  publishRecompute: () => api.post("/api/creative/publish/recompute"),
};

export const ragAPI = {
  products: () => api.get("/api/rag/products"),
  // 重建知识库索引：默认增量(只处理新增/改动)，full=true 才全量
  reindex: (full = false) => api.post("/api/rag/reindex", null, { params: { full }, timeout: 600000 }),
  // 智能权限基座·管理员密级管控
  secOverview: () => api.get("/api/rag/security/overview"),
  secSources: (domain = "__all__", q = "", limit = 800) =>
    api.get("/api/rag/security/sources", { params: { domain, q, limit }, timeout: 60000 }),
  secSetLevel: (key: string, level: string, prefix = false) =>
    api.post("/api/rag/security/set-level", { key, level, prefix }),
  secSetSynth: (key: string, synth: boolean | null) =>
    api.post("/api/rag/security/set-synth", { key, synth }),
  secClearOverride: (key: string, prefix = false) =>
    api.post("/api/rag/security/clear-override", { key, prefix }),
};

export const geoAPI = {
  readiness: () => api.get("/api/geo/readiness"),
  workflow: () => api.get("/api/geo/workflow"),
  saveWorkflow: (payload: { auto_publish: boolean; approved_sources: string[]; account_ids: number[]; revision: number }) =>
    api.put("/api/geo/workflow", payload),
  edit: (id: string, revision: number, contents: Record<string, { title: string; body: string; hashtags: string[] }>) =>
    api.put(`/api/geo/content/${id}`, { revision, contents }),
  publish: (id: string, revision: number, account_ids: number[]) =>
    api.post(`/api/geo/content/${id}/publish`, { revision, account_ids }),
  platforms: () => api.get("/api/geo/platforms"),
  // 多平台并发生成，超时放宽到 3 分钟
  generate: (payload: { models: string[]; topic: string; platforms: string[]; config_revision?: number }) =>
    api.post("/api/geo/generate", payload, { timeout: 180000 }),
  contents: (limit = 30) => api.get("/api/geo/contents", { params: { limit } }),
  content: (id: string) => api.get(`/api/geo/content/${id}`),
  setStatus: (id: string, status: string) => api.post(`/api/geo/content/${id}/status`, { status }),
  remove: (id: string) => api.delete(`/api/geo/content/${id}`),
};

/* GEO 平台账号中心：扫码登录/检活/公众号官方接口绑定。凭据只回掩码，前端拿不到明文 */
export const geoAccountAPI = {
  list: (platform = "") => api.get("/api/geo/accounts", { params: { platform } }),
  create: (payload: { platform: string; account_name: string; auth_type?: string }) =>
    api.post("/api/geo/accounts", payload),
  update: (id: number, patch: any) => api.patch(`/api/geo/accounts/${id}`, patch),
  remove: (id: number) => api.delete(`/api/geo/accounts/${id}`),
  // 起扫码会话要拉子进程开浏览器、等平台登录页加载，放宽到 60 秒
  loginStart: (payload: { platform?: string; account_name?: string; account_id?: number }) =>
    api.post("/api/geo/accounts/login/start", payload, { timeout: 60000 }),
  loginStatus: (sid: string) => api.get(`/api/geo/accounts/login/${sid}`),
  loginCancel: (sid: string) => api.post(`/api/geo/accounts/login/${sid}/cancel`),
  // 检活要真开一次 headless 浏览器（10-40 秒）
  check: (id: number) => api.post(`/api/geo/accounts/${id}/check`, {}, { timeout: 120000 }),
  bindApi: (id: number, appid: string, secret: string) =>
    api.post(`/api/geo/accounts/${id}/bind_api`, { appid, secret }, { timeout: 30000 }),
  pasteCookie: (id: number, blob: string) => api.post(`/api/geo/accounts/${id}/cookie`, { blob }),
};

/* GEO 推文排期：今天哪个号发哪条 */
export const geoScheduleAPI = {
  run: (id: number) => api.post(`/api/geo/schedules/${id}/run`),
  confirm: (id: number, url: string) => api.post(`/api/geo/schedules/${id}/confirm`, { url }),
  list: (days = 7, status = "") => api.get("/api/geo/schedules", { params: { days, status } }),
  add: (payload: { content_id: string; account_id: number; plan_date?: string; plan_time?: string }) =>
    api.post("/api/geo/schedules", payload),
  remove: (id: number) => api.delete(`/api/geo/schedules/${id}`),
  planNow: (date = "") => api.post("/api/geo/schedules/plan", { date }, { timeout: 60000 }),
};

export const systemAPI = {
  health: () => api.get("/health"),
  status: () => api.get("/api/system/status"),
};

export const executiveAPI = {
  // 采购/生产走 T100 计算，首次较慢，超时放大
  overview: () => api.get("/api/executive/overview", { timeout: 300000 }),
};

export const qualityAPI = {
  // AI 生成检验标准走 DeepSeek，超时放大
  generateStandard: (target: string, std_type: string) =>
    api.post("/api/quality/inspection-standard", { target, std_type }, { timeout: 120000 }),
  getStandards: (std_type?: string) =>
    api.get("/api/quality/standards", { params: { std_type } }),
  addIssue: (data: any) => api.post("/api/quality/issue", data),
  // issue_type 空串=全部类型（客诉/生产异常也看得到）
  getIssues: (issue_type?: string) => api.get("/api/quality/issues", { params: { issue_type } }),
  optimizeStandard: (related_id: string, std_type: string) =>
    api.post("/api/quality/optimize-standard", { related_id, std_type }, { timeout: 120000 }),
  genReport: (data: any) => api.post("/api/quality/report", data, { timeout: 60000 }),
  getReports: (report_type?: string) => api.get("/api/quality/reports", { params: { report_type } }),
  reportExcelUrl: (id: number) => `${API_BASE_URL}/api/quality/report/${id}/excel`,
};

export const officeAPI = {
  sources: () => api.get("/api/office/sources"),
  // 翻译+导出走 LLM 分块翻译，可能较慢，超时放大
  translateDoc: (data: { source: string; target_lang: string; formats: string[] }) =>
    api.post("/api/office/translate-doc", data, { timeout: 600000 }),
  downloadUrl: (filename: string) => `${API_BASE_URL}/api/office/download/${encodeURIComponent(filename)}`,
};

// 用户反馈闭环（8.6）
export const feedbackAPI = {
  add: (data: { question?: string; answer: string; rating: string; feedback_text?: string; model?: string }) =>
    api.post("/api/kb/feedback", data),
  judge: () => api.post("/api/kb/feedback/judge", { batch_size: 30 }, { timeout: 180000 }),
  valuable: (status: string = "pending") => api.get("/api/kb/feedback/valuable", { params: { status } }),
  approve: (id: number) => api.post(`/api/kb/feedback/valuable/${id}/approve`, {}, { timeout: 60000 }),
  reject: (id: number) => api.post(`/api/kb/feedback/valuable/${id}/reject`, {}),
  stats: () => api.get("/api/kb/feedback/stats"),
};

// RAG 检索审计(P2)：每次检索注入/拦截明细
export const raglogAPI = {
  list: (params: { q?: string; domain?: string; zero_hit?: string; days?: number } = {}) =>
    api.get("/api/kb/raglog", { params }),
};

// 学习类知识治理(P1)：沉淀条目 列表/修订/删除
export const learnedAPI = {
  list: (source: string = "", search: string = "") =>
    api.get("/api/kb/learned", { params: { source, search } }),
  update: (id: number, content: string) => api.put(`/api/kb/learned/${id}`, { content }, { timeout: 60000 }),
  del: (id: number) => api.delete(`/api/kb/learned/${id}`),
};

// 9.4 对话价值闭环：全量对话记录 → 五维评分 → 待审列表/知识缺口/升级建议/周报
export const convalueAPI = {
  stats: () => api.get("/api/kb/convalue/stats"),
  judge: () => api.post("/api/kb/convalue/judge", { batch_size: 300 }, { timeout: 300000 }),
  records: (level: string = "", reviewed: string = "pending") =>
    api.get("/api/kb/convalue/records", { params: { level, reviewed } }),
  decide: (id: number, decision: string, extra: Record<string, string> = {}) =>
    api.post(`/api/kb/convalue/records/${id}/decision`, { decision, ...extra }, { timeout: 60000 }),
  gaps: () => api.get("/api/kb/convalue/gaps"),
  suggestions: () => api.get("/api/kb/convalue/suggestions"),
  reports: () => api.get("/api/kb/convalue/reports"),
  buildReport: () => api.post("/api/kb/convalue/reports/build", {}, { timeout: 300000 }),
};

// 模块 AI 对话气泡(V3)：路由感知的悬浮气泡，S1封顶+财务拦截+输出脱敏
// 质检申诉闭环（客服工作台「质检申诉」页签）
// 可见性和动作权限全由后端判——前端只负责按 my_actions 渲染按钮，不自己算权限
export const qcAppealAPI = {
  meta: () => api.get("/api/qc-appeal/meta"),
  contacts: (keyword = "") => api.get("/api/qc-appeal/contacts", { params: { keyword } }),
  uploadEvidence: (files: File[], ticket_no = "") => {
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    fd.append("ticket_no", ticket_no);
    return api.post("/api/qc-appeal/evidence", fd, { timeout: 120000 });
  },
  create: (body: {
    content: string; targets: { userid: string; name: string }[]; evidence?: any[];
    shop_name?: string; order_no?: string; customer_id?: string;
    mode?: string;                              // 质检(默认) | 案例（2026-08-27）
    case_title?: string; case_tags?: string[];
    period_month?: string; deduct_points?: number;
  }) =>
    api.post("/api/qc-appeal/tickets", body, { timeout: 60000 }),
  targets: (params: {
    scope?: string; state?: string; month?: string; search?: string;
    shop?: string; order_no?: string; page?: number;
  }) => api.get("/api/qc-appeal/targets", { params }),
  detail: (rowId: number) => api.get(`/api/qc-appeal/targets/${rowId}`),
  // 同订单号关联：工单中心的工单 + 同单号的其他质检记录（后者仅全量可见者）
  related: (rowId: number) => api.get(`/api/qc-appeal/targets/${rowId}/related`),
  // 发起前校验客户单号在工单中心有没有工单（只提示不拦提交）
  orderCheck: (order_no: string) =>
    api.get("/api/qc-appeal/order-check", { params: { order_no } }),
  // 反向入口：工单中心问「这个客户单号有哪些质检记录」（按权限过滤）
  byOrder: (order_no: string) =>
    api.get("/api/qc-appeal/by-order", { params: { order_no } }),
  // 质检案例：列表 / 详情(打开即已读) / 留言 / 设为案例
  cases: (params: { page?: number; search?: string; tag?: string; month?: string } = {}) =>
    api.get("/api/qc-appeal/cases", { params }),
  caseDetail: (rowId: number) => api.get(`/api/qc-appeal/cases/${rowId}`),
  caseComment: (rowId: number, content: string) =>
    api.post(`/api/qc-appeal/cases/${rowId}/comment`, { content }),
  setCase: (rowId: number, on: boolean, note = "", case_title = "", case_tags: string[] = []) =>
    api.post(`/api/qc-appeal/targets/${rowId}/case`, { on, note, case_title, case_tags }),
  updateCaseMetadata: (rowId: number, case_title: string, case_tags: string[]) =>
    api.patch(`/api/qc-appeal/cases/${rowId}/metadata`, { case_title, case_tags }),
  act: (rowId: number, body: { action: string; comment?: string; evidence?: any[]; referee_id?: string; referee_name?: string }) =>
    api.post(`/api/qc-appeal/targets/${rowId}/act`, body, { timeout: 60000 }),
  // 证据取图：接口要鉴权（质检证据是 HR 敏感材料，不能靠静态目录裸露），
  // 所以不能给 <img src> 裸链接，必须走 blob 带上令牌
  evidenceBlob: async (path: string) => {
    const parts = String(path || "").split("/");
    const name = parts.pop() || "";
    const ticket = parts.pop() || "misc";
    const r = await api.get(
      `/api/qc-appeal/evidence/${encodeURIComponent(ticket)}/${encodeURIComponent(name)}`,
      { responseType: "blob", timeout: 60000 });
    return URL.createObjectURL(r.data as Blob);
  },
  score: (params: { month?: string; user_id?: string } = {}) =>
    api.get("/api/qc-appeal/score", { params }),
  report: (month = "", refresh = false) =>
    api.get("/api/qc-appeal/report", { params: { month, refresh } }),
  // 手动兜底扫超时（定时任务每小时也会跑）。限管理员+白名单。
  scanTimeout: () => api.post("/api/qc-appeal/scan-timeout", {}, { timeout: 120000 }),
};

export const moduleChatAPI = {
  scopes: () => api.get("/api/module-chat/scopes"),
  // pageContext：当前页面的上下文（如外贸客户页的 {customer_id}）。
  // 为什么需要：气泡按 pathname 匹配模块，但客户详情是**抽屉不是路由**，
  // 光看路径不知道在看哪个客户 → 前端从 URL query 取了传过来。
  send: (moduleId: string, message: string, history: { role: string; content: string }[] = [], conv_id = "",
         pageContext: any = undefined) =>
    api.post(`/api/module-chat/${moduleId}`, { message, history, conv_id, page_context: pageContext },
             { timeout: 300000 }),
  // 气泡也走 Agent 后一轮最长 200 秒，必须流式推进度，否则用户以为卡死了。
  // 和 agentAPI.askStream 一样用原生 fetch（axios 拿不到 ReadableStream）。
  sendStream: async (
    moduleId: string, message: string,
    history: { role: string; content: string }[] = [], conv_id = "",
    onEvent: (ev: any) => void = () => {},
    pageContext: any = undefined,
    signal?: AbortSignal,
  ) => {
    const r = await fetch(`${API_BASE_URL}/api/module-chat/${moduleId}/stream`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
      body: JSON.stringify({ message, history, conv_id, page_context: pageContext }),
      signal,
    });
    if (!r.ok || !r.body) throw new Error(`stream ${r.status}`);
    const reader = r.body.getReader();
    const dec = new TextDecoder();
    let buf = "", result: any = null;
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buf += dec.decode(value, { stream: true });
      const frames = buf.split("\n\n");
      buf = frames.pop() || "";
      for (const f of frames) {
        const line = f.split("\n").find((x) => x.startsWith("data:"));
        if (!line) continue;                       // 心跳 ": ping" 跳过
        let ev: any;
        try { ev = JSON.parse(line.slice(5).trim()); } catch { continue; }
        if (ev.type === "done") result = ev.result;
        else if (ev.type === "error") throw new Error(ev.message || "stream error");
        else onEvent(ev);
      }
    }
    if (!result) throw new Error("流中断，没拿到最终结果");
    return result;
  },
};

// 无忧服务(上门服务工单)只读查询
export const wuyouAPI = {
  overview: () => api.get("/api/wuyou/overview"),
  enums: () => api.get("/api/wuyou/enums"),
  serviceOrders: (params: any) => api.post("/api/wuyou/service-orders", params, { timeout: 60000 }),
  facilitatorOrders: (params: any) => api.post("/api/wuyou/facilitator-orders", params, { timeout: 60000 }),
  serviceOrderDetail: (orderCode: string) => api.get(`/api/wuyou/service-orders/${encodeURIComponent(orderCode)}`),
  agents: (params: any) => api.post("/api/wuyou/agents", params, { timeout: 60000 }),
  agentAreas: (params: any) => api.post("/api/wuyou/agent-areas", params, { timeout: 60000 }),
  agentFees: (params: any) => api.post("/api/wuyou/agent-fees", params, { timeout: 60000 }),
  feeTypes: (params: any) => api.post("/api/wuyou/fee-types", params, { timeout: 60000 }),
  feeConfirm: (params: any) => api.post("/api/wuyou/fee-confirm", params, { timeout: 60000 }),
  settlements: (params: any) => api.post("/api/wuyou/settlements", params, { timeout: 60000 }),
  salesOrders: (params: any) => api.post("/api/wuyou/sales-orders", params, { timeout: 60000 }),
  journeys: (params: any) => api.post("/api/wuyou/journeys", params, { timeout: 60000 }),
  customOrders: (params: any) => api.post("/api/wuyou/custom-orders", params, { timeout: 60000 }),
  customEnums: () => api.get("/api/wuyou/custom-enums"),
  rebate: (params: any) => api.post("/api/wuyou/rebate", params, { timeout: 60000 }),
  rebateEnums: () => api.get("/api/wuyou/rebate-enums"),
  customTasks: (params: any) => api.post("/api/wuyou/custom-tasks", params, { timeout: 60000 }),
  customTaskEnums: () => api.get("/api/wuyou/custom-task-enums"),
  coordination: (params: any) => api.post("/api/wuyou/coordination", params, { timeout: 60000 }),
  circulate: (params: any) => api.post("/api/wuyou/circulate", params, { timeout: 60000 }),
  workorderFiles: (params: any) => api.post("/api/wuyou/workorder-files", params, { timeout: 60000 }),
};

// 无忧简化 MIS 首批流程：标准现货 / 测量定制，写入自有业务库并复用现有定制任务
export const wuyouFlowAPI = {
  policies: () => api.get('/api/biz/wuyou-flow/policies/list'),
  policyPropose: (data: any) => api.post('/api/biz/wuyou-flow/policies/propose', data),
  policyReview: (kind: string, data: any) => api.post(`/api/biz/wuyou-flow/policies/${encodeURIComponent(kind)}/review`, data),
  templatePreview: (data: any) => api.post('/api/biz/wuyou-flow/template-preview', data),
  takeoverPreview: (data: any) => api.post('/api/biz/wuyou-flow/takeover/preview', data),
  takeoverSubmit: (data: any) => api.post('/api/biz/wuyou-flow/takeover/submit', data),
  takeovers: () => api.get('/api/biz/wuyou-flow/takeover/list'),
  takeoverReview: (id: string, data: any) => api.post(`/api/biz/wuyou-flow/takeover/${encodeURIComponent(id)}/review`, data),
  operate: (code: string, data: any) => api.post(`/api/biz/wuyou-flow/${encodeURIComponent(code)}/operations`, data),
  pendingReceipts: () => api.get('/api/biz/wuyou-flow/receipts/pending'),
  templates: () => api.get("/api/biz/wuyou-flow/templates"),
  list: (params: any = {}) => api.post("/api/biz/wuyou-flow/list", params, { timeout: 60000 }),
  get: (code: string) => api.get(`/api/biz/wuyou-flow/${encodeURIComponent(code)}`),
  create: (data: any) => api.post("/api/biz/wuyou-flow/create", data, { timeout: 60000 }),
  action: (code: string, data: any) => api.post(`/api/biz/wuyou-flow/${encodeURIComponent(code)}/action`, data, { timeout: 60000 }),
  upload: (code: string, step: string, file: File) => {
    const form = new FormData(); form.append("file", file);
    return api.post(`/api/biz/wuyou-flow/${encodeURIComponent(code)}/files/${encodeURIComponent(step)}`, form, { timeout: 60000 });
  },
  download: (code: string, id: string) => api.get(`/api/biz/wuyou-flow/${encodeURIComponent(code)}/files/${encodeURIComponent(id)}`, { responseType: "blob" }),
};

// 订单/工单增量可写跟进层(跟进状态/认领/备注·天能AI侧协作数据)
export const bizFollowAPI = {
  enums: () => api.get("/api/biz/order-follow/enums"),
  get: (bizType: string, orderCode: string) => api.get(`/api/biz/order-follow/${bizType}/${encodeURIComponent(orderCode)}`),
  map: (data: any) => api.post("/api/biz/order-follow/map", data),
  upsert: (data: any) => api.post("/api/biz/order-follow/upsert", data),
  note: (data: any) => api.post("/api/biz/order-follow/note", data),
  myTodos: (params: any) => api.post("/api/biz/order-follow/my-todos", params),
};

// 服务商管理·可写(取代MIS第一个试点, 读写天能AI自有业务库)
export const bizAgentAPI = {
  list: (params: any) => api.post("/api/biz/service-agent/list", params, { timeout: 60000 }),
  get: (id: number) => api.get(`/api/biz/service-agent/${id}`),
  create: (data: any) => api.post("/api/biz/service-agent/create", data),
  update: (id: number, data: any) => api.post(`/api/biz/service-agent/${id}/update`, data),
  remove: (id: number) => api.post(`/api/biz/service-agent/${id}/delete`, {}),
  syncFromMis: () => api.post("/api/biz/service-agent/sync", {}, { timeout: 120000 }),
  // 客服代设师傅端门户(/sf)登录密码——走员工态鉴权
  setPortalPassword: (agent_code: string, password: string) =>
    api.post(`/api/biz/service-agent/${encodeURIComponent(agent_code)}/set-portal-password`, { password }),
};

// 无忧服务·服务工单·可写(取代MIS核心, 双状态机, 自有库 aptenon_ai)
export const bizServiceOrderAPI = {
  list: (params: any) => api.post("/api/biz/service-order/list", params, { timeout: 60000 }),
  get: (code: string) => api.get(`/api/biz/service-order/${encodeURIComponent(code)}`),
  create: (data: any) => api.post("/api/biz/service-order/create", data),
  update: (code: string, data: any) => api.post(`/api/biz/service-order/${encodeURIComponent(code)}/update`, data),
  assign: (code: string, agent_code: string) => api.post(`/api/biz/service-order/${encodeURIComponent(code)}/assign`, { agent_code }),
  submit: (code: string) => api.post(`/api/biz/service-order/${encodeURIComponent(code)}/submit`, {}),
  accept: (code: string) => api.post(`/api/biz/service-order/${encodeURIComponent(code)}/accept`, {}),
  complete: (code: string) => api.post(`/api/biz/service-order/${encodeURIComponent(code)}/complete`, {}),
  // 推万师傅(Phase D 第三方派单): 未配置 WSF 凭据时后端会拒绝, dry_run 可预览报文
  pushWsf: (code: string, body?: any) => api.post(`/api/biz/service-order/${encodeURIComponent(code)}/push-wsf`, body || {}, { timeout: 60000 }),
  // 费用确认(批4): 收对象 {adjust_amount?, adjust_note?, fee_status?, review_point1?..3?}; 兼容旧的传数字
  confirmFee: (code: string, payload: any) =>
    api.post(`/api/biz/service-order/${encodeURIComponent(code)}/confirm-fee`,
      (payload && typeof payload === "object") ? payload : { adjust_amount: payload }),
  cancel: (code: string, reason: string) => api.post(`/api/biz/service-order/${encodeURIComponent(code)}/cancel`, { reason }),
  fail: (code: string, reason: string) => api.post(`/api/biz/service-order/${encodeURIComponent(code)}/fail`, { reason }),
  review: (code: string, review_point: any, customer_review: string) => api.post(`/api/biz/service-order/${encodeURIComponent(code)}/review`, { review_point, customer_review }),
  deleteFile: (code: string, file_id: number) => api.post(`/api/biz/service-order/${encodeURIComponent(code)}/delete-file`, { file_id }),
  settlements: (params: any) => api.post("/api/biz/service-order/settlements", params),
  generateSettlement: (agent_code: string, order_codes: string[]) => api.post("/api/biz/service-order/settlements/generate", { agent_code, order_codes }),
  getSettlement: (fee_code: string) => api.get(`/api/biz/service-order/settlements/${encodeURIComponent(fee_code)}`),
  paySettlement: (fee_code: string, data: any) => api.post(`/api/biz/service-order/settlements/${encodeURIComponent(fee_code)}/pay`, data),
  migrateFromMis: (limit?: number) => api.post("/api/biz/service-order/migrate-from-mis", limit ? { limit } : {}, { timeout: 300000 }),
  uploadUrl: (code: string) => `${API_BASE_URL}/api/biz/service-order/${encodeURIComponent(code)}/upload-file?token=${getToken()}`,
  fileSrc: (url: string) => `${API_BASE_URL}${url}${url.includes("?") ? "&" : "?"}token=${getToken()}`,
  // 认领/归属人/备注(并入可写模块本身): assign-owner 传 {claim:true} 认领给自己 或 {assignee,assignee_name} 指派
  assignOwner: (code: string, payload: any) => api.post(`/api/biz/service-order/${encodeURIComponent(code)}/assign-owner`, payload),
  addNote: (code: string, content: string) => api.post(`/api/biz/service-order/${encodeURIComponent(code)}/note`, { content }),
  listNotes: (code: string) => api.get(`/api/biz/service-order/${encodeURIComponent(code)}/notes`),
};

// 无忧服务·销售订单·可写(取代MIS, 三态: 订单/发货/退款, 自有库 aptenon_ai)
export const bizSalesOrderAPI = {
  list: (params: any) => api.post("/api/biz/sales-order/list", params, { timeout: 60000 }),
  get: (code: string) => api.get(`/api/biz/sales-order/${encodeURIComponent(code)}`),
  create: (data: any) => api.post("/api/biz/sales-order/create", data),
  update: (code: string, data: any) => api.post(`/api/biz/sales-order/${encodeURIComponent(code)}/update`, data),
  process: (code: string) => api.post(`/api/biz/sales-order/${encodeURIComponent(code)}/process`, {}),
  setDelivery: (code: string, data: any) => api.post(`/api/biz/sales-order/${encodeURIComponent(code)}/set-delivery`, data),
  setRefund: (code: string, refund_amount: any) => api.post(`/api/biz/sales-order/${encodeURIComponent(code)}/set-refund`, { refund_amount }),
  cancel: (code: string, reason: string) => api.post(`/api/biz/sales-order/${encodeURIComponent(code)}/cancel`, { reason }),
  migrateFromMis: (limit?: number) => api.post("/api/biz/sales-order/migrate-from-mis", limit ? { limit } : {}, { timeout: 300000 }),
  // 旺店通拉单(Phase B 进单自动化): 手动触发一次增量同步, hours_back 可选回溯小时数
  syncWdt: (hours_back?: number) => api.post("/api/biz/sales-order/sync-wdt", hours_back ? { hours_back } : {}, { timeout: 300000 }),
  // 认领/归属人/备注(并入可写模块本身): assign-owner 传 {claim:true} 认领给自己 或 {assignee,assignee_name} 指派
  assignOwner: (code: string, payload: any) => api.post(`/api/biz/sales-order/${encodeURIComponent(code)}/assign-owner`, payload),
  addNote: (code: string, content: string) => api.post(`/api/biz/sales-order/${encodeURIComponent(code)}/note`, { content }),
  listNotes: (code: string) => api.get(`/api/biz/sales-order/${encodeURIComponent(code)}/notes`),
};

// 无忧服务·客户全景(客户360): 按手机号聚合 销售订单/定制工单/服务工单 + 建单防重在途汇总
export const customer360API = {
  panorama: (phone: string) => api.get(`/api/biz/customer360/${encodeURIComponent(phone)}`, { timeout: 60000 }),
  openSummary: (phone: string) => api.get("/api/biz/customer360/open-summary", { params: { phone } }),
};

// 无忧服务·定制工单·门信息(测量结果+门参数信息, 复刻MIS左右双栏)
export const customDoorAPI = {
  get: (code: string) => api.get(`/api/biz/custom-door/${encodeURIComponent(code)}`),
  save: (code: string, data: any) => api.post(`/api/biz/custom-door/${encodeURIComponent(code)}/save`, data),
  migrateFromMis: (limit?: number) => api.post("/api/biz/custom-door/migrate-from-mis", limit ? { limit } : {}, { timeout: 300000 }),
};

// 无忧服务·定制任务中心(待办/已办)：跨工单的任务查询与处理(Codex 批2)
export const customTaskAPI = {
  list: (params: any) => api.post("/api/biz/custom-task/list", params, { timeout: 60000 }),
  summary: (params: any = {}) => api.post("/api/biz/custom-task/summary", params),
  get: (taskId: number) => api.get(`/api/biz/custom-task/${taskId}`),
  start: (taskId: number) => api.post(`/api/biz/custom-task/${taskId}/start`, {}),
  finish: (taskId: number, handle_result: string, express_memo?: string) =>
    api.post(`/api/biz/custom-task/${taskId}/finish`, { handle_result, express_memo }),
  goback: (taskId: number, reason: string) =>
    api.post(`/api/biz/custom-task/${taskId}/goback`, { reason }),
  transfer: (taskId: number, to_user: string, to_user_name: string, reason = "") =>
    api.post(`/api/biz/custom-task/${taskId}/transfer`, { to_user, to_user_name, reason }),
  cancel: (taskId: number, reason: string) =>
    api.post(`/api/biz/custom-task/${taskId}/cancel`, { reason }),
  flag: (taskId: number, flag: string) =>
    api.post(`/api/biz/custom-task/${taskId}/flag`, { flag }),
  urge: (taskId: number) => api.post(`/api/biz/custom-task/${taskId}/urge`, {}),
  // 完工图片走服务工单的文件端点(任务通过 service_order_code 关联到服务单)
  fileSrc: (storedName: string) =>
    `${API_BASE_URL}/api/biz/service-order/file/${encodeURIComponent(storedName)}?token=${getToken()}`,
};

// 无忧服务·服务商工单属性(区域与内容+服务店铺, 全新)
export const agentAreaAPI = {
  list: (params: any) => api.post("/api/biz/agent-area/list", params, { timeout: 60000 }),
  create: (data: any) => api.post("/api/biz/agent-area/create", data),
  createBatch: (data: any) => api.post("/api/biz/agent-area/create-batch", data),
  update: (id: number, data: any) => api.post(`/api/biz/agent-area/${id}/update`, data),
  remove: (id: number) => api.post(`/api/biz/agent-area/${id}/delete`, {}),
  deleteMany: (ids: number[]) => api.post("/api/biz/agent-area/delete-many", { ids }),
};

// 无忧服务·销售流水(全新, 财务Excel导入, 仅罗燕/李小丽可见, 后端子键守卫)
export const salesJourneyAPI = {
  list: (params: any) => api.post("/api/biz/sales-journey/list", params, { timeout: 60000 }),
  create: (data: any) => api.post("/api/biz/sales-journey/create", data),
  update: (id: number, data: any) => api.post(`/api/biz/sales-journey/${id}/update`, data),
  remove: (id: number) => api.post(`/api/biz/sales-journey/${id}/delete`, {}),
  deleteMany: (ids: number[]) => api.post("/api/biz/sales-journey/delete-many", { ids }),
  importExcel: (data: FormData) => api.post("/api/biz/sales-journey/import", data, { timeout: 300000 }),
  templateUrl: () => `${API_BASE_URL}/api/biz/sales-journey/template?token=${getToken()}`,
};

// 无忧服务·定制工单·可写(取代MIS, 工单头 Initial→Doing→Finished + 任务明细, 自有库 aptenon_ai)
export const bizCustomOrderAPI = {
  list: (params: any) => api.post("/api/biz/custom-order/list", params, { timeout: 60000 }),
  get: (code: string) => api.get(`/api/biz/custom-order/${encodeURIComponent(code)}`),
  create: (data: any) => api.post("/api/biz/custom-order/create", data),
  update: (code: string, data: any) => api.post(`/api/biz/custom-order/${encodeURIComponent(code)}/update`, data),
  submit: (code: string) => api.post(`/api/biz/custom-order/${encodeURIComponent(code)}/submit`, {}),
  finish: (code: string) => api.post(`/api/biz/custom-order/${encodeURIComponent(code)}/finish`, {}),
  cancel: (code: string, reason: string) => api.post(`/api/biz/custom-order/${encodeURIComponent(code)}/cancel`, { reason }),
  listTasks: (code: string) => api.post(`/api/biz/custom-order/tasks/${encodeURIComponent(code)}/list`, {}),
  addTask: (code: string, data: any) => api.post(`/api/biz/custom-order/tasks/${encodeURIComponent(code)}/add`, data),
  updateTask: (task_id: number, data: any) => api.post(`/api/biz/custom-order/tasks/${task_id}/update`, data),
  deleteTask: (task_id: number) => api.post(`/api/biz/custom-order/tasks/${task_id}/delete`, {}),
  migrateFromMis: (limit?: number) => api.post("/api/biz/custom-order/migrate-from-mis", limit ? { limit } : {}, { timeout: 300000 }),
  // ── T100 对外集成(Phase C): 推真实销售订单/出货通知单/取消 + 客户建议 + 任务派生服务单 ──
  // body: {t100_client_code?, doc_type?, order_direct?("Y"/"N"), dry_run?} → {success, t100_sales_order_code} 或 {success:false, error}
  t100CreateOrder: (code: string, body: any) => api.post(`/api/biz/custom-order/${encodeURIComponent(code)}/t100/create-order`, body || {}, { timeout: 60000 }),
  t100CreateShipment: (code: string, body: any = {}) => api.post(`/api/biz/custom-order/${encodeURIComponent(code)}/t100/create-shipment`, body, { timeout: 60000 }),
  t100Close: (code: string, body: any = {}) => api.post(`/api/biz/custom-order/${encodeURIComponent(code)}/t100/close`, body, { timeout: 60000 }),
  t100ClientSuggest: (params: { platform?: string; customer?: string }) =>
    api.get("/api/biz/custom-order/t100/client-suggest", { params }),
  deriveServiceOrder: (task_id: number) => api.post(`/api/biz/custom-order/tasks/${task_id}/derive-service-order`, {}, { timeout: 60000 }),
  // 认领/归属人/备注(并入可写模块本身): assign-owner 传 {claim:true} 认领给自己 或 {assignee,assignee_name} 指派
  assignOwner: (code: string, payload: any) => api.post(`/api/biz/custom-order/${encodeURIComponent(code)}/assign-owner`, payload),
  addNote: (code: string, content: string) => api.post(`/api/biz/custom-order/${encodeURIComponent(code)}/note`, { content }),
  listNotes: (code: string) => api.get(`/api/biz/custom-order/${encodeURIComponent(code)}/notes`),
};

// 无忧服务·焕新管理(rebate)·可写(取代MIS, None/Initial→Doing→Finished + Cancelled, 自有库 aptenon_ai)
export const bizRebateAPI = {
  list: (params: any) => api.post("/api/biz/rebate/list", params, { timeout: 60000 }),
  enums: () => api.get("/api/biz/rebate/enums"),
  get: (code: string) => api.get(`/api/biz/rebate/${encodeURIComponent(code)}`),
  create: (data: any) => api.post("/api/biz/rebate/create", data),
  update: (code: string, data: any) => api.post(`/api/biz/rebate/${encodeURIComponent(code)}/update`, data),
  process: (code: string) => api.post(`/api/biz/rebate/${encodeURIComponent(code)}/process`, {}),
  finish: (code: string) => api.post(`/api/biz/rebate/${encodeURIComponent(code)}/finish`, {}),
  cancel: (code: string, reason: string) => api.post(`/api/biz/rebate/${encodeURIComponent(code)}/cancel`, { reason }),
  migrateFromMis: (limit?: number) => api.post("/api/biz/rebate/migrate-from-mis", limit ? { limit } : {}, { timeout: 300000 }),
  // 认领/归属人/备注(并入可写模块本身): assign-owner 传 {claim:true} 认领给自己 或 {assignee,assignee_name} 指派
  assignOwner: (code: string, payload: any) => api.post(`/api/biz/rebate/${encodeURIComponent(code)}/assign-owner`, payload),
  addNote: (code: string, content: string) => api.post(`/api/biz/rebate/${encodeURIComponent(code)}/note`, { content }),
  listNotes: (code: string) => api.get(`/api/biz/rebate/${encodeURIComponent(code)}/notes`),
};

// 我的待办(读四张可写订单表: 我认领的跨模块待办)
export const bizMyTodosAPI = {
  list: (params: any) => api.post("/api/biz/my-todos/list", params),
};

// 通用字典 UDC·可写(tenon_udc, 复刻MIS机制: system_code+system_type 归组, srp1~5 扩展位)
// 全站下拉的统一取值口 = combo(); list/create/update/remove/groups 给定制工单配置页的字典维护Tab用。
export const udcAPI = {
  // {system_code?, system_type?, udc_code?, display_text?, status?, pageIndex?, pageSize?} → {list,total,pageIndex,pageSize}
  list: (params: any = {}) => api.post("/api/biz/udc/list", params, { timeout: 60000 }),
  // ★下拉专用 {system_code, system_type, parent_code?, srp2?, only_enabled?} → [{value,label,srp1..srp5,parent_code}]
  combo: (params: any) => api.post("/api/biz/udc/combo", params),
  // → {list:[{system_code,system_type,count}], total, rows}
  groups: () => api.post("/api/biz/udc/groups", {}),
  create: (data: any) => api.post("/api/biz/udc/create", data),
  update: (id: number, data: any) => api.post(`/api/biz/udc/${id}/update`, data),
  remove: (id: number) => api.post(`/api/biz/udc/${id}/delete`, {}),
};

// 无忧服务·定制工单配置(无忧服务复刻·批1)：渠道协作模板(头+任务行) + 门参数模板
// ★任务行的处理人按渠道配在 channel_handlers(JSON)里：
//   {"EC":{handler_type:"提交人|负责人|指定人",handler,handler_name,dept_id,dept_name},"HOME":{...},"TRADE":{...},"PROJ":{...}}
export const customConfigAPI = {
  // → {channels, handler_types, approval_types, order_nodes, service_types, udc_channels}
  enums: () => api.get("/api/biz/custom-config/enums"),
  // 协作模板头
  listTemplates: (params: any = {}) => api.post("/api/biz/custom-config/templates/list", params, { timeout: 60000 }),
  createTemplate: (data: any) => api.post("/api/biz/custom-config/templates/create", data),
  updateTemplate: (code: string, data: any) =>
    api.post(`/api/biz/custom-config/templates/${encodeURIComponent(code)}/update`, data),
  deleteTemplate: (code: string) =>
    api.post(`/api/biz/custom-config/templates/${encodeURIComponent(code)}/delete`, {}),
  getTemplate: (code: string) => api.get(`/api/biz/custom-config/templates/${encodeURIComponent(code)}`),
  // 协作模板任务行(channel_handlers 按 EC/HOME/TRADE/PROJ 配处理人)
  createTask: (code: string, data: any) =>
    api.post(`/api/biz/custom-config/templates/${encodeURIComponent(code)}/tasks/create`, data),
  updateTask: (taskId: number, data: any) => api.post(`/api/biz/custom-config/tasks/${taskId}/update`, data),
  deleteTask: (taskId: number) => api.post(`/api/biz/custom-config/tasks/${taskId}/delete`, {}),
  // 门参数模板(数值 mm)
  listDoorTemplates: (params: any = {}) =>
    api.post("/api/biz/custom-config/door-templates/list", params, { timeout: 60000 }),
  createDoorTemplate: (data: any) => api.post("/api/biz/custom-config/door-templates/create", data),
  updateDoorTemplate: (id: number, data: any) => api.post(`/api/biz/custom-config/door-templates/${id}/update`, data),
  deleteDoorTemplate: (id: number) => api.post(`/api/biz/custom-config/door-templates/${id}/delete`, {}),
  // 手动实例化补救入口(提交时实例化失败/模板后补后, 客服手动给工单生成任务) {order_code, channel?, template_code?}
  instantiate: (data: { order_code: string; channel?: string; template_code?: string }) =>
    api.post("/api/biz/custom-config/instantiate", data, { timeout: 60000 }),

  // 分组式别名(与上面扁平方法等价, 页面按 templates./tasks./doorTemplates. 的写法调用)
  templates: {
    list: (params: any = {}) => api.post("/api/biz/custom-config/templates/list", params, { timeout: 60000 }),
    create: (data: any) => api.post("/api/biz/custom-config/templates/create", data),
    update: (code: string, data: any) =>
      api.post(`/api/biz/custom-config/templates/${encodeURIComponent(code)}/update`, data),
    remove: (code: string) =>
      api.post(`/api/biz/custom-config/templates/${encodeURIComponent(code)}/delete`, {}),
    delete: (code: string) =>
      api.post(`/api/biz/custom-config/templates/${encodeURIComponent(code)}/delete`, {}),
    get: (code: string) => api.get(`/api/biz/custom-config/templates/${encodeURIComponent(code)}`),
  },
  tasks: {
    create: (code: string, data: any) =>
      api.post(`/api/biz/custom-config/templates/${encodeURIComponent(code)}/tasks/create`, data),
    update: (taskId: number, data: any) => api.post(`/api/biz/custom-config/tasks/${taskId}/update`, data),
    remove: (taskId: number) => api.post(`/api/biz/custom-config/tasks/${taskId}/delete`, {}),
    delete: (taskId: number) => api.post(`/api/biz/custom-config/tasks/${taskId}/delete`, {}),
  },
  doorTemplates: {
    list: (params: any = {}) =>
      api.post("/api/biz/custom-config/door-templates/list", params, { timeout: 60000 }),
    create: (data: any) => api.post("/api/biz/custom-config/door-templates/create", data),
    update: (id: number, data: any) => api.post(`/api/biz/custom-config/door-templates/${id}/update`, data),
    remove: (id: number) => api.post(`/api/biz/custom-config/door-templates/${id}/delete`, {}),
    delete: (id: number) => api.post(`/api/biz/custom-config/door-templates/${id}/delete`, {}),
  },
};

// ══ 服务商门户(师傅端 H5, /sf/*) ══════════════════════════════════
// 与员工态彻底隔离：独立 localStorage 键(员工是 tenon_token, 绝不混用) + 独立 axios 实例
// (不共享员工实例的拦截器)。401 只清师傅 token 并整页回 /sf/login，
// 绝不触发员工端的 reload / 企微 OAuth 逻辑。
const AGENT_TOKEN_KEY = "tenon_agent_token";
export const getAgentToken = () => localStorage.getItem(AGENT_TOKEN_KEY) || "";
export const setAgentToken = (t: string) => localStorage.setItem(AGENT_TOKEN_KEY, t);
export const clearAgentToken = () => localStorage.removeItem(AGENT_TOKEN_KEY);

const agentApi = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: { "Content-Type": "application/json" },
});
agentApi.interceptors.request.use((config) => {
  const t = getAgentToken();
  if (t) config.headers.Authorization = `Bearer ${t}`;
  // FormData(完工图上传)：去掉默认 JSON 头，让浏览器自动设 multipart 边界
  if (typeof FormData !== "undefined" && config.data instanceof FormData) {
    delete (config.headers as any)["Content-Type"];
  }
  return config;
});
agentApi.interceptors.response.use(
  (resp) => resp,
  (error) => {
    if (error?.response?.status === 401) {
      clearAgentToken();
      // 应用是 BrowserRouter(路径路由)：整页跳师傅端登录页；已在登录页则不跳，避免死循环
      if (!window.location.pathname.startsWith("/sf/login")) {
        const path = /^\/sf\/orders\/[^/?#\\]+$/.test(window.location.pathname) ? window.location.pathname : '/sf/orders';
        window.location.href = '/sf/login?next=' + encodeURIComponent(path);
      }
    }
    return Promise.reject(error);
  }
);

export const agentPortalAPI = {
  wechatBinding: () => agentApi.get('/api/agent/wechat/binding'),
  wechatStart: () => agentApi.post('/api/agent/wechat/binding/start', { confirmed: true }),
  wechatFinish: (body: any) => agentApi.post('/api/agent/wechat/binding/finish', body),
  wechatUnbind: (revision: string) => agentApi.post('/api/agent/wechat/binding/unbind', { confirmed: true, revision }),
  notification: (code: string) => agentApi.get(`/api/agent/orders/${encodeURIComponent(code)}/notification`),
  readNotification: (code: string, noticeId: string) => agentApi.post(`/api/agent/orders/${encodeURIComponent(code)}/notification/read`, { notice_id: noticeId }),
  login: (phone: string, password: string) => agentApi.post("/api/agent/login", { phone, password }),
  me: () => agentApi.get("/api/agent/me"),
  changePassword: (oldPassword: string, newPassword: string) =>
    agentApi.post("/api/agent/change-password", { old: oldPassword, new: newPassword }),
  listOrders: (params: { service_status?: string; pageIndex: number; pageSize: number }) =>
    agentApi.post("/api/agent/orders/list", params, { timeout: 60000 }),
  getOrder: (code: string) => agentApi.get(`/api/agent/orders/${encodeURIComponent(code)}`),
  accept: (code: string, data: { book_date?: string; book_time?: string } = {}) =>
    agentApi.post(`/api/agent/orders/${encodeURIComponent(code)}/accept`, data),
  refuse: (code: string, reason = "") =>
    agentApi.post(`/api/agent/orders/${encodeURIComponent(code)}/refuse`, { reason }),
  book: (code: string, book_date: string, book_time: string) =>
    agentApi.post(`/api/agent/orders/${encodeURIComponent(code)}/book`, { book_date, book_time }),
  complete: (code: string, data: { extra_items?: any[]; service_note?: string } = {}) =>
    agentApi.post(`/api/agent/orders/${encodeURIComponent(code)}/complete`, data, { timeout: 60000 }),
  fail: (code: string, reason: string) =>
    agentApi.post(`/api/agent/orders/${encodeURIComponent(code)}/fail`, { reason }),
  uploadFile: (code: string, file: File, note = "完工") => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("note", note);
    return agentApi.post(`/api/agent/orders/${encodeURIComponent(code)}/upload-file`, fd, { timeout: 180000 });
  },
  feesList: (params: { pageIndex: number; pageSize: number }) =>
    agentApi.post("/api/agent/fees/list", params, { timeout: 60000 }),
  // <img> 不带请求头，用 token 查询参数鉴权（师傅 token，不是员工 token）
  fileSrc: (code: string, storedName: string) =>
    `${API_BASE_URL}/api/agent/orders/${encodeURIComponent(code)}/file/${encodeURIComponent(storedName)}?token=${encodeURIComponent(getAgentToken())}`,
};
// ── 工程文档台账（2026-08-19）──────────────────────────────────────────
// 台账全员可看（知道"库里有这份文档"不泄露技术）；正文只能问 AI，
// 且受 doc_security 的「仅推理导出」约束——AI 只给归纳结论，不吐原文。
// ⚠ 名字不能叫 engDocAPI——那个已被「工程资料中心」（请款/结算资料生成）占用。
export const engIndexAPI = {
  list: (params: { platform?: string; product_code?: string; search?: string } = {}) =>
    api.get("/api/eng-docs/list", { params }),
  scan: () => api.post("/api/eng-docs/scan", {}, { timeout: 120000 }),
  enrich: (limit = 20) => api.post("/api/eng-docs/enrich", { limit }, { timeout: 600000 }),
  update: (id: number, body: any) => api.post(`/api/eng-docs/${id}`, body),
  audit: (limit = 100) => api.get("/api/eng-docs/audit", { params: { limit } }),
};

// ── 董事长战略驾驶舱（10.4）：仅 admin 可见 ──────────────────────────
// 产品工程 · BOM 审计（10.6 §13 第一批，全只读）
// 全库体检要三次全量取数 + 1.7 万个成品跑规则（实测约 12 秒），后端按天盘缓存，
// 所以这里超时给足 300s：**首次/强刷会真算**，之后秒回。
export const peBomAPI = {
  project: (projectId: number, qty?: number) =>
    api.get<any>("/api/pe-project/bom-audit", { params: { project_id: projectId, qty }, timeout: 180000 }),
  health: (force = false) =>
    api.get<any>("/api/pe-bom/health", { params: { force }, timeout: 300000 }),
  // 单成品现算（不走缓存）。走 query 不走路径参数——料号可能带斜杠
  product: (code: string) =>
    api.get<any>("/api/pe-bom/product", { params: { code }, timeout: 120000 }),
  rules: () => api.get<any>("/api/pe-bom/rules"),
};

// 前PLM 产品工程项目工作台（10.6 §5）。全部只碰"定型前"的过程，不写 PLM/ERP。
export const peIntelligenceAPI = {
  status: (id: number) => api.get<any>('/api/pe-intelligence/' + id, { timeout: 30000 }),
  command: (id: number, action: string, payload: any) => api.post<any>('/api/pe-intelligence/' + id + '/' + action, payload, { timeout: 180000 }),
  evidence: (id: number, evidence: number) => api.get<any>('/api/pe-intelligence/' + id + '/evidence/' + evidence),
  search: (payload: any) => api.post<any>('/api/pe-intelligence/search', payload, { timeout: 180000 }),
};

export const peProjectAPI = {
  workflow: (id: number) => api.get<any>("/api/pe-project/workflow/" + id),
  workflowRead: (id: number, section: string, params: any = {}) =>
    api.get<any>("/api/pe-project/workflow/" + id + "/" + section, { params, timeout: 180000 }),
  workflowCommand: (id: number, action: string, body: any = {}) =>
    api.post<any>("/api/pe-project/workflow/" + id + "/" + action, body, { timeout: 180000 }),
  workflowDownload: (id: number, release: number) =>
    api.get("/api/pe-project/workflow/" + id + "/release/" + release, { responseType: 'blob', timeout: 180000 }),
  workflowReceipt: (id: number, form: FormData) =>
    api.post("/api/pe-project/workflow/" + id + "/receipt-upload", form, { timeout: 180000 }),
  workflowRequirementFile: (id: number, file: number) =>
    api.get("/api/pe-project/workflow/" + id + "/requirement-file/" + file, { responseType: 'blob' }),
  meta: () => api.get<any>("/api/pe-project/meta"),
  portfolio: (params: any = {}) =>
    api.get<any>("/api/pe-project/portfolio", { params }),
  detail: (id: number) => api.get<any>("/api/pe-project/detail", { params: { id } }),
  create: (body: any) => api.post<any>("/api/pe-project/create", body),
  stage: (body: any) => api.post<any>("/api/pe-project/stage", body),
  // 确认差异项：把 pending 定成真实处理方式 + 写 confirmed_at。
  // ⚠ 2026-09-13 补的 —— 在此之前没有任何接口能写 confirmed_at，而
  // 设计冻结闸门第一个阻断项就是"差异仍有待确认项" → 项目走到这里死锁。
  // 按型号名搜成品料号（输入 D7pro → 列出候选料号）。
  // ⚠ 2026-09-13 加：在此之前「基础型号」只能手填 12 位料号，而字段名
  // 让人以为填型号名 —— 用户实测填 D7pro 撞墙。
  // 删除项目（默认软删，可恢复）。confirm_no 必须与项目编号一致 —— 防手滑。
  // ⚠ 2026-09-13 补：在此之前界面上没有任何删除入口，测试项目只能找开发跑脚本删。
  deleteProject: (body: any) => api.post<any>("/api/pe-project/delete", body),
  restoreProject: (id: number) =>
    api.post<any>("/api/pe-project/restore", { id }),
  deletedProjects: () => api.get<any>("/api/pe-project/deleted"),
  // 本项目图纸：查 = 复用图纸台账（它本来就支持按 project_no 过滤）
  projectDrawings: (project_no: string) =>
    api.get<any>("/api/pe-project/drawings", { params: { project_no } }),
  // 上传图纸 → 落盘 → 解析 → 登记进**统一图纸台账**（不另存一套）
  drawingUpload: (form: FormData) =>
    api.post<any>("/api/pe-project/drawing-upload", form, { timeout: 300000 }),
  searchModel: (q: string, limit = 20) =>
    api.get<any>("/api/pe-project/search-model", { params: { q, limit } }),
  // ── 10.6 M1/M2 项目BOM节点树 ────────────────────────────────────────
  // 树是工程师的主工作入口（补充要求 §17/§19）：展开折叠、直接在节点上标
  // 继承/修改/新增，而不是先去填一张抽象的差异矩阵再回来找物料。
  // ⚠ 读 T100 多阶 BOM，给足超时（实测单个智能锁成品 233 节点 / 5 阶）。
  nodesGenerate: (body: any) =>
    api.post<any>("/api/pe-project/nodes/generate", body, { timeout: 180000 }),
  nodesAdd: (body: any) => api.post<any>("/api/pe-project/nodes/add", body),
  nodes: (params: any) => api.get<any>("/api/pe-project/nodes", { params }),
  nodesChangeType: (body: any) =>
    api.post<any>("/api/pe-project/nodes/change-type", body),
  nodesAssign: (body: any) => api.post<any>("/api/pe-project/nodes/assign", body),
  // 比对 T100 当前状态。名字像只读、其实会往节点打提醒标记（是写操作）。
  nodesDetectT100: (body: any) =>
    api.post<any>("/api/pe-project/nodes/detect-t100", body, { timeout: 180000 }),
  // ── 10.6 M5 蓝图版本 ────────────────────────────────────────────────
  // §39：历史版本永不消失 —— 列表返回**全部**版本，不过滤已取代/已撤回。
  blueprintVersions: (project_id: number, limit = 50) =>
    api.get<any>("/api/pe-project/blueprint-versions",
                 { params: { project_id, limit } }),
  blueprintVersionDiff: (project_id: number, a: number, b: number) =>
    api.get<any>("/api/pe-project/blueprint-version-diff",
                 { params: { project_id, a, b } }),
  // ⚠ 把**当前屏幕上的 items** 发回去存版本。后端不重算 ——
  // 否则用户看的是 10:00 的核价、库里存的是 10:05 的。
  blueprintVersionCreate: (body: any) =>
    api.post<any>("/api/pe-project/blueprint-version/create", body),
  blueprintVersionUpload: (form: FormData) =>
    api.post<any>("/api/pe-project/blueprint-version/upload", form,
                  { timeout: 300000 }),
  blueprintVersionDownloadUrl: (bp_id: number) =>
    `/api/pe-project/blueprint-version/download?bp_id=${bp_id}`,
  blueprintReviewClear: (body: any) =>
    api.post<any>("/api/pe-project/blueprint-review/clear", body),
  // ── 10.6 M4 需求版本 ────────────────────────────────────────────────
  // §4：V1-V7 永远不能消失 —— 列表返回**全部**版本，不过滤已取代/已撤回。
  reqVersions: (project_id: number, limit = 50) =>
    api.get<any>("/api/pe-project/req-versions",
                 { params: { project_id, limit } }),
  reqVersionDiff: (project_id: number, a: number, b: number) =>
    api.get<any>("/api/pe-project/req-version-diff",
                 { params: { project_id, a, b } }),
  reqVersionCreate: (body: any) =>
    api.post<any>("/api/pe-project/req-version/create", body),
  // 把节点状态重新汇总成差异矩阵（M3）。平时由节点操作自动触发，
  // 这个口是给"汇总掉队了想手动重来一次"用的。
  diffSyncFromNodes: (body: any) =>
    api.post<any>("/api/pe-project/diff/sync-from-nodes", body,
                  { timeout: 120000 }),
  projectTimeline: (project_id: number, limit = 200) =>
    api.get<any>("/api/pe-project/timeline", { params: { project_id, limit } }),
  diffConfirm: (body: any) => api.post<any>("/api/pe-project/diff/confirm", body),
  diffUnconfirm: (body: any) => api.post<any>("/api/pe-project/diff/unconfirm", body),
  architectDecide: (body: any) => api.post<any>("/api/pe-project/architect-decide", body),
  // 气泡：AI 整理要调模型，给足超时
  bubble: (body: any) =>
    api.post<any>("/api/pe-project/bubble", body, { timeout: 180000 }),
  bubbleConfirm: (body: any) => api.post<any>("/api/pe-project/bubble-confirm", body),
  diff: (body: any) => api.post<any>("/api/pe-project/diff", body),
  // 拖入文件：multipart，解析+AI 整理都在这一次里做完
  // 继承与差异（拍板 §22 第三优先）。多阶展开要读 T100，给足超时。
  inheritPreview: (body: any) =>
    api.post<any>("/api/pe-project/inherit-preview", body, { timeout: 180000 }),
  inheritApply: (body: any) =>
    api.post<any>("/api/pe-project/inherit-apply", body, { timeout: 180000 }),
  compare: (base: string, target: string) =>
    api.get<any>("/api/pe-project/compare", { params: { base, target }, timeout: 180000 }),
  // 临时BOM+体检+预采购（拍板 §22 第四优先）。要多阶展开基础型号，给足超时。
  tbom: (projectId: number, qty?: number) =>
    api.get<any>("/api/pe-project/tbom",
      { params: { project_id: projectId, qty: qty || 0 }, timeout: 180000 }),
  // AD 元件表导入（R9 的 EDA 路径）。要建全库物料索引，给足超时。
  edaImport: (file: File, qty?: number) => {
    const fd = new FormData();
    fd.append("file", file);
    if (qty) fd.append("qty", String(qty));
    return api.post<any>("/api/pe-project/eda-import", fd, { timeout: 300000 });
  },
  // 设计冻结闸门 + PLM/ERP 抛转核验（10.6 三段式架构的中间那道门）
  freezeCheck: (projectId: number) =>
    api.get<any>("/api/pe-project/freeze-check", { params: { project_id: projectId } }),
  freeze: (body: any) => api.post<any>("/api/pe-project/freeze", body),
  transferHealth: (days = 180, productCode = "") =>
    api.get<any>("/api/pe-project/transfer-health",
      { params: { days, product_code: productCode }, timeout: 120000 }),
  // 型号-料号-承认书知识链（10.6 工程知识资产）
  ackChain: (productCode: string) =>
    api.get<any>("/api/pe-project/ack-chain",
      { params: { product_code: productCode }, timeout: 180000 }),
  ackCoverage: () =>
    api.get<any>("/api/pe-project/ack-coverage", { timeout: 180000 }),
  // 工程图纸台账（10.6 补充要求：共享盘图纸扫描与工程知识库归档）
  drawings: (params: any = {}) => api.get<any>("/api/pe-project/drawings", { params }),
  drawing: (id: number) => api.get<any>("/api/pe-project/drawing", { params: { id } }),
  drawingScan: (root: string, limit = 200) =>
    api.post<any>("/api/pe-project/drawing-scan", { root, limit }, { timeout: 300000 }),
  drawingParse: (body: any) =>
    api.post<any>("/api/pe-project/drawing-parse", body, { timeout: 120000 }),
  drawingRegister: (body: any) =>
    api.post<any>("/api/pe-project/drawing-register", body, { timeout: 120000 }),
  drawingCheck: (id: number) => api.post<any>("/api/pe-project/drawing-check", { id }),
  drawingMergeSuggest: (id: number) =>
    api.get<any>("/api/pe-project/drawing-merge-suggest", { params: { id } }),
  drawingMerge: (body: any) => api.post<any>("/api/pe-project/drawing-merge", body),
  // ── M6a：图纸绑 BOM 节点 + 版本链 ──
  // 绑完后端会回写节点的资料状态（missing → ready），M3 的冻结闸门读的就是它。
  drawingBindNode: (body: { drawing_id: number; node_id: number; project_id?: number }) =>
    api.post<any>("/api/pe-project/drawing-bind-node", body),
  // 图纸改了内容 → 登记一条新记录（不改旧记录，§39）。要传文件，给足超时。
  drawingNewVersion: (form: FormData) =>
    api.post<any>("/api/pe-project/drawing-new-version", form, { timeout: 300000 }),
  // 最终采购清单（§11.1）。要多阶展开+查库存在途，给足超时。
  purchaseList: (productCode: string, planQty: number) =>
    api.get<any>("/api/pe-project/purchase-list",
      { params: { product_code: productCode, plan_qty: planQty }, timeout: 180000 }),
  // 采购零件技术要求（§11.2）。要读整份承认书正文，给足超时。
  techSpec: (productCode: string) =>
    api.get<any>("/api/pe-project/tech-spec",
      { params: { product_code: productCode }, timeout: 180000 }),
  // 说明书核验（§11.5）。2225 个成品逐个多阶展开很慢，页面默认给上限。
  manualCheck: (limit = 200, productCode = "") =>
    api.get<any>("/api/pe-project/manual-check",
      { params: { limit, product_code: productCode }, timeout: 600000 }),
  // 专业任务包（§5.7/§5.8）。分解要读差异矩阵并写多条，给足超时。
  tasksDecompose: (projectId: number, force = false) =>
    api.post<any>("/api/pe-project/tasks/decompose",
      { project_id: projectId, force }, { timeout: 120000 }),
  tasksList: (projectId: number) =>
    api.get<any>("/api/pe-project/tasks", { params: { project_id: projectId } }),
  taskUpdate: (payload: any) =>
    api.post<any>("/api/pe-project/tasks/update", payload),
  taskConfirmInherit: (taskId: number, conclusion: string) =>
    api.post<any>("/api/pe-project/tasks/confirm-inherit",
      { task_id: taskId, conclusion }),
  taskDependency: (taskId: number, blockedById: number, reason = "") =>
    api.post<any>("/api/pe-project/tasks/dependency",
      { task_id: taskId, blocked_by_id: blockedById, reason }),
  // 临时物料（§7.3）
  tempMaterials: (projectId: number) =>
    api.get<any>("/api/pe-project/temp-materials",
      { params: { project_id: projectId } }),
  tempMaterialCreate: (payload: any) =>
    api.post<any>("/api/pe-project/temp-materials/create", payload),
  tempMaterialUpdate: (payload: any) =>
    api.post<any>("/api/pe-project/temp-materials/update", payload),
  tempMaterialMap: (id: number, formalCode: string) =>
    api.post<any>("/api/pe-project/temp-materials/map",
      { id, formal_code: formalCode }),
  // AI 初步设计蓝图草案（§5.5）。要展开 BOM + 算成本交期，给足超时。
  blueprint: (projectId: number) =>
    api.get<any>("/api/pe-project/blueprint",
      { params: { project_id: projectId }, timeout: 180000 }),
  // MBOM 与生产流程（§11.3）。要读 BOM + 工单发料明细，给足超时。
  mbom: (productCode: string) =>
    api.get<any>("/api/pe-project/mbom",
      { params: { product_code: productCode }, timeout: 180000 }),
  bubbleUpload: (files: File[], text: string, projectId?: number) => {
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    fd.append("text", text || "");
    if (projectId) fd.append("project_id", String(projectId));
    return api.post<any>("/api/pe-project/bubble-upload", fd, { timeout: 300000 });
  },
};

export const cockpitAPI = {
  overview: (days = 90) => api.get("/api/cockpit/overview", { params: { days }, timeout: 300000 }),
  goals: () => api.get("/api/cockpit/goals"),
  saveGoal: (body: any) => api.post("/api/cockpit/goals", body, { timeout: 120000 }),
  deleteGoal: (id: number) => api.post(`/api/cockpit/goals/${id}/delete`, {}),
  syncGoals: () => api.post("/api/cockpit/goals/sync", {}, { timeout: 300000 }),
  brief: (kind = "daily", regenerate = false) =>
    api.get("/api/cockpit/brief", { params: { kind, regenerate }, timeout: 300000 }),
};

// ── 总架构师工作台 V1.0 / M1（2026-09-13）──────────────────────────
// 权限**不走 adminAPI.me 的 modules**：那是后台可授权的模块树，而本工作台的权限
// 独立于 S 级与后台授权之外（拍板第四条）。入口显不显示只认 caAPI.whoami。
export const caAPI = {
  central: (module_key = '') => api.get('/api/ca/central', { params: { enterprise_id: 'aptenon', module_key } }),
  centralTasks: (module_key = '', deleted = false) => api.get('/api/ca/central/tasks', { params: { enterprise_id: 'aptenon', module_key, deleted } }),
  centralTask: (code: string) => api.get('/api/ca/central/tasks/' + encodeURIComponent(code)),
  centralTaskCreate: (module_key: string, body: any) => api.post('/api/ca/central/tasks', body, { params: { module_key } }),
  centralTaskAction: (code: string, action: string, body: any) => api.post('/api/ca/central/tasks/' + encodeURIComponent(code) + '/' + action, body),
  centralTaskHandoff: (code: string, kind: string) => api.get('/api/ca/central/tasks/' + encodeURIComponent(code) + '/handoff', { params: { kind } }),
  centralTaskTemplates: (code: string) => api.get('/api/ca/central/tasks/' + encodeURIComponent(code) + '/templates'),
  workspaceContext: (module_key = "") => api.get("/api/ca/workspace/context", { params: { module_key } }),
  blueprintHierarchy: (module_key: string) => api.get("/api/ca/blueprint-hierarchy", { params: { module_key } }),
  saveBlueprintHierarchy: (module_key: string, body: any) => api.post("/api/ca/blueprint-hierarchy", body, { params: { module_key } }),
  snapshots: (module_key = "", offset = 0, limit = 20) => api.get("/api/ca/snapshots", { params: { module_key, offset, limit } }),
  snapshotRuntime: (id: number, module_key = "", category = "schedule", query = "", offset = 0, outcome = "") => api.get(`/api/ca/snapshots/${id}/runtime`, { params: { module_key, category, query, offset, outcome, limit: 30 } }),
  runtimeMonitor: (hours = 24, outcome = '', query = '', offset = 0) => api.get('/api/ca/runtime-monitor', { params: { hours, outcome, query, offset } }),
  compareSnapshots: (before_id: number, after_id: number, module_key = "", change = "", category = "", query = "", offset = 0) => api.get("/api/ca/snapshots/compare", { params: { before_id, after_id, module_key, change, category, query, offset, limit: 30 } }),
  snapshotInventory: (id: number, module_key = "", category = "", query = "", offset = 0, attribution = "") => api.get(`/api/ca/snapshots/${id}/inventory`, { params: { module_key, category, query, offset, attribution, limit: 30 } }),
  codeMap: (module_key: string, revision = 0) => api.get("/api/ca/code-map", { params: { module_key, revision } }),
  codeMapHead: () => api.get("/api/ca/code-map/head"),
  codeMapSave: (body: any) => api.post("/api/ca/code-map", body, { timeout: 120000 }),
  codeMapResource: (module_key: string, revision: number, feature_key: string, index: number, session: string) =>
    api.get("/api/ca/code-map/resource", { params: { module_key, revision, feature_key, index, session } }),
  moduleVersions: (module_key: string) => api.get("/api/ca/module-versions", { params: { module_key } }),
  moduleVersion: (id: number) => api.get("/api/ca/module-versions/" + id),
  moduleVersionCreate: (body: any) => api.post("/api/ca/module-versions", body, { timeout: 120000 }),
  executions: (module_key = "") => api.get("/api/ca/executions", { params: { module_key } }),
  modelUsage: (params: { module_key?: string; execution_id?: number; group_by?: string; since?: string; until?: string }) => api.get('/api/ca/model-usage', { params }),
  execution: (id: number) => api.get("/api/ca/executions/" + id),
  executionCreate: (body: any) => api.post("/api/ca/executions", body, { timeout: 120000 }),
  executionAction: (id: number, body: any) => api.post("/api/ca/executions/" + id + "/actions", body, { timeout: 120000 }),
  executionGenerate: (id: number, body: any) => api.post(`/api/ca/executions/${id}/generate`, body, { timeout: 210000 }),
  executionSourceReview: (id: number) => api.get(`/api/ca/executions/${id}/source-review`),
  executionEngineeringEvidence: (id: number) => api.get(`/api/ca/executions/${id}/engineering-evidence`),
  executionRecordSourceReview: (id: number, body: any) => api.post(`/api/ca/executions/${id}/source-review`, body),
  executionChain: (id: number, body: any) => api.post(`/api/ca/executions/${id}/engineering-chain`, body, { timeout: 1800000 }),
  executionSandbox: (id: number, body: any) => api.post(`/api/ca/executions/${id}/sandbox`, body, { timeout: 300000 }),
  executionStaging: (id: number, body: any) => api.post(`/api/ca/executions/${id}/staging`, body, { timeout: 900000 }),
  executionArchitectureReview: (id: number, body: any) => api.post(`/api/ca/executions/${id}/architecture-review`, body, { timeout: 240000 }),
  deliveries: (module_key = "") => api.get("/api/ca/deliveries", { params: { module_key } }),
  delivery: (id: number) => api.get("/api/ca/deliveries/" + id),
  deliveryCreate: (body: any) => api.post("/api/ca/deliveries", body, { timeout: 120000 }),
  deliveryAction: (id: number, body: any) => api.post("/api/ca/deliveries/" + id + "/actions", body, { timeout: 120000 }),
  deliveryRetry: (id: number) => api.post("/api/ca/delivery-tasks/" + id + "/retry", {}, { timeout: 120000 }),
  deliveryComplete: (id: number, body: any) => api.post("/api/ca/delivery-tasks/" + id + "/complete", body),
  whoami: () => api.get("/api/ca/whoami"),
  overview: () => api.get("/api/ca/overview"),
  roadmap: () => api.get("/api/ca/roadmap"),
  capabilities: () => api.get("/api/ca/capabilities"),
  audit: (date = "", limit = 200) => api.get("/api/ca/audit", { params: { date, limit } }),
  // M2 模块台账
  modules: () => api.get("/api/ca/modules", { timeout: 120000 }),
  modulesReseed: () => api.post("/api/ca/modules/reseed", {}, { timeout: 120000 }),
  moduleUpdate: (key: string, changes: Record<string, any>, reason = "") =>
    api.post(`/api/ca/modules/${encodeURIComponent(key)}/update`, { changes, reason }),
  baselineVersion: (version: string, reason = "") =>
    api.post("/api/ca/modules/baseline-version", { version, reason }, { timeout: 120000 }),
  modulesChangelog: (module_key = "", limit = 200) =>
    api.get("/api/ca/modules-changelog", { params: { module_key, limit } }),
  // M3 蓝图库
  blueprints: (module_key = "", include_parent = false) =>
    api.get("/api/ca/blueprints", { params: { module_key, include_parent }, timeout: 120000 }),
  blueprintDetail: (id: number) => api.get(`/api/ca/blueprints/${id}`, { timeout: 120000 }),
  changeExternalSource: (code: string, payload: any) => api.post(`/api/ca/changes/${encodeURIComponent(code)}/external-sources`, payload),
  changeProgress: (code: string, kind = '', offset = 0) => api.get(`/api/ca/changes/${encodeURIComponent(code)}/progress`, { params: { kind, offset } }),
  blueprintConflictSuggestions: (module_key: string, entries: any[]) =>
    api.post('/api/ca/blueprint-hierarchy/suggest-conflicts', { entries }, { params: { module_key }, timeout: 130000 }),
  blueprintCompare: (before_id: number, after_id: number, kind = "", query = "", offset = 0) =>
    api.get('/api/ca/blueprints/compare', { params: { before_id, after_id, kind, query, offset } }),
  blueprintUpload: (form: FormData) =>
    api.post("/api/ca/blueprints/upload", form, { timeout: 600000 }),
  blueprintSetPrimary: (id: number) => api.post(`/api/ca/blueprints/${id}/set-primary`, {}),
  // M4 差异分析
  gapRuns: (module_key = "") => api.get("/api/ca/gap/runs", { params: { module_key }, timeout: 120000 }),
  gapCreate: (blueprint_id: number, module_key = "", request_key = crypto.randomUUID()) =>
    api.post("/api/ca/gap/runs", { blueprint_id, module_key, request_key }, { timeout: 600000 }),
  gapBasis: (runId: number) => api.get("/api/ca/gap/runs/" + runId + "/basis"),
  gapDecision: (id: number, body: any) => api.post("/api/ca/narratives/" + id + "/decision", body),
  gapExport: (runId: number, narrative_id?: number) => api.post("/api/ca/gap/runs/" + runId + "/export", { narrative_id }),
  // 分页：百万字蓝图能切出 3000 条，不分页一次要回 4.4MB
  gapDetail: (run_id: number, verdict = "", offset = 0, limit = 300) =>
    api.get(`/api/ca/gap/runs/${run_id}`,
      { params: { verdict, offset, limit }, timeout: 180000 }),
  // V1.1 §7 我的决策台：第一屏回答「今天需要我决定什么」（不调模型，纯查库）
  desk: (module_key = "") => api.get("/api/ca/desk", { params: { module_key }, timeout: 120000 }),
  requirements: (id: number, revision = 0) => api.get(`/api/ca/blueprints/${id}/requirements`, { params: { revision } }),
  requirementsWrite: (id: number, body: any) => api.post(`/api/ca/blueprints/${id}/requirements`, body),
  requirementsSuggest: (id: number, body: any) => api.post(`/api/ca/blueprints/${id}/requirements/suggest`, body, { timeout: 180000 }),
  // V1.1 §8.4 归并：一级系统层（叶子模块的键一个都没动）
  systems: () => api.get("/api/ca/systems", { timeout: 120000 }),
  systemUpdate: (key: string, changes: Record<string, any>, reason = "") =>
    api.post(`/api/ca/systems/${encodeURIComponent(key)}/update`,
      { changes, reason }),
  // V1.1 §9 蓝图元数据与权威规则
  blueprintUpdateMeta: (id: number, changes: Record<string, any>) =>
    api.post(`/api/ca/blueprints/${id}/meta`, { changes }),
  blueprintSetAuthority: (id: number, expected_revision: number, expected_token: string, reason: string) =>
    api.post(`/api/ca/blueprints/${id}/set-authority`, { expected_revision, expected_token, reason }),
  // V1.1 §12 人话差异报告：差异中心的第一层（锚点与计数退到展开层）
  gapNarrative: (run_id: number) =>
    api.get(`/api/ca/gap/runs/${run_id}/narrative`, { timeout: 120000 }),
  gapCollectFrontend: (run_id: number, request_key: string) =>
    api.post(`/api/ca/gap/runs/${run_id}/observations/frontend`, { request_key }, { timeout: 30000 }),
  gapObservations: (run_id: number) =>
    api.get(`/api/ca/gap/runs/${run_id}/observations`, { timeout: 120000 }),
  gapObservationCreate: (run_id: number, observation: any, request_key: string) =>
    api.post(`/api/ca/gap/runs/${run_id}/observations`, { observation, request_key }),
  // 生成前先看要花多少（组数/调用次数/预计耗时）—— 大蓝图要跑几分钟
  gapNarrativePlan: (run_id: number) =>
    api.get(`/api/ca/gap/runs/${run_id}/narrative/plan`, { timeout: 120000 }),
  gapNarrativeGenerate: (run_id: number, only_missing = true) =>
    api.post(`/api/ca/gap/runs/${run_id}/narrative`, { only_missing },
      { timeout: 1800000 }),   // 大蓝图 170+ 次调用，超时给足
  narrativeEvidence: (id: number) =>
    api.get(`/api/ca/narratives/${id}/evidence`, { timeout: 120000 }),
  // 汇总：按缺失锚点归并成「缺口清单」，一组就是一份变更计划的候选。
  // 不按蓝图章节汇总的原因见后端 gap.run_summary 的文档（PDF 抽的正文没有可靠结构）。
  gapSummary: (run_id: number, by = "anchor") =>
    api.get(`/api/ca/gap/runs/${run_id}/summary`,
      { params: { by }, timeout: 180000 }),
  gapVerdict: (item_id: number, verdict: string, note = "") =>
    api.post(`/api/ca/gap/items/${item_id}/verdict`, { verdict, note }),
  gapAiSuggest: (item_id: number) =>
    api.post(`/api/ca/gap/items/${item_id}/ai-suggest`, {}, { timeout: 300000 }),
  // 自动化：规则重判（不调模型，秒回）+ 批量 AI 判定（每条一次调用，慢）
  gapAutoVerdict: (run_id: number) =>
    api.post(`/api/ca/gap/runs/${run_id}/auto-verdict`, {}, { timeout: 300000 }),
  gapAiBatch: (run_id: number, limit = 60) =>
    api.post(`/api/ca/gap/runs/${run_id}/ai-batch`, { limit },
      { timeout: 1800000 }),
  // M5 变更计划与审批
  changes: (status = "", module_key = "") => api.get("/api/ca/changes", { params: { status, module_key }, timeout: 120000 }),
  changeCreate: (body: any) => api.post("/api/ca/changes", body, { timeout: 120000 }),
  changeDetail: (code: string) => api.get(`/api/ca/changes/${code}`, { timeout: 120000 }),
  changeUpdate: (code: string, changes: Record<string, any>, note = "", token?: any) =>
    api.post("/api/ca/changes/" + code + "/update", { changes, note, expected_revision: token?.revision, expected_hash: token?.content_hash }),
  changeSubmit: (code: string, token?: any) =>
    api.post("/api/ca/changes/" + code + "/submit", { expected_revision: token?.revision, expected_hash: token?.content_hash }),
  changeDecide: (code: string, action: string, note = "", token?: any) =>
    api.post("/api/ca/changes/" + code + "/decide", { action, note, expected_revision: token?.revision, expected_hash: token?.content_hash }),
  changeFromNarrative: (narrativeId: number, target_module = "") =>
    api.post("/api/ca/changes/from-narrative/" + narrativeId, { target_module }, { timeout: 120000 }),
  changeFromNarratives: (narrative_ids: number[], target_module: string) =>
    api.post("/api/ca/changes/from-narratives", { narrative_ids, target_module }),
  changeGroupingPreview: (selection: any[]) =>
    api.post("/api/ca/changes/grouping-preview", { selection }, { timeout: 120000 }),
  changeGrouped: (body: any) =>
    api.post("/api/ca/changes/grouped", body, { timeout: 120000 }),
  changeDraftTech: (code: string, overwrite = false, token?: any) =>
    api.post("/api/ca/changes/" + code + "/draft-tech", { overwrite, expected_revision: token?.revision, expected_hash: token?.content_hash }, { timeout: 300000 }),
  changeReviewTech: (code: string, note = "", token?: any) =>
    api.post("/api/ca/changes/" + code + "/review-tech", { note, expected_revision: token?.revision, expected_hash: token?.content_hash }),
  changeDismissProtected: (code: string, reason: string, token?: any) =>
    api.post("/api/ca/changes/" + code + "/dismiss-protected", { reason, expected_revision: token?.revision, expected_hash: token?.content_hash }),
  changeGovernance: (code: string, body: any) => api.post("/api/ca/changes/" + code + "/governance", body),
  changeRevision: (code: string, revision: number) => api.get("/api/ca/changes/" + code + "/revisions/" + revision),
  changeRevise: (code: string, body: any) => api.post("/api/ca/changes/" + code + "/revise", body),
  changeCheckScope: (code: string, token: any) => api.post("/api/ca/changes/" + code + "/check-scope", { expected_revision: token?.revision, expected_hash: token?.content_hash }),
  // M6 源码保险库（只读）
  vaultInfo: () => api.get("/api/ca/vault/info", { timeout: 120000 }),
  vaultOpenSession: (purpose: string, plan_code = "") =>
    api.post("/api/ca/vault/sessions", { purpose, plan_code }),
  vaultCloseSession: (code: string) => api.post(`/api/ca/vault/sessions/${code}/close`, {}),
  vaultAccesses: (code: string) => api.get(`/api/ca/vault/sessions/${code}/accesses`),
  vaultTree: (session: string, path = "", ref = "HEAD") =>
    api.get("/api/ca/vault/tree", { params: { session, path, ref }, timeout: 120000 }),
  vaultFile: (session: string, path: string, ref = "HEAD", worktree = false) =>
    api.get("/api/ca/vault/file", { params: { session, path, ref, worktree }, timeout: 120000 }),
  vaultCommits: (session: string, ref = "HEAD", limit = 30, path = "") =>
    api.get("/api/ca/vault/commits", { params: { session, ref, limit, path }, timeout: 120000 }),
  vaultCommit: (session: string, sha: string) =>
    api.get(`/api/ca/vault/commit/${sha}`, { params: { session }, timeout: 120000 }),
  vaultSearch: (session: string, keyword: string) =>
    api.get("/api/ca/vault/search", { params: { session, keyword }, timeout: 120000 }),
  // 下载走带 token 的 URL：<a download> 发不出鉴权头（同 outsourcingExcelUrl 的既有做法）
  blueprintDownloadUrl: (id: number) =>
    `${API_BASE_URL}/api/ca/blueprints/${id}/download?token=${encodeURIComponent(getToken())}`,
  // E-4 验收、发布、回滚
  acceptanceList: () => api.get("/api/ca/acceptance", { timeout: 120000 }),
  acceptanceDetail: (code: string) => api.get(`/api/ca/acceptance/${code}`, { timeout: 120000 }),
  acceptanceCreate: (body: any) => api.post("/api/ca/acceptance/create", body),
  acceptanceCheckPreconditions: (code: string) => api.post(`/api/ca/acceptance/${code}/check-preconditions`, {}),
  acceptanceSubmit: (code: string) => api.post(`/api/ca/acceptance/${code}/submit`, {}),
  acceptanceDecide: (code: string, action: string, note = "") =>
    api.post(`/api/ca/acceptance/${code}/decide`, { action, note }),
  releaseList: () => api.get("/api/ca/releases", { timeout: 120000 }),
  releaseDetail: (code: string) => api.get(`/api/ca/releases/${code}`, { timeout: 120000 }),
  releaseCreate: (body: any) => api.post("/api/ca/releases/create", body),
  releaseApprove: (code: string, note = "") => api.post(`/api/ca/releases/${code}/approve`, { note }),
  releaseMarkReleased: (code: string, note = "") => api.post(`/api/ca/releases/${code}/mark-released`, { note }),
  rollbackList: () => api.get("/api/ca/rollbacks", { timeout: 120000 }),
  rollbackDetail: (code: string) => api.get(`/api/ca/rollbacks/${code}`, { timeout: 120000 }),
  rollbackCreate: (body: any) => api.post("/api/ca/rollbacks/create", body),
  rollbackApprove: (code: string, note = "") => api.post(`/api/ca/rollbacks/${code}/approve`, { note }),
  rollbackMarkCompleted: (code: string, note = "") => api.post(`/api/ca/rollbacks/${code}/mark-completed`, { note }),
};

// ── 知识库总览 + 跨库一次搜全（2026-08-24 入口统一）─────────────────
export const kbOverviewAPI = {
  summary: () => api.get("/api/kb-overview/summary", { timeout: 120000 }),
  search: (q: string, top_k = 6) =>
    api.get("/api/kb-overview/search", { params: { q, top_k }, timeout: 180000 }),
  bizKnowledge: (top = "") =>
    api.get("/api/kb-overview/biz-knowledge", { params: { top }, timeout: 120000 }),
  ingestGaps: () => api.get("/api/kb-overview/ingest-gaps", { timeout: 180000 }),
  // 一键增量导入（仅管理员）：把盘上新增/修改的文件补进向量库，可能跑几分钟
  reindex: () => api.post("/api/kb-overview/reindex", {}, { timeout: 1800000 }),
};

// ── 外贸 AI CRM（10.7）：客户主档 / 公海私海 / 跟进任务 / 询盘商机 ──
export const ftcrmAPI = {
  t100LinkList: (params: any = {}, signal?: AbortSignal) => api.get<any>('/api/ftcrm/t100-link', { params, signal }),
  t100LinkHistory: (customer_id:number) => api.get<any>('/api/ftcrm/t100-link/history', { params:{customer_id} }),
  t100LinkScan: (enabled?: boolean) => api.post<any>('/api/ftcrm/t100-link/scan', enabled === undefined ? {} : { enabled }),
  t100LinkDecision: (body: any) => api.post<any>('/api/ftcrm/t100-link/decision', body, { timeout: 60000 }),
  customerFilters: () => api.get<any>("/api/ftcrm/customer/filter-options"),
  t100Search: (name: string) => api.get<any>("/api/ftcrm/customer/t100-search", { params: { name } }),
  meta: () => api.get<any>("/api/ftcrm/meta"),
  customers: (params: any = {}, signal?: AbortSignal) => api.get<any>("/api/ftcrm/customers", { params, signal }),
  t100NewCustomers: (params: any = {}) => api.get<any>("/api/ftcrm/customers/t100-new", { params, timeout: 60000 }),
  customer: (id: number) => api.get<any>("/api/ftcrm/customer", { params: { id } }),
  checkDuplicate: (body: any) => api.post<any>("/api/ftcrm/customer/check-duplicate", body),
  createCustomer: (body: any) => api.post<any>("/api/ftcrm/customer/create", body),
  updateCustomer: (body: any) => api.post<any>("/api/ftcrm/customer/update", body),
  voidCustomer: (body: any) => api.post<any>("/api/ftcrm/customer/void", body),
  mergeCustomers: (body: any) => api.post<any>("/api/ftcrm/customer/merge", body),
  createContact: (body: any) => api.post<any>("/api/ftcrm/contact/create", body),
  claim: (body: any) => api.post<any>("/api/ftcrm/pool/claim", body),
  release: (body: any) => api.post<any>("/api/ftcrm/pool/release", body),
  // 撤销错认领：与 release 分开是因为**留痕原因不同**（误领 vs 主动退回），
  // 不是同一件事换个说法。条件（零跟进 + 7 天内）由后端判，前端不重复一遍。
  undoClaim: (body: any) => api.post<any>("/api/ftcrm/pool/undo-claim", body),
  canUndoClaim: (customer_id: number) =>
    api.get<any>("/api/ftcrm/pool/can-undo-claim", { params: { customer_id } }),
  transfer: (body: any) => api.post<any>("/api/ftcrm/pool/transfer", body),
  recyclePreview: () => api.get<any>("/api/ftcrm/pool/recycle-preview"),
  handover: (body: any) => api.post<any>("/api/ftcrm/pool/handover", body),
  createActivity: (body: any) => api.post<any>("/api/ftcrm/activity/create", body),
  confirmAi: (body: any) => api.post<any>("/api/ftcrm/activity/confirm-ai", body),
  tasks: (params: any = {}) => api.get<any>("/api/ftcrm/tasks", { params }),
  createTask: (body: any) => api.post<any>("/api/ftcrm/task/create", body),
  completeTask: (body: any) => api.post<any>("/api/ftcrm/task/complete", body),
  inquiries: (params: any = {}) => api.get<any>("/api/ftcrm/inquiries", { params }),
  createInquiry: (body: any) => api.post<any>("/api/ftcrm/inquiry/create", body),
  convertInquiry: (body: any) => api.post<any>("/api/ftcrm/inquiry/convert", body),
  funnel: (params: any = {}) => api.get<any>("/api/ftcrm/funnel", { params }),
  createOpportunity: (body: any) => api.post<any>("/api/ftcrm/opportunity/create", body),
  changeStage: (body: any) => api.post<any>("/api/ftcrm/opportunity/stage", body),
  opportunity: (id: number) => api.get<any>("/api/ftcrm/opportunity", { params: { id } }),
};

// 外贸 AI CRM 第二批：工作台 / 时间线 / 报价 / OEM-ODM / 邮件状态
export const ftcrmMoreAPI = {
  workbench: () => api.get<any>("/api/ftcrm/workbench"),
  timeline: (customer_id: number) => api.get<any>("/api/ftcrm/timeline", { params: { customer_id } }),
  quotes: (params: any = {}) => api.get<any>("/api/ftcrm/quotes", { params }),
  createQuote: (body: any) => api.post<any>("/api/ftcrm/quote/create", body),
  applyAiPrice: (body: any) => api.post<any>("/api/ftcrm/quote/apply-ai", body),
  sendQuote: (body: any) => api.post<any>("/api/ftcrm/quote/send", body),
  oemRequests: (params: any = {}) => api.get<any>("/api/ftcrm/oem-requests", { params }),
  createOemRequest: (body: any) => api.post<any>("/api/ftcrm/oem-request/create", body),
  submitOemRequest: (body: any) => api.post<any>("/api/ftcrm/oem-request/submit", body),
  syncPe: (body: any = {}) => api.post<any>("/api/ftcrm/oem-request/sync-pe", body),
  mailStatus: () => api.get<any>("/api/ftcrm/mail/status"),
};
